import React, { useCallback, useEffect, useRef, useState } from 'react';
import Draggable from 'react-draggable';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { getLocalizedAppLabel, getLocalizedShortcutLabel, normalizeLanguage, t } from '../utils/i18n';
import { normalizeOsMode, resolveDesktopLayout, WINDOW_MANAGEMENT_IDS } from '../utils/os';
import './Window.css';

const MAX_UPLOAD_BYTES = 10 * 1024 * 1024;
const MAX_AI_CHAT_ATTACHMENT_CHARS = 60000;
const AI_CHAT_AUTO_SCROLL_THRESHOLD = 120;
const AI_CHAT_THUMBNAIL_MAX_EDGE = 240;
const AI_CHAT_HISTORY_STORAGE_KEY = 'portfolio-ai-chat-sessions';
const AI_CHAT_HISTORY_LIMIT = 12;
const AI_IMPORT_JOB_POLL_VISIBLE_DELAY_MS = 700;
const AI_IMPORT_JOB_POLL_HIDDEN_DELAY_MS = 1500;
const AI_IMPORT_JOB_MAX_POLLS = 360;
const PORTFOLIO_IMPORT_TEXT_MIME_TYPES = [
  'text/csv',
  'application/csv',
  'application/vnd.ms-excel',
  'text/plain',
  'application/octet-stream',
  ''
];
const PORTFOLIO_IMPORT_DOCUMENT_MIME_TYPES_BY_EXTENSION = {
  '.pdf': [
    'application/pdf',
    'application/octet-stream',
    ''
  ],
  '.doc': [
    'application/msword',
    'application/doc',
    'application/vnd.ms-word',
    'application/octet-stream',
    ''
  ],
  '.docx': [
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/zip',
    'application/octet-stream',
    ''
  ],
  '.xls': [
    'application/vnd.ms-excel',
    'application/excel',
    'application/xls',
    'application/octet-stream',
    ''
  ],
  '.xlsx': [
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/zip',
    'application/octet-stream',
    ''
  ],
  '.ppt': [
    'application/vnd.ms-powerpoint',
    'application/mspowerpoint',
    'application/powerpoint',
    'application/x-mspowerpoint',
    'application/octet-stream',
    ''
  ],
  '.pptx': [
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    'application/zip',
    'application/octet-stream',
    ''
  ],
  '.hwp': [
    'application/x-hwp',
    'application/haansofthwp',
    'application/vnd.hancom.hwp',
    'application/octet-stream',
    ''
  ],
  '.hwpx': [
    'application/haansofthwpx',
    'application/vnd.hancom.hwpx',
    'application/x-hwp+zip',
    'application/zip',
    'application/octet-stream',
    ''
  ]
};
const PORTFOLIO_IMPORT_DOCUMENT_EXTENSIONS = Object.keys(PORTFOLIO_IMPORT_DOCUMENT_MIME_TYPES_BY_EXTENSION);
const PORTFOLIO_IMPORT_UPLOAD_ACCEPT = ['.csv', ...PORTFOLIO_IMPORT_DOCUMENT_EXTENSIONS].join(',');
const PORTFOLIO_IMPORT_SUPPORTED_FILE_LABEL = 'CSV, PDF, Word, Excel, PowerPoint, and HWP/HWPX';
const PROJECT_AI_CHAT_SUPPORTED_SCOPE_LABEL = 'Profile, Resume, and Projects';
const GUARD_BYPASS_LOGIN_PATH = '/.blacklist-guard/bypass/login';
const GUARD_ACCESS_KEY_REQUIRED = 'Access key required — open this site with your access key link to use AI Chat.';
const getGuardBypassLoginUrl = () => {
  if (typeof window === 'undefined') return GUARD_BYPASS_LOGIN_PATH;
  return `${GUARD_BYPASS_LOGIN_PATH}?next=${encodeURIComponent(`${window.location.pathname}${window.location.search}`)}`;
};
const PORTFOLIO_IMPORT_TARGET_LABELS = {
  profile: 'Profile',
  resume: 'Resume',
  projects: 'Projects'
};
const PROTECTED_ICON_IDS = ['settings', 'profile', 'resume', 'projects', 'ai-auth', 'ai-chat', 'change-os'];
const PREVIEW_ICON_EXTENSIONS = ['svg', 'png', 'icns'];
const PREVIEW_SPECIAL_ICON_IDS = {
  auth: 'ai-auth-default',
  chat: 'chat-text-default'
};
const PREVIEW_ICON_SOURCE_FALLBACKS = {
  settings: ['/icons/settings-icon.svg'],
  profile: [],
  resume: ['/icons/resume-icon.svg'],
  projects: ['/icons/projects-folder.svg'],
  github: ['/icons/github-icon.svg'],
  docker: ['/icons/docker.png']
};
const PREVIEW_ICON_EMOJI_FALLBACKS = {
  settings: 'S',
  profile: 'P',
  resume: 'R',
  projects: 'F',
  'ai-auth': 'A',
  'ai-chat': 'C',
  'change-os': '🖥️',
  github: 'G',
  docker: 'D'
};
const aiChatImageThumbnailCache = new Map();
// Chat targets, all served by ai-chat-hub (hub design §8.2): keys, logins and model lists
// live there. `importable` = usable for portfolio import (Codex, structured output).
// Order: Gemma (this server's CPU), NVIDIA, Codex, Claude, Antigravity.
const CHAT_TARGET_OPTIONS = [
  { id: 'gemma',  tool: 'gemma',  strategy: 'ai-chat-hub', label: 'Gemma (로컬 CPU)' },
  { id: 'nvidia', tool: 'nvidia', strategy: 'ai-chat-hub', label: 'NVIDIA' },
  { id: 'codex',  tool: 'codex',  strategy: 'ai-chat-hub', label: 'Codex', importable: true },
  { id: 'claude', tool: 'claude', strategy: 'ai-chat-hub', label: 'Claude (ccacw)' },
  { id: 'agy',    tool: 'agy',    strategy: 'ai-chat-hub', label: 'Antigravity (ccacw)' }
];
// Target ids saved in browser history before the hub (per-login-location variants).
const LEGACY_CHAT_TARGET_IDS = {
  'codex-account-home': 'codex', 'codex-project-user-data': 'codex',
  'claude-account-home': 'claude', 'claude-project-user-data': 'claude',
  'agy-account-home': 'agy', 'nvidia-api': 'nvidia'
};
const normalizeChatTargetId = (value) => {
  const id = String(value || '');
  if (CHAT_TARGET_OPTIONS.some((target) => target.id === id)) return id;
  return LEGACY_CHAT_TARGET_IDS[id] || 'codex';
};
const DEFAULT_NEW_ICON = {
  label: 'New Item',
  localizedLabels: {
    english: 'New Item',
    korean: 'New Item'
  },
  icon: '?',
  link: ''
};
const DEFAULT_WINDOW_SIZES = {
  default: { width: 600, height: 450 },
  'ai-auth': { width: 937, height: 506 },
  'change-os': { width: 420, height: 214 },
  settings: { width: 980, height: 510 },
  'ai-chat': { width: 760, height: 616 },
  'profile-settings': { width: 700, height: 760 },
  'resume-settings': { width: 720, height: 320 },
  'projects-settings': { width: 860, height: 680 },
  profile: { width: 784, height: 532 },
  resume: { width: 700, height: 510 },
  projects: { width: 932, height: 809 }
};
const MIN_WINDOW_SIZES = {
  default: { width: 420, height: 320 },
  'ai-auth': { width: 937, height: 520 },
  'change-os': { width: 380, height: 196 },
  settings: { width: 784, height: 400 },
  'ai-chat': { width: 620, height: 506 },
  'profile-settings': { width: 620, height: 620 },
  'resume-settings': { width: 620, height: 280 },
  'projects-settings': { width: 700, height: 520 },
  profile: { width: 620, height: 430 },
  resume: { width: 560, height: 400 },
  projects: { width: 900, height: 620 }
};

/**
 * Announced by whichever window changed a project gallery; every open Projects window reloads
 * from the server when it hears it.
 *
 * The shared store on the desktop should already carry the change to the other window. This
 * exists because "should" was not good enough twice: a memo comparator, a prop that never got
 * threaded, or a window mounted before the store existed all end the same way — a viewer that
 * keeps painting a stale gallery until it is closed and reopened. A listener lives inside the
 * window instance, so its state update re-renders that instance no matter what sits above it.
 */
const PROJECT_IMAGES_CHANGED_EVENT = 'portfolio:project-images-changed';

const announceProjectImagesChanged = () => {
  try {
    window.dispatchEvent(new Event(PROJECT_IMAGES_CHANGED_EVENT));
  } catch (_) {
    // A browser without CustomEvent support still has the shared store; nothing to do.
  }
};

const RESIZABLE_WINDOW_IDS = ['ai-chat', 'projects', 'profile', 'profile-settings', 'resume', 'resume-settings', 'projects-settings'];

// eslint-disable-next-line no-unused-vars
const sanitizeShortcutLabel = (value) => String(value || '')
  .replace(/[\r\n\t]/g, '')
  .trim()
  .slice(0, 12);

const sanitizeEditableShortcutLabel = (value) => String(value || '')
  .replace(/[\r\n\t]/g, '')
  .trim()
  .slice(0, 12);

const buildShortcutLabelDrafts = (desktopIcons = [], language = 'english') => desktopIcons.reduce((drafts, iconItem) => {
  drafts[iconItem.id] = getLocalizedShortcutLabel(iconItem, language);
  return drafts;
}, {});

const cloneDraftValue = (value) => JSON.parse(JSON.stringify(value || {}));

const createProfileActivity = () => ({
  activity_type: '',
  name: '',
  institution: '',
  start_date: '',
  end_date: '',
  description: ''
});

const createProfileSkill = () => ({
  name: '',
  proficiency: '',
  usage_note: ''
});

const createProfileLanguage = () => ({
  language: '',
  proficiency: '',
  test_name: '',
  score: '',
  acquired_date: '',
  institution: ''
});

const createProfileCertificate = () => ({
  name: '',
  grade: '',
  acquired_date: '',
  issuer: '',
  note: ''
});

const createProfileAward = () => ({
  award_name: '',
  award_date: '',
  organization: '',
  description: ''
});

const createSelfIntroductionSection = () => ({
  title: '',
  content: ''
});

const createResumeWorkExperience = () => ({
  company: '',
  department: '',
  job_title: '',
  position_rank: '',
  start_date: '',
  end_date: '',
  is_current: false,
  duration: '',
  main_tasks: [''],
  responsibilities: [''],
  achievements: [''],
  detailed_descriptions: [
    {
      section_title: '',
      details: ['']
    }
  ]
});

const createResumeEducation = () => ({
  school_name: '',
  school_type: '',
  degree: '',
  major: '',
  start_date: '',
  end_date: '',
  status: '',
  gpa: '',
  gpa_scale: '',
  location: '',
  relevant_coursework: ['']
});

const createResumeTraining = () => ({
  course_name: '',
  institution: '',
  start_date: '',
  end_date: '',
  completion_date: '',
  hours: '',
  description: ''
});

const createProjectEntry = () => ({
  company: '',
  department: '',
  position_rank: '',
  project_name: '',
  start_date: '',
  end_date: '',
  description: '',
  role_and_responsibility: '',
  outcomes: ['']
});

const toMultilineText = (items) => (
  Array.isArray(items)
    ? items.filter((item) => String(item || '').trim()).join('\n')
    : ''
);

const fromMultilineText = (text) => {
  const lines = String(text || '')
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);

  return lines.length > 0 ? lines : [''];
};

const resolvePreviewIconSource = (iconValue, extension = 'svg') => `/icons/${iconValue}.${extension}`;

const CHAT_TOOL_LABELS = {
  gemma: 'Gemma',
  nvidia: 'NVIDIA',
  codex: 'Codex',
  claude: 'Claude',
  agy: 'Antigravity'
};

const getChatTargetDefinition = (targetId) => CHAT_TARGET_OPTIONS.find((item) => item.id === targetId) || CHAT_TARGET_OPTIONS[0];

// Ready = the hub can serve this target now (key present, ccacw not cooling down, codex
// logged in). Status comes from GET /api/ai-tools/status → { hub: { targets } }.
const isChatTargetAuthenticated = (aiToolsStatus, targetId) => {
  const target = getChatTargetDefinition(targetId);
  return Boolean(aiToolsStatus?.hub?.targets?.[target.id]?.available);
};

const chatToolOptionStyle = (authenticated) => ({
  backgroundColor: authenticated ? '#b8f2c2' : '#2b2b2b',
  color: authenticated ? '#0d1117' : '#b4bbc5'
});

const waitForDelay = (durationMs) => new Promise((resolve) => {
  window.setTimeout(resolve, Math.max(0, Number(durationMs) || 0));
});

const getApiErrorMessage = (payload, fallbackMessage) => {
  const fallback = String(fallbackMessage || 'Request failed.');
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
    return fallback;
  }

  // blacklist-guard answers 401 guard_auth_required on token-gated APIs (Profile, Resume,
  // Projects and AI Chat) when this browser has no access key.
  if (payload.error === 'guard_auth_required') {
    return GUARD_ACCESS_KEY_REQUIRED;
  }

  if (typeof payload.error === 'string' && payload.error.trim()) {
    return payload.error.trim();
  }

  if (payload.error && typeof payload.error === 'object' && !Array.isArray(payload.error)) {
    const nestedMessage = String(payload.error.message || payload.error.code || '').trim();
    if (nestedMessage) {
      return nestedMessage;
    }
  }

  const message = String(payload.message || '').trim();
  return message || fallback;
};

const getAiImportJobId = (payload) => {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
    return '';
  }

  return String(payload.job_id || payload.jobId || payload.id || '').trim();
};

const getAiImportResultPayload = (payload) => {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
    return {};
  }

  if (payload.result && typeof payload.result === 'object' && !Array.isArray(payload.result)) {
    return payload.result;
  }

  return payload;
};

const arrayBufferToBase64 = (buffer) => {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  const chunkSize = 0x8000;

  for (let offset = 0; offset < bytes.length; offset += chunkSize) {
    const chunk = bytes.subarray(offset, Math.min(offset + chunkSize, bytes.length));
    binary += String.fromCharCode(...chunk);
  }

  return window.btoa(binary);
};

const getAiChatAttachmentExtension = (fileName = '') => {
  const segments = String(fileName || '').trim().split('.');
  return segments.length > 1 ? segments.pop().toLowerCase() : '';
};

const isAiChatImageAttachment = (attachment) => {
  const extension = getAiChatAttachmentExtension(attachment?.fileName);
  return String(attachment?.mimeType || '').startsWith('image/')
    || ['png', 'jpg', 'jpeg', 'webp', 'gif', 'bmp', 'svg'].includes(extension);
};

const getAiChatAttachmentBadge = (attachment) => {
  const extension = getAiChatAttachmentExtension(attachment?.fileName);

  if (isAiChatImageAttachment(attachment)) {
    return 'IMG';
  }

  if (['pdf'].includes(extension)) {
    return 'PDF';
  }

  if (['ppt', 'pptx'].includes(extension)) {
    return 'PPT';
  }

  if (['xls', 'xlsx'].includes(extension)) {
    return 'XLS';
  }

  if (['doc', 'docx'].includes(extension)) {
    return 'DOC';
  }

  if (['txt'].includes(extension)) {
    return 'TXT';
  }

  if (['md', 'markdown'].includes(extension)) {
    return 'MD';
  }

  if (['json'].includes(extension)) {
    return 'JSON';
  }

  return (extension || 'FILE').slice(0, 4).toUpperCase();
};

const formatAiChatAttachmentSize = (size) => {
  const normalizedSize = Number(size || 0);

  if (!Number.isFinite(normalizedSize) || normalizedSize <= 0) {
    return '';
  }

  if (normalizedSize >= 1024 * 1024) {
    return `${(normalizedSize / (1024 * 1024)).toFixed(1)} MB`;
  }

  if (normalizedSize >= 1024) {
    return `${Math.round(normalizedSize / 1024)} KB`;
  }

  return `${normalizedSize} B`;
};

const loadAiChatImageElement = (sourceUrl) => new Promise((resolve, reject) => {
  const image = new Image();
  image.decoding = 'async';
  image.onload = () => resolve(image);
  image.onerror = () => reject(new Error('Failed to load AI chat image preview.'));
  image.src = sourceUrl;
});

const buildAiChatImageThumbnail = async (attachment) => {
  const sourceUrl = String(attachment?.url || '').trim();
  const mimeType = String(attachment?.mimeType || '').toLowerCase();

  if (!sourceUrl) {
    return '';
  }

  if (mimeType === 'image/svg+xml') {
    return sourceUrl;
  }

  if (aiChatImageThumbnailCache.has(sourceUrl)) {
    return aiChatImageThumbnailCache.get(sourceUrl);
  }

  const image = await loadAiChatImageElement(sourceUrl);
  const longestEdge = Math.max(image.naturalWidth || 0, image.naturalHeight || 0);
  const scale = longestEdge > AI_CHAT_THUMBNAIL_MAX_EDGE
    ? AI_CHAT_THUMBNAIL_MAX_EDGE / longestEdge
    : 1;

  if (scale >= 0.999) {
    aiChatImageThumbnailCache.set(sourceUrl, sourceUrl);
    return sourceUrl;
  }

  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round((image.naturalWidth || AI_CHAT_THUMBNAIL_MAX_EDGE) * scale));
  canvas.height = Math.max(1, Math.round((image.naturalHeight || AI_CHAT_THUMBNAIL_MAX_EDGE) * scale));
  const context = canvas.getContext('2d', { alpha: true });

  if (!context) {
    aiChatImageThumbnailCache.set(sourceUrl, sourceUrl);
    return sourceUrl;
  }

  context.drawImage(image, 0, 0, canvas.width, canvas.height);

  const thumbnailBlob = await new Promise((resolve) => {
    canvas.toBlob(resolve, 'image/webp', 0.82);
  });

  if (!thumbnailBlob) {
    aiChatImageThumbnailCache.set(sourceUrl, sourceUrl);
    return sourceUrl;
  }

  const thumbnailUrl = URL.createObjectURL(thumbnailBlob);
  aiChatImageThumbnailCache.set(sourceUrl, thumbnailUrl);
  return thumbnailUrl;
};

const areAiChatAttachmentsEqual = (previousAttachments, nextAttachments) => {
  const left = Array.isArray(previousAttachments) ? previousAttachments : [];
  const right = Array.isArray(nextAttachments) ? nextAttachments : [];

  if (left.length !== right.length) {
    return false;
  }

  return left.every((attachment, index) => {
    const nextAttachment = right[index];
    return attachment?.fileName === nextAttachment?.fileName
      && attachment?.url === nextAttachment?.url
      && attachment?.mimeType === nextAttachment?.mimeType
      && attachment?.size === nextAttachment?.size
      && attachment?.kind === nextAttachment?.kind;
  });
};

// Each send is a new server session record, but the conversation continues (one hub session per
// chat), so earlier messages stay and only this record's messages are added or updated in place.
const mergeChatMessages = (prevMessages, nextMessages) => {
  if (!Array.isArray(nextMessages) || nextMessages.length === 0) {
    return prevMessages;
  }

  let changed = false;
  const nextById = new Map(nextMessages.map((message) => [message.id, message]));
  const isSameMessage = (previousMessage, message) => (
    previousMessage.role === message.role
    && previousMessage.content === message.content
    && previousMessage.status === message.status
    && previousMessage.variant === message.variant
    && previousMessage.tool === message.tool
    && areAiChatAttachmentsEqual(previousMessage.attachments, message.attachments)
  );
  const mergedMessages = prevMessages.map((previousMessage) => {
    const message = nextById.get(previousMessage.id);
    if (!message || isSameMessage(previousMessage, message)) {
      return previousMessage;
    }
    changed = true;
    return message;
  });
  const knownIds = new Set(prevMessages.map((message) => message.id));
  nextMessages.forEach((message) => {
    if (!knownIds.has(message.id)) {
      changed = true;
      mergedMessages.push(message);
    }
  });

  return changed ? mergedMessages : prevMessages;
};

const createAiChatHistoryId = () => `ai-chat-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

const getAiChatMessageText = (message) => String(message?.content || '').replace(/\s+/g, ' ').trim();

const buildAiChatHistoryTitle = (messages) => {
  const userMessage = (Array.isArray(messages) ? messages : []).find((message) => message?.role === 'user' && getAiChatMessageText(message));
  const firstMessage = userMessage || (Array.isArray(messages) ? messages : []).find((message) => getAiChatMessageText(message));
  const title = getAiChatMessageText(firstMessage);

  if (!title) {
    return 'New chat';
  }

  return title.length > 48 ? `${title.slice(0, 45)}...` : title;
};

const buildAiChatHistoryPreview = (messages) => {
  const usableMessages = (Array.isArray(messages) ? messages : []).filter((message) => getAiChatMessageText(message));
  const lastMessage = usableMessages[usableMessages.length - 1];
  const preview = getAiChatMessageText(lastMessage);

  if (!preview) {
    return 'No messages yet';
  }

  return preview.length > 72 ? `${preview.slice(0, 69)}...` : preview;
};

const formatAiChatHistoryTimestamp = (timestamp) => {
  const date = new Date(Number(timestamp) || Date.now());

  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const normalizeStoredAiChatHistorySessions = (value) => (
  Array.isArray(value)
    ? value
        .filter((session) => session && typeof session === 'object' && Array.isArray(session.messages))
        .map((session) => ({
          id: String(session.id || createAiChatHistoryId()),
          title: String(session.title || buildAiChatHistoryTitle(session.messages)),
          preview: String(session.preview || buildAiChatHistoryPreview(session.messages)),
          messages: session.messages,
          target: normalizeChatTargetId(session.target),
          // Models are provider-specific and come from the hub — keep any saved id.
          model: session.model ? String(session.model) : 'auto',
          statusText: String(session.statusText || 'Ready for a new chat.'),
          createdAt: Number(session.createdAt) || Date.now(),
          updatedAt: Number(session.updatedAt) || Date.now()
        }))
        .sort((left, right) => Number(right.updatedAt || 0) - Number(left.updatedAt || 0))
        .slice(0, AI_CHAT_HISTORY_LIMIT)
    : []
);

const loadStoredAiChatHistorySessions = () => {
  try {
    if (typeof window === 'undefined' || !window.localStorage) {
      return [];
    }

    return normalizeStoredAiChatHistorySessions(JSON.parse(window.localStorage.getItem(AI_CHAT_HISTORY_STORAGE_KEY) || '[]'));
  } catch (error) {
    return [];
  }
};

const AiChatImagePreview = React.memo(({ attachment, onLoad }) => {
  const sourceUrl = String(attachment?.url || '').trim();
  const [previewUrl, setPreviewUrl] = useState(() => aiChatImageThumbnailCache.get(sourceUrl) || sourceUrl);

  useEffect(() => {
    let isCancelled = false;

    if (!sourceUrl) {
      setPreviewUrl('');
      return undefined;
    }

    if (aiChatImageThumbnailCache.has(sourceUrl)) {
      setPreviewUrl(aiChatImageThumbnailCache.get(sourceUrl) || sourceUrl);
      return undefined;
    }

    buildAiChatImageThumbnail(attachment)
      .then((nextPreviewUrl) => {
        if (!isCancelled) {
          setPreviewUrl(nextPreviewUrl || sourceUrl);
        }
      })
      .catch(() => {
        if (!isCancelled) {
          setPreviewUrl(sourceUrl);
        }
      });

    return () => {
      isCancelled = true;
    };
  }, [attachment, sourceUrl]);

  return (
    <a
      className="ai-chat-attachment-preview"
      href={attachment.url}
      target="_blank"
      rel="noreferrer"
    >
      <img
        src={previewUrl || sourceUrl}
        alt={attachment.fileName || 'AI generated file'}
        loading="lazy"
        decoding="async"
        fetchPriority="low"
        onLoad={onLoad}
      />
    </a>
  );
});

const AiChatAttachmentCard = React.memo(({ attachment, onPreviewLoad }) => {
  const isImage = isAiChatImageAttachment(attachment);
  const sizeLabel = formatAiChatAttachmentSize(attachment.size);
  const badgeLabel = getAiChatAttachmentBadge(attachment);

  return (
    <article className={`ai-chat-attachment-card ${isImage ? 'image' : 'file'}`.trim()}>
      {isImage ? (
        <AiChatImagePreview attachment={attachment} onLoad={onPreviewLoad} />
      ) : (
        <div className="ai-chat-attachment-filehead">
          <div className="ai-chat-attachment-icon" aria-hidden="true">{badgeLabel}</div>
          <div className="ai-chat-attachment-meta">
            <strong className="ai-chat-attachment-name">{attachment.fileName}</strong>
            <span className="ai-chat-attachment-subtitle">
              {[badgeLabel, sizeLabel].filter(Boolean).join(' / ')}
            </span>
          </div>
        </div>
      )}
      {isImage ? (
        <div className="ai-chat-attachment-meta">
          <strong className="ai-chat-attachment-name">{attachment.fileName}</strong>
          <span className="ai-chat-attachment-subtitle">
            {[badgeLabel, sizeLabel].filter(Boolean).join(' / ')}
          </span>
        </div>
      ) : null}
      <div className="ai-chat-attachment-actions">
        {isImage ? (
          <a
            className="ai-chat-attachment-link"
            href={attachment.url}
            target="_blank"
            rel="noreferrer"
          >
            View
          </a>
        ) : null}
        <a className="ai-chat-attachment-link" href={attachment.url} download={attachment.fileName}>
          Download
        </a>
      </div>
    </article>
  );
});

const AiChatMessageBubble = React.memo(({ message, onAttachmentLoad }) => {
  const role = message.role || 'assistant';
  const roleLabel = role === 'user' ? 'You' : (role === 'assistant' ? 'Assistant' : 'System');
  const isTyping = role === 'assistant' && !String(message.content || '').trim() && message.status === 'streaming';
  const isCompleted = message.status === 'complete' || message.status === 'completed';
  const statusLabel = isCompleted
    ? 'Completed'
    : (message.status === 'streaming' ? 'Working' : '');
  const attachments = Array.isArray(message.attachments) ? message.attachments : [];

  return (
    <div className={`ai-chat-message ${role}${message.variant === 'error' ? ' error' : ''}`.trim()}>
      <div className="ai-chat-message-meta">
        {role !== 'system' ? <span className="ai-chat-message-avatar" aria-hidden="true">{role === 'user' ? 'Y' : 'C'}</span> : null}
        <span>{roleLabel}</span>
        {statusLabel ? <span className={`ai-chat-status-chip ${isCompleted ? 'completed' : 'working'}`.trim()}>{statusLabel}</span> : null}
        {message.tool ? <span className="ai-chat-tool-chip">{CHAT_TOOL_LABELS[message.tool] || message.tool}</span> : null}
      </div>
      <div className="ai-chat-message-bubble">
        {isTyping ? (
          <div className="ai-chat-typing" aria-label="Assistant is typing">
            <span></span>
            <span></span>
            <span></span>
          </div>
        ) : (
          <>
            {String(message.content || '').trim() ? (
              <div className="ai-chat-message-text">{String(message.content || '')}</div>
            ) : null}
            {attachments.length > 0 ? (
              <div className="ai-chat-attachments">
                {attachments.map((attachment, index) => (
                  <AiChatAttachmentCard
                    key={`${attachment.url || attachment.fileName || 'attachment'}-${index}`}
                    attachment={attachment}
                    onPreviewLoad={onAttachmentLoad}
                  />
                ))}
              </div>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
});

const getIconDisplayName = (iconValue) => {
  if (!iconValue) {
    return '';
  }

  if (iconValue.startsWith('/user-assets/icons/')) {
    return iconValue.split('/').pop() || iconValue;
  }

  if (iconValue.startsWith('/icons/')) {
    return iconValue.split('/').pop() || iconValue;
  }

  return iconValue;
};

const SettingsShortcutPreview = ({ iconId, iconValue, altText }) => {
  const [keywordExtensionIndex, setKeywordExtensionIndex] = useState(0);
  const [sourceIndex, setSourceIndex] = useState(0);

  useEffect(() => {
    setKeywordExtensionIndex(0);
    setSourceIndex(0);
  }, [iconId, iconValue]);

  const fallbackSources = PREVIEW_ICON_SOURCE_FALLBACKS[iconId] || [];
  const emojiFallback = PREVIEW_ICON_EMOJI_FALLBACKS[iconId] || '?';

  if (iconValue === PREVIEW_SPECIAL_ICON_IDS.auth) {
    return (
      <div className="special-shortcut-icon special-shortcut-icon-auth" aria-label={altText}>
        <div className="special-shortcut-auth-core">
          <span className="special-shortcut-auth-mark">C</span>
        </div>
        <span className="special-shortcut-auth-badge">AI</span>
      </div>
    );
  }

  if (iconValue === PREVIEW_SPECIAL_ICON_IDS.chat) {
    return (
      <div className="special-shortcut-icon special-shortcut-icon-chat" aria-label={altText}>
        <span className="special-shortcut-chat-label">Chat</span>
      </div>
    );
  }

  if (!iconValue) {
    return <span className="icons-preview-emoji">{emojiFallback}</span>;
  }

  if (iconValue.includes('/') || iconValue.includes('.')) {
    const imageSources = [iconValue, ...fallbackSources.filter((source) => source !== iconValue)];
    const activeSource = imageSources[sourceIndex];

    if (!activeSource) {
      return <span className="icons-preview-emoji">{emojiFallback}</span>;
    }

    return (
      <img
        src={activeSource}
        alt={altText}
        draggable="false"
        onError={() => {
          setSourceIndex((prev) => prev + 1);
        }}
      />
    );
  }

  const isKeyword = /^[a-zA-Z0-9-_]+$/.test(iconValue);

  if (isKeyword) {
    if (sourceIndex > 0) {
      const fallbackSource = fallbackSources[sourceIndex - 1];

      if (fallbackSource) {
        return (
          <img
            src={fallbackSource}
            alt={altText}
            draggable="false"
            onError={() => {
              setSourceIndex((prev) => prev + 1);
            }}
          />
        );
      }

      return <span className="icons-preview-emoji">{emojiFallback}</span>;
    }

    const extension = PREVIEW_ICON_EXTENSIONS[keywordExtensionIndex];

    return (
      <img
        src={resolvePreviewIconSource(iconValue, extension)}
        alt={altText}
        draggable="false"
        onError={() => {
          if (keywordExtensionIndex < PREVIEW_ICON_EXTENSIONS.length - 1) {
            setKeywordExtensionIndex((prev) => prev + 1);
            return;
          }

          setSourceIndex((prev) => prev + 1);
        }}
      />
    );
  }

  return <span className="icons-preview-emoji">{iconValue || emojiFallback}</span>;
};

const isUploadedIconPath = (iconValue) => typeof iconValue === 'string' && iconValue.startsWith('/user-assets/icons/');

const WallpaperCard = ({ label, selected, assignedThemes = [], onClick, previewStyle, onDelete }) => (
  <div className={`wallpaper-card-shell ${onDelete ? 'deletable' : ''}`}>
    <button
      type="button"
      className={`wallpaper-card ${selected ? 'selected' : ''}`.trim()}
      onClick={onClick}
      title={label}
    >
      {assignedThemes.length > 0 ? (
        <span className="wallpaper-card-badges" aria-hidden="true">
          {assignedThemes.includes('light') ? (
            <span className="wallpaper-card-badge wallpaper-card-badge-light">L</span>
          ) : null}
          {assignedThemes.includes('dark') ? (
            <span className="wallpaper-card-badge wallpaper-card-badge-dark">D</span>
          ) : null}
          {assignedThemes.includes('landing') ? (
            <span className="wallpaper-card-badge wallpaper-card-badge-landing">B</span>
          ) : null}
        </span>
      ) : null}
      <span className="wallpaper-card-preview" style={previewStyle}></span>
      <span className="wallpaper-card-label">{label}</span>
    </button>
    {onDelete ? (
      <button
        type="button"
        className="wallpaper-card-delete"
        onClick={(event) => {
          event.stopPropagation();
          onDelete();
        }}
        aria-label={`Delete ${label}`}
        title={`Delete ${label}`}
      >
        x
      </button>
    ) : null}
  </div>
);

const getWindowSize = (appId) => DEFAULT_WINDOW_SIZES[appId] || DEFAULT_WINDOW_SIZES.default;
const getMinWindowSize = (appId) => MIN_WINDOW_SIZES[appId] || MIN_WINDOW_SIZES.default;
const DEFAULT_DESKTOP_LAYOUT = resolveDesktopLayout('mac');
const getSafeArea = (desktopLayout) => desktopLayout?.safeArea || DEFAULT_DESKTOP_LAYOUT.safeArea;
const getWindowBottomBoundary = (desktopMetrics, desktopLayout) => (
  Number.isFinite(desktopMetrics?.bottomBoundaryY)
    ? desktopMetrics.bottomBoundaryY
    : ((desktopMetrics?.height || window.innerHeight) - getSafeArea(desktopLayout).bottom)
);

const clampWindowSize = (size, position, desktopMetrics, minSize, desktopLayout) => {
  const safeArea = getSafeArea(desktopLayout);
  const availableWidth = Math.max(0, ((desktopMetrics?.width || window.innerWidth) - safeArea.right) - (position?.x || safeArea.left));
  const availableHeight = Math.max(0, getWindowBottomBoundary(desktopMetrics, desktopLayout) - (position?.y || safeArea.top));
  const minimumWidth = Math.min(minSize?.width || 0, availableWidth);
  const minimumHeight = Math.min(minSize?.height || 0, availableHeight);

  return {
    width: Math.min(Math.max(size.width, minimumWidth), availableWidth),
    height: Math.min(Math.max(size.height, minimumHeight), availableHeight)
  };
};

const clampWindowPosition = (position, size, desktopMetrics, desktopLayout) => {
  const safeArea = getSafeArea(desktopLayout);
  const maxX = Math.max(safeArea.left, desktopMetrics.width - safeArea.right - size.width);
  const maxY = Math.max(safeArea.top, getWindowBottomBoundary(desktopMetrics, desktopLayout) - size.height);

  return {
    x: Math.min(Math.max(position.x, safeArea.left), maxX),
    y: Math.min(Math.max(position.y, safeArea.top), maxY)
  };
};

const getWindowPosition = (appId, desktopLayout = DEFAULT_DESKTOP_LAYOUT) => {
  const safeArea = getSafeArea(desktopLayout);

  if (appId === 'ai-auth') {
    return { x: safeArea.left + 138, y: safeArea.top + 42 };
  }

  if (appId === 'ai-chat') {
    return { x: safeArea.left + 158, y: safeArea.top + 50 };
  }

  if (appId === 'profile') {
    return { x: safeArea.left + 108, y: safeArea.top + 28 };
  }

  if (appId === 'settings') {
    return { x: safeArea.left + 168, y: safeArea.top + 48 };
  }

  if (appId === 'profile-settings') {
    return { x: safeArea.left + 128, y: safeArea.top + 38 };
  }

  if (appId === 'resume-settings') {
    return { x: safeArea.left + 168, y: safeArea.top + 58 };
  }

  if (appId === 'projects-settings') {
    return { x: safeArea.left + 148, y: safeArea.top + 48 };
  }

  if (appId === 'projects') {
    return { x: safeArea.left + 60, y: safeArea.top + 36 };
  }

  return { x: safeArea.left + 188, y: safeArea.top + 58 };
};

const COMPACT_CONTENT_SCALE = 0.8;

const WindowContentViewport = React.memo(({ className, style, children, isCompactContent = false }) => {
  const compactContentRef = useRef(null);
  const [compactContentHeight, setCompactContentHeight] = useState(null);
  const isFullHeightCompactContent = isCompactContent && (
    className.includes('window-content-scroll-ai-chat')
    || className.includes('window-content-scroll-change-os')
  );

  useEffect(() => {
    if (!isCompactContent || isFullHeightCompactContent || !compactContentRef.current) {
      setCompactContentHeight(null);
      return undefined;
    }

    const contentNode = compactContentRef.current;
    let animationFrameId = 0;

    const updateCompactHeight = () => {
      window.cancelAnimationFrame(animationFrameId);
      animationFrameId = window.requestAnimationFrame(() => {
        setCompactContentHeight(Math.ceil(contentNode.scrollHeight * COMPACT_CONTENT_SCALE));
      });
    };

    updateCompactHeight();

    const resizeObserver = new ResizeObserver(updateCompactHeight);
    resizeObserver.observe(contentNode);
    window.addEventListener('resize', updateCompactHeight);

    return () => {
      window.cancelAnimationFrame(animationFrameId);
      resizeObserver.disconnect();
      window.removeEventListener('resize', updateCompactHeight);
    };
  }, [children, isCompactContent, isFullHeightCompactContent]);

  return (
    <div className={className} style={style}>
      {isCompactContent ? (
        <div
          className={`window-content-compact-frame ${isFullHeightCompactContent ? 'window-content-compact-frame-full' : ''}`.trim()}
          style={compactContentHeight ? { height: `${compactContentHeight}px` } : undefined}
        >
          <div ref={compactContentRef} className="window-content-compact-scale">
            {children}
          </div>
        </div>
      ) : children}
    </div>
  );
});

const WindowFrame = React.memo(({
  draggableKey,
  nodeRef,
  dragBounds,
  isMaximized,
  maximizedFrame,
  clampedWindowPosition,
  activeTheme,
  osMode,
  isFocused,
  isResizable,
  isVisuallyHidden,
  isInteracting,
  frameStyle,
  localizedWindowTitle,
  onFocus,
  onDragStart,
  onDragStop,
  onClose,
  onMinimize,
  onToggleMaximize,
  appId,
  windowContentClassName,
  windowContentStyle,
  windowMenuNode,
  contentNode,
  overlayNode,
  portfolioImportMessage,
  onResizeStart,
  isPublicShareView,
  isCompactContent
}) => {
  const resolvedOsMode = normalizeOsMode(osMode);
  const handleHeaderDoubleClick = (event) => {
    if (event.target.closest('.window-control')) {
      return;
    }

    onToggleMaximize(appId);
  };
  const controlButtons = (
    <>
      {resolvedOsMode === 'mac' ? (
        <>
          <button type="button" className="window-control close" onClick={() => onClose(appId)} aria-label="Close window">x</button>
          <button type="button" className="window-control minimize" onClick={() => onMinimize(appId)} aria-label="Minimize window">-</button>
          <button type="button" className="window-control maximize" onClick={() => onToggleMaximize(appId)} aria-label="Toggle maximize window">+</button>
        </>
      ) : (
        <>
          <button type="button" className="window-control minimize" onClick={() => onMinimize(appId)} aria-label="Minimize window">-</button>
          <button type="button" className="window-control maximize" onClick={() => onToggleMaximize(appId)} aria-label="Toggle maximize window">+</button>
          <button type="button" className="window-control close" onClick={() => onClose(appId)} aria-label="Close window">x</button>
        </>
      )}
    </>
  );

  if (isPublicShareView) {
    return (
      <div
        ref={nodeRef}
        data-window-id={appId}
        className={`window-frame public-share-window window-os-${resolvedOsMode} ${activeTheme === 'dark' ? 'window-dark' : 'window-light'}`.trim()}
        style={frameStyle}
      >
        <WindowContentViewport className={windowContentClassName} style={windowContentStyle} isCompactContent={isCompactContent}>
          {contentNode}
        </WindowContentViewport>
        {overlayNode}
        {portfolioImportMessage ? <div className="portfolio-import-toast upload-success">{portfolioImportMessage}</div> : null}
      </div>
    );
  }

  return (
    <Draggable
    key={draggableKey}
    nodeRef={nodeRef}
    handle=".window-header"
    cancel=".window-control,.window-management-bar,.window-management-button"
    bounds={dragBounds}
      disabled={isMaximized}
      defaultPosition={isMaximized ? undefined : clampedWindowPosition}
      position={isMaximized ? { x: maximizedFrame.x, y: maximizedFrame.y } : undefined}
      onStart={onDragStart}
      onStop={onDragStop}
    >
      <div
        ref={nodeRef}
        data-window-id={appId}
        className={`window-frame glass-effect window-os-${resolvedOsMode} ${isFocused ? 'focused' : ''} ${activeTheme === 'dark' ? 'window-dark' : 'window-light'} ${isResizable ? 'window-resizable' : ''} ${isMaximized ? 'window-maximized' : ''} ${isVisuallyHidden ? 'window-transition-hidden' : ''} ${isInteracting ? 'window-interacting' : ''}`.trim()}
        style={frameStyle}
        onMouseDownCapture={() => onFocus(appId)}
      >
        <div className="window-header" onDoubleClick={handleHeaderDoubleClick}>
          {resolvedOsMode === 'mac' ? (
            <>
              <div className="traffic-lights">{controlButtons}</div>
              <span className="window-title">{localizedWindowTitle}</span>
            </>
          ) : (
            <>
              <span className="window-title window-title-inline">{localizedWindowTitle}</span>
              <div className={`window-controls-inline window-controls-inline-${resolvedOsMode}`.trim()}>{controlButtons}</div>
            </>
          )}
        </div>
        {windowMenuNode}
        <WindowContentViewport className={windowContentClassName} style={windowContentStyle} isCompactContent={isCompactContent}>
          {contentNode}
        </WindowContentViewport>
        {overlayNode}
        {portfolioImportMessage ? <div className="portfolio-import-toast upload-success">{portfolioImportMessage}</div> : null}
        {isResizable ? <div className="window-resize-handle" onMouseDown={onResizeStart}></div> : null}
      </div>
    </Draggable>
  );
});

const Window = ({
  app,
  onClose,
  onMinimize,
  onToggleMaximize,
  isFocused,
  isMaximized,
  onFocus,
  config,
  onSettingChange,
  onProfileDataChange,
  onResumeDataChange,
  onProjectsDataChange,
  onDesktopIconsChange,
  onOpenApp,
  onWindowLayoutChange,
  isAdminAuthPassEnabled,
  isAdminAuthenticated,
  onAdminAuthPassToggle,
  onAdminLogout,
  aiToolsStatus,
  aiToolsLoading,
  onRefreshAiToolsStatus,
  fileMenuRequest,
  onConsumeFileMenuRequest,
  onShowToast,
  onQueuePersistToast,
  osMode,
  desktopLayout,
  onProtectedMenuAction,
  onRequestProtectedFileImport,
  onPortfolioImportProcessingChange,
  ensureAdminPortalSession,
  projectViewIndex,
  onProjectViewIndexChange,
  projectImages: sharedProjectImages,
  onProjectImagesChange,
  projectImageLimit: sharedProjectImageLimit,
  onProjectImageLimitChange,
  isPublicShareView = false,
  isVisuallyHidden = false
}) => {
  const resolvedOsMode = normalizeOsMode(osMode || config.settings?.osMode);
  const resolvedDesktopLayout = desktopLayout || resolveDesktopLayout(resolvedOsMode);
  const safeArea = getSafeArea(resolvedDesktopLayout);
  const nodeRef = useRef(null);
  const resizeStateRef = useRef(null);
  const isDraggingRef = useRef(false);
  const isResizingRef = useRef(false);
  const previousFrameRef = useRef(null);
  const initialWindowPosition = app.windowPosition || getWindowPosition(app.id, resolvedDesktopLayout);
  const windowSizeRef = useRef(app.windowSize || getWindowSize(app.id));
  const windowPositionRef = useRef(initialWindowPosition);
  const lastCommittedLayoutRef = useRef({
    windowSize: app.windowSize || getWindowSize(app.id),
    windowPosition: initialWindowPosition
  });
  const wallpaperInputRef = useRef(null);
  const profilePhotoInputRef = useRef(null);
  const iconInputRefs = useRef({});
  const importFileInputRef = useRef(null);
  const aiChatFileInputRef = useRef(null);
  const [desktopMetrics, setDesktopMetrics] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
    bottomBoundaryY: window.innerHeight - safeArea.bottom
  });
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(false);
  const [wallpapers, setWallpapers] = useState([]);
  const [wallpapersLoading, setWallpapersLoading] = useState(false);
  const [deletingWallpaperId, setDeletingWallpaperId] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [uploadMessage, setUploadMessage] = useState('');
  const [iconMessage, setIconMessage] = useState('');
  const [iconError, setIconError] = useState('');
  const [uploadingIconId, setUploadingIconId] = useState('');
  const [deletingIconId, setDeletingIconId] = useState('');
  const [draggedIconId, setDraggedIconId] = useState('');
  const [dragOverIconId, setDragOverIconId] = useState('');
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [profileDraft, setProfileDraft] = useState(() => cloneDraftValue(config.profileData || {}));
  const [profileSaveMessage, setProfileSaveMessage] = useState('');
  const [resumeDraft, setResumeDraft] = useState(() => cloneDraftValue(config.resumeData || {}));
  const [resumeSaveMessage, setResumeSaveMessage] = useState('');
  const [projectsDraft, setProjectsDraft] = useState(() => cloneDraftValue(config.projectsData || {}));
  const [projectsSaveMessage, setProjectsSaveMessage] = useState('');
  const [saveClosePrompt, setSaveClosePrompt] = useState(null);
  const [initializePrompt, setInitializePrompt] = useState(null);
  const [currentProjectIndex, setCurrentProjectIndexState] = useState(
    () => (Number.isFinite(projectViewIndex) ? projectViewIndex : 0)
  );
  // Per-project gallery. Kept in its own state rather than inside projectsData because the
  // projects editor round-trips that document through the server's schema normalizer, which
  // would drop anything the schema does not declare.
  // The desktop owns these so both Projects windows see the same galleries. The public share
  // page renders a Window on its own, with no desktop above it, so a local copy stands in.
  const [ownProjectImages, setOwnProjectImages] = useState({});
  const [ownProjectImageLimit, setOwnProjectImageLimit] = useState(30);
  const projectImages = sharedProjectImages || ownProjectImages;
  const setProjectImages = onProjectImagesChange || setOwnProjectImages;
  const projectImageLimit = Number.isFinite(sharedProjectImageLimit) ? sharedProjectImageLimit : ownProjectImageLimit;
  const setProjectImageLimit = onProjectImageLimitChange || setOwnProjectImageLimit;
  const [projectImageBusy, setProjectImageBusy] = useState(false);
  const [projectImageError, setProjectImageError] = useState('');
  // Which image of the current project the slider is showing.
  const [projectImageIndex, setProjectImageIndex] = useState(0);
  const [projectImageStatus, setProjectImageStatus] = useState('');
  const [projectImageProgress, setProjectImageProgress] = useState(0);
  // Bumped when another window reports a gallery change; re-runs the fetch effect below.
  const [projectImagesTick, setProjectImagesTick] = useState(0);
  const projectImageInputRef = useRef(null);
  const projectComfyRunRef = useRef(0);
  const [shortcutLabelDrafts, setShortcutLabelDrafts] = useState(() => buildShortcutLabelDrafts(config.desktopIcons, normalizeLanguage(config.settings.language || 'english')));
  const [aiToolsError, setAiToolsError] = useState('');
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [selectedImportTarget, setSelectedImportTarget] = useState('codex');
  const [aiChatTarget, setAiChatTarget] = useState('codex');
  const [aiChatModel, setAiChatModel] = useState('auto');
  // Per-target model list from the hub.
  const [aiChatAvailableModels, setAiChatAvailableModels] = useState([]);
  const [aiChatModelsLoading, setAiChatModelsLoading] = useState(false);
  const [aiChatPrompt, setAiChatPrompt] = useState('');
  const [aiChatAttachment, setAiChatAttachment] = useState(null);
  const [aiChatMessages, setAiChatMessages] = useState([]);
  const [aiChatSessionId, setAiChatSessionId] = useState('');
  const [aiChatHistorySessions, setAiChatHistorySessions] = useState(() => loadStoredAiChatHistorySessions());
  const [activeAiChatHistoryId, setActiveAiChatHistoryId] = useState(() => createAiChatHistoryId());
  const [isAiChatHistoryOpen, setIsAiChatHistoryOpen] = useState(true);
  const [aiChatStatusText, setAiChatStatusText] = useState('Ready for a new chat.');
  const [aiChatSending, setAiChatSending] = useState(false);
  const [isAiChatPollingActive, setIsAiChatPollingActive] = useState(false);
  const [importingPortfolioFile, setImportingPortfolioFile] = useState(false);
  const [portfolioImportError, setPortfolioImportError] = useState('');
  const [portfolioImportMessage, setPortfolioImportMessage] = useState('');
  const [portfolioImportSuggestions, setPortfolioImportSuggestions] = useState([]);
  const [portfolioImportContinuationSourceId, setPortfolioImportContinuationSourceId] = useState('');
  const [resettingPortfolioTarget, setResettingPortfolioTarget] = useState('');
  const [windowSize, setWindowSize] = useState(() => app.windowSize || getWindowSize(app.id));
  const [windowPosition, setWindowPosition] = useState(() => initialWindowPosition);
  const [isInteracting, setIsInteracting] = useState(false);
  const [wallpaperSelectionTarget, setWallpaperSelectionTarget] = useState('theme');
  const aiAuthContentRef = useRef(null);
  const activeTheme = config.settings.theme || 'dark';
  const activeLanguage = normalizeLanguage(config.settings.language || 'english');
  const initializeActionLabel = activeLanguage === 'korean' ? '초기화' : 'initialize';
  const localizedWindowTitle = getLocalizedAppLabel(app.id, app.label, activeLanguage);
  const windowEditorTargetById = {
    profile: 'profile-settings',
    resume: 'resume-settings',
    projects: 'projects-settings'
  };
  const inactiveWindowMenuItemIds = new Set(['view', 'go', 'help']);
  const shouldRenderWindowMenu = WINDOW_MANAGEMENT_IDS.includes(app.id);
  const windowMenuItems = shouldRenderWindowMenu
    ? [
        { id: 'file', label: t(activeLanguage, 'menu_file') },
        { id: 'edit', label: t(activeLanguage, 'menu_edit') },
        { id: 'view', label: t(activeLanguage, 'menu_view') },
        { id: 'go', label: t(activeLanguage, 'menu_go') },
        { id: 'window', label: t(activeLanguage, 'menu_window') },
        { id: 'help', label: t(activeLanguage, 'menu_help') }
      ]
    : [];
  const activeThemeWallpaper = config.settings.themeWallpapers?.[activeTheme] || config.settings.wallpaper;
  const landingPageSettings = config.settings.landingPage || {};
  const authenticatedImportTargets = CHAT_TARGET_OPTIONS
    .filter((target) => target.importable)
    .filter((target) => isChatTargetAuthenticated(aiToolsStatus, target.id))
    .map((target) => target.id);
  // Every target stays selectable (so its "not ready" reason can be shown); sending needs ready.
  const selectableChatTargets = CHAT_TARGET_OPTIONS.map((target) => target.id);
  const showAiChatErrorToast = useCallback((message) => {
    if (!message) {
      return;
    }

    onShowToast?.(message, 'error');
  }, [onShowToast]);
  useEffect(() => {
    try {
      if (typeof window === 'undefined' || !window.localStorage) {
        return;
      }

      window.localStorage.setItem(AI_CHAT_HISTORY_STORAGE_KEY, JSON.stringify(aiChatHistorySessions));
    } catch (error) {
      // Browser storage can be unavailable or full; the active chat should keep working.
    }
  }, [aiChatHistorySessions]);
  useEffect(() => {
    if (app.id !== 'ai-chat' || aiChatMessages.length === 0) {
      return;
    }

    const now = Date.now();
    setAiChatHistorySessions((prevSessions) => {
      const existingSession = prevSessions.find((session) => session.id === activeAiChatHistoryId);
      const nextSession = {
        id: activeAiChatHistoryId,
        title: buildAiChatHistoryTitle(aiChatMessages),
        preview: buildAiChatHistoryPreview(aiChatMessages),
        messages: aiChatMessages,
        target: aiChatTarget,
        model: aiChatModel,
        statusText: aiChatStatusText,
        createdAt: existingSession?.createdAt || now,
        updatedAt: now
      };
      return [
        nextSession,
        ...prevSessions.filter((session) => session.id !== activeAiChatHistoryId)
      ].slice(0, AI_CHAT_HISTORY_LIMIT);
    });
  }, [activeAiChatHistoryId, aiChatMessages, aiChatModel, aiChatStatusText, aiChatTarget, app.id]);
  const aiChatFeedRef = useRef(null);
  const aiChatShouldAutoScrollRef = useRef(true);
  const isResizable = RESIZABLE_WINDOW_IDS.includes(app.id);
  const minWindowSize = getMinWindowSize(app.id);
  const windowBottomBoundary = getWindowBottomBoundary(desktopMetrics, resolvedDesktopLayout);
  const maximizedFramePosition = { x: safeArea.left, y: safeArea.top };
  const maximizedFrameSize = clampWindowSize(
    {
      width: Math.max(0, desktopMetrics.width - safeArea.left - safeArea.right),
      height: Math.max(0, windowBottomBoundary - safeArea.top)
    },
    maximizedFramePosition,
    desktopMetrics,
    { width: 0, height: 0 },
    resolvedDesktopLayout
  );
  const maximizedFrame = {
    ...maximizedFramePosition,
    ...maximizedFrameSize
  };
  const clampedWindowPosition = clampWindowPosition(windowPosition, windowSize, desktopMetrics, resolvedDesktopLayout);
  const clampedWindowSize = clampWindowSize(windowSize, clampedWindowPosition, desktopMetrics, minWindowSize, resolvedDesktopLayout);
  const dragBounds = {
    left: safeArea.left,
    top: safeArea.top,
    right: Math.max(safeArea.left, desktopMetrics.width - safeArea.right - clampedWindowSize.width),
    bottom: Math.max(safeArea.top, windowBottomBoundary - clampedWindowSize.height)
  };
  const frameStyle = {
    zIndex: Number.isFinite(app.stackOrder) ? app.stackOrder : (isFocused ? 999 : 1),
    width: `${(isMaximized ? maximizedFrame.width : clampedWindowSize.width)}px`,
    height: `${(isMaximized ? maximizedFrame.height : clampedWindowSize.height)}px`
  };
  const resolvedFrameStyle = isPublicShareView
    ? {
        zIndex: 1,
        width: '100%',
        height: '100%'
      }
    : frameStyle;
  const windowContentStyle = undefined;
  const queueSettingsPersistToast = useCallback((message = 'Settings saved successfully.') => {
    onQueuePersistToast?.('config', message);
  }, [onQueuePersistToast]);
  const queueUserIconsPersistToast = useCallback((message = 'Shortcuts saved successfully.') => {
    onQueuePersistToast?.('userIcons', message);
  }, [onQueuePersistToast]);
  const updateAiChatStatusText = useCallback((nextStatusText) => {
    const normalizedStatusText = String(nextStatusText || '').trim() || 'Ready for a new chat.';
    setAiChatStatusText((previousStatusText) => (
      previousStatusText === normalizedStatusText ? previousStatusText : normalizedStatusText
    ));
  }, []);
  const updateAiChatAutoScrollState = useCallback(() => {
    const feedNode = aiChatFeedRef.current;

    if (!feedNode) {
      return;
    }

    const distanceToBottom = feedNode.scrollHeight - feedNode.scrollTop - feedNode.clientHeight;
    aiChatShouldAutoScrollRef.current = distanceToBottom <= AI_CHAT_AUTO_SCROLL_THRESHOLD;
  }, []);
  const scrollAiChatFeedToBottom = useCallback(() => {
    const feedNode = aiChatFeedRef.current;

    if (!feedNode) {
      return;
    }

    feedNode.scrollTop = feedNode.scrollHeight;
  }, []);
  const handleAiChatAttachmentLoad = useCallback(() => {
    if (!aiChatShouldAutoScrollRef.current) {
      return;
    }

    scrollAiChatFeedToBottom();
  }, [scrollAiChatFeedToBottom]);
  const applySettingChange = useCallback((key, value, toastMessage = 'Settings saved successfully.') => {
    queueSettingsPersistToast(toastMessage);
    onSettingChange(key, value);
  }, [onSettingChange, queueSettingsPersistToast]);
  const commitWindowLayout = useCallback((nextSize = windowSizeRef.current, nextPosition = windowPositionRef.current) => {
    const previousLayout = lastCommittedLayoutRef.current;
    const sizeChanged = previousLayout.windowSize.width !== nextSize.width
      || previousLayout.windowSize.height !== nextSize.height;
    const positionChanged = previousLayout.windowPosition.x !== nextPosition.x
      || previousLayout.windowPosition.y !== nextPosition.y;

    if (!sizeChanged && !positionChanged) {
      return;
    }

    lastCommittedLayoutRef.current = {
      windowSize: nextSize,
      windowPosition: nextPosition
    };
    onWindowLayoutChange(app.id, {
      windowSize: nextSize,
      windowPosition: nextPosition
    });
  }, [app.id, onWindowLayoutChange]);

  useEffect(() => {
    if (isResizingRef.current || isDraggingRef.current) {
      return;
    }

    const nextSize = app.windowSize || getWindowSize(app.id);
    if (nextSize.width === windowSizeRef.current.width && nextSize.height === windowSizeRef.current.height) {
      return;
    }

    setWindowSize(nextSize);
  }, [app.id, app.windowSize]);

  useEffect(() => {
    if (isResizingRef.current || isDraggingRef.current) {
      return;
    }

    const nextSize = app.windowSize || getWindowSize(app.id);
    const nextPosition = clampWindowPosition(
      app.windowPosition || getWindowPosition(app.id, resolvedDesktopLayout),
      nextSize,
      desktopMetrics,
      resolvedDesktopLayout
    );
    if (nextPosition.x === windowPositionRef.current.x && nextPosition.y === windowPositionRef.current.y) {
      return;
    }

    setWindowPosition(nextPosition);
  }, [app.id, app.windowPosition, app.windowSize, desktopMetrics, resolvedDesktopLayout]);

  useEffect(() => {
    windowSizeRef.current = windowSize;
  }, [windowSize]);

  useEffect(() => {
    windowPositionRef.current = windowPosition;
  }, [windowPosition]);

  useEffect(() => {
    lastCommittedLayoutRef.current = {
      windowSize: app.windowSize || getWindowSize(app.id),
      windowPosition: app.windowPosition || getWindowPosition(app.id, resolvedDesktopLayout)
    };
  }, [app.id, app.windowPosition, app.windowSize, resolvedDesktopLayout]);

  useEffect(() => {
    if (isDraggingRef.current || isResizingRef.current) {
      return;
    }

    commitWindowLayout(windowSize, windowPosition);
  }, [commitWindowLayout, windowPosition, windowSize]);

  useEffect(() => {
    setProfileDraft(cloneDraftValue(config.profileData || {}));
  }, [config.profileData]);

  useEffect(() => {
    setResumeDraft(cloneDraftValue(config.resumeData || {}));
  }, [config.resumeData]);

  useEffect(() => {
    setProjectsDraft(cloneDraftValue(config.projectsData || {}));
  }, [config.projectsData]);

  /**
   * One setter for both copies of the index: the window's own, and the shared one on the
   * desktop that the other Projects window reads. Call sites keep the useState signature,
   * so they can still pass a value or an updater.
   */
  const setCurrentProjectIndex = useCallback((updater) => {
    setCurrentProjectIndexState((previousIndex) => {
      const nextIndex = typeof updater === 'function' ? updater(previousIndex) : updater;
      if (nextIndex !== previousIndex) {
        onProjectViewIndexChange?.(nextIndex);
      }
      return nextIndex;
    });
  }, [onProjectViewIndexChange]);

  // Adopt a move made in the other window.
  useEffect(() => {
    if (Number.isFinite(projectViewIndex)) {
      setCurrentProjectIndexState(projectViewIndex);
    }
  }, [projectViewIndex]);

  // The gallery is public to read, so it loads for visitors and for the share view alike.
  useEffect(() => {
    if (app.id !== 'projects' && app.id !== 'projects-settings') {
      return undefined;
    }

    let cancelled = false;
    fetch('/api/projects/images')
      .then((response) => (response.ok ? response.json() : Promise.reject(new Error('load failed'))))
      .then((data) => {
        if (cancelled) return;
        setProjectImages(data.projects || {});
        if (Number.isFinite(data.limit)) setProjectImageLimit(data.limit);
      })
      .catch(() => {
        // Keep whatever is already loaded: this map is shared with the other Projects window,
        // and a transient fetch failure is no reason to blank its gallery.
      });

    return () => { cancelled = true; };
  }, [app.id, projectImagesTick, setProjectImageLimit, setProjectImages]);

  useEffect(() => {
    if (app.id !== 'projects' && app.id !== 'projects-settings') {
      return undefined;
    }
    const handleChanged = () => setProjectImagesTick((tick) => tick + 1);
    window.addEventListener(PROJECT_IMAGES_CHANGED_EVENT, handleChanged);
    return () => window.removeEventListener(PROJECT_IMAGES_CHANGED_EVENT, handleChanged);
  }, [app.id]);

  const getProjectImages = useCallback(
    (projectId) => (Array.isArray(projectImages[projectId]) ? projectImages[projectId] : []),
    [projectImages]
  );

  // Every mutation below is admin-gated on the server; prompting here first means the user
  // gets the OTP dialog instead of a bare 401 after picking a file.
  const withAdminSession = useCallback(async () => {
    if (typeof ensureAdminPortalSession !== 'function') {
      setProjectImageError('Admin authentication is unavailable.');
      return false;
    }
    const authenticated = await ensureAdminPortalSession();
    if (!authenticated) {
      setProjectImageError('Admin authentication is required.');
    }
    return authenticated;
  }, [ensureAdminPortalSession]);

  const handleProjectImageUpload = useCallback(async (projectId, file) => {
    if (!file) return;
    setProjectImageError('');
    if (!projectId) {
      setProjectImageError('이 프로젝트에 식별자가 없어 이미지를 붙일 수 없습니다. Save 로 한 번 저장한 뒤 다시 시도하세요.');
      return;
    }

    if (getProjectImages(projectId).length >= projectImageLimit) {
      setProjectImageError(`이미지는 프로젝트당 최대 ${projectImageLimit}장까지 추가할 수 있습니다.`);
      return;
    }
    if (!await withAdminSession()) return;

    setProjectImageBusy(true);
    try {
      const dataUrl = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result || ''));
        reader.onerror = () => reject(new Error('read failed'));
        reader.readAsDataURL(file);
      });

      const response = await fetch(`/api/projects/${encodeURIComponent(projectId)}/images`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ name: file.name, image: dataUrl })
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || '이미지를 추가하지 못했습니다.');

      setProjectImages((prev) => ({ ...prev, [projectId]: data.images || [] }));
      announceProjectImagesChanged();
      onShowToast?.('이미지를 추가했습니다.', 'success');
    } catch (error) {
      setProjectImageError(error.message || '이미지를 추가하지 못했습니다.');
    } finally {
      setProjectImageBusy(false);
    }
  }, [getProjectImages, onShowToast, projectImageLimit, setProjectImages, withAdminSession]);

  const handleProjectImageDelete = useCallback(async (projectId, imageId) => {
    if (!projectId || !imageId) return;
    setProjectImageError('');
    if (!await withAdminSession()) return;

    setProjectImageBusy(true);
    try {
      const response = await fetch(
        `/api/projects/${encodeURIComponent(projectId)}/images/${encodeURIComponent(imageId)}`,
        { method: 'DELETE', credentials: 'include' }
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || '이미지를 삭제하지 못했습니다.');

      setProjectImages((prev) => ({ ...prev, [projectId]: data.images || [] }));
      announceProjectImagesChanged();
    } catch (error) {
      setProjectImageError(error.message || '이미지를 삭제하지 못했습니다.');
    } finally {
      setProjectImageBusy(false);
    }
  }, [setProjectImages, withAdminSession]);

  useEffect(() => {
    const projectItems = Array.isArray(config.projectsData?.projects) ? config.projectsData.projects : [];

    if (projectItems.length === 0) {
      setCurrentProjectIndex(0);
      return;
    }

    setCurrentProjectIndex((prevIndex) => Math.min(prevIndex, projectItems.length - 1));
  }, [config.projectsData, setCurrentProjectIndex]);

  // Moving to another project restarts its slider at the first image.
  useEffect(() => {
    setProjectImageIndex(0);
    setProjectImageError('');
    setProjectImageStatus('');
    setProjectImageProgress(0);
  }, [currentProjectIndex]);

  /**
   * One button, one image.
   *
   * The prompt is the project itself — the server composes it from the entry's own fields, so
   * there is nothing to type here. Generating and keeping stay two calls on the wire (broker
   * results are transient files), but the operator sees a single action: the render lands in
   * the slider or it reports why it did not.
   */
  const generateProjectImage = useCallback(async (project) => {
    const projectId = project?.id || '';
    setProjectImageError('');
    if (!projectId) {
      setProjectImageError('이 프로젝트에 식별자가 없어 이미지를 생성할 수 없습니다. Save 로 한 번 저장한 뒤 다시 시도하세요.');
      return;
    }
    if (getProjectImages(projectId).length >= projectImageLimit) {
      setProjectImageError(`이미지는 프로젝트당 최대 ${projectImageLimit}장까지 추가할 수 있습니다.`);
      return;
    }
    if (!await withAdminSession()) return;

    projectComfyRunRef.current += 1;
    const runId = projectComfyRunRef.current;
    const isCurrent = () => projectComfyRunRef.current === runId;

    setProjectImageBusy(true);
    setProjectImageStatus('이미지 생성 요청 중…');
    try {
      const response = await fetch(`/api/projects/${encodeURIComponent(projectId)}/images/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ width: 1920, height: 1080 })
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || '이미지 생성 요청에 실패했습니다.');

      const jobId = data.job?.id || '';
      const prompt = data.prompt || '';
      let state = data.job?.state || 'queued';
      let position = data.position;
      let step = data.job?.step || 0;
      let stepsTotal = data.job?.stepsTotal || 0;
      let images = data.images || [];

      // The GPU is shared with two other apps, so a queue position is the normal answer, and
      // the broker publishes the sampler's step count once the render actually starts. Both
      // are reported: silence for a minute is indistinguishable from a dead button.
      while (isCurrent() && jobId && !['done', 'failed', 'cancelled'].includes(state)) {
        const queuePosition = position;
        const currentStep = step;
        const totalSteps = stepsTotal;
        setProjectImageStatus(
          state === 'queued' && Number.isFinite(queuePosition) && queuePosition > 0
            ? `GPU 대기 중… 큐 ${queuePosition}번째`
            : (totalSteps ? `이미지 생성 중… ${currentStep}/${totalSteps}` : '이미지 생성 중…')
        );
        setProjectImageProgress(totalSteps ? currentStep / totalSteps : 0);

        await new Promise((resolve) => setTimeout(resolve, 1500));
        if (!isCurrent()) return;

        const pollResponse = await fetch(`/api/comfy/jobs/${encodeURIComponent(jobId)}`, { credentials: 'include' });
        const pollData = await pollResponse.json().catch(() => ({}));
        if (!pollResponse.ok) throw new Error(pollData.error || '작업 상태를 확인하지 못했습니다.');

        state = pollData.job?.state || state;
        position = pollData.position;
        step = pollData.job?.step || step;
        stepsTotal = pollData.job?.stepsTotal || stepsTotal;
        images = pollData.images || images;
      }

      if (!isCurrent()) return;
      if (state === 'failed') throw new Error('이미지 생성에 실패했습니다.');
      if (state === 'cancelled') throw new Error('요청이 취소되었습니다.');
      if (!images.length) throw new Error('결과 이미지가 없습니다.');

      setProjectImageStatus('생성한 이미지를 저장하는 중…');
      const attachResponse = await fetch(`/api/projects/${encodeURIComponent(projectId)}/images/attach`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ jobId, name: images[0].name, prompt })
      });
      const attachData = await attachResponse.json().catch(() => ({}));
      if (!attachResponse.ok) throw new Error(attachData.error || '이미지를 저장하지 못했습니다.');

      const nextImages = attachData.images || [];
      setProjectImages((prev) => ({ ...prev, [projectId]: nextImages }));
      announceProjectImagesChanged();
      setProjectImageIndex(Math.max(0, nextImages.length - 1));
      setProjectImageStatus('');
      setProjectImageProgress(0);
      onShowToast?.('이미지를 생성했습니다.', 'success');
    } catch (error) {
      if (!isCurrent()) return;
      setProjectImageStatus('');
      setProjectImageProgress(0);
      setProjectImageError(error.message || '이미지 생성에 실패했습니다.');
    } finally {
      if (isCurrent()) setProjectImageBusy(false);
    }
  }, [getProjectImages, onShowToast, projectImageLimit, setProjectImages, withAdminSession]);


  useEffect(() => {
    setShortcutLabelDrafts(buildShortcutLabelDrafts(config.desktopIcons, activeLanguage));
  }, [activeLanguage, config.desktopIcons]);

  useEffect(() => {
    return () => {
      if (resizeStateRef.current) {
        resizeStateRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    const desktopElement = document.querySelector('.desktop');
    if (!desktopElement) {
      return undefined;
    }

    const updateDesktopMetrics = () => {
      const desktopRect = desktopElement.getBoundingClientRect();
      const dockElement = document.querySelector('.dock-shell');
      let bottomBoundaryY = desktopElement.clientHeight - safeArea.bottom;

      if (dockElement && resolvedDesktopLayout.shortcutsPlacement === 'dock') {
        const dockRect = dockElement.getBoundingClientRect();
        bottomBoundaryY = Math.max(
          safeArea.top + 120,
          (dockRect.top - desktopRect.top) - 12
        );
      }

      setDesktopMetrics({
        width: desktopElement.clientWidth,
        height: desktopElement.clientHeight,
        bottomBoundaryY
      });
    };

    updateDesktopMetrics();
    window.addEventListener('resize', updateDesktopMetrics);

    return () => {
      window.removeEventListener('resize', updateDesktopMetrics);
    };
  }, [resolvedDesktopLayout.shortcutsPlacement, safeArea.bottom, safeArea.top]);

  useEffect(() => {
    if (isMaximized) {
      previousFrameRef.current = {
        size: windowSizeRef.current,
        position: clampWindowPosition(windowPositionRef.current, windowSizeRef.current, desktopMetrics, resolvedDesktopLayout)
      };
      setWindowPosition({ x: maximizedFrame.x, y: maximizedFrame.y });
    } else if (previousFrameRef.current) {
      const restoredSize = previousFrameRef.current.size;
      setWindowSize(restoredSize);
      setWindowPosition(
        clampWindowPosition(previousFrameRef.current.position, restoredSize, desktopMetrics, resolvedDesktopLayout)
      );
      previousFrameRef.current = null;
    }
  }, [desktopMetrics, isMaximized, maximizedFrame.x, maximizedFrame.y, resolvedDesktopLayout]);

  useEffect(() => {
    if (isMaximized) {
      return;
    }

    const nextPosition = clampWindowPosition(windowPosition, windowSize, desktopMetrics, resolvedDesktopLayout);
    if (nextPosition.x !== windowPosition.x || nextPosition.y !== windowPosition.y) {
      setWindowPosition(nextPosition);
    }
  }, [desktopMetrics, isMaximized, resolvedDesktopLayout, windowPosition, windowSize]);

  useEffect(() => {
    if (isMaximized) {
      return;
    }

    const nextSize = clampWindowSize(windowSize, windowPosition, desktopMetrics, minWindowSize, resolvedDesktopLayout);
    if (nextSize.width !== windowSize.width || nextSize.height !== windowSize.height) {
      setWindowSize(nextSize);
    }
  }, [desktopMetrics, isMaximized, minWindowSize, resolvedDesktopLayout, windowPosition, windowSize]);

  const syncAiAuthWindowHeight = useCallback(() => {
    if (app.id !== 'ai-auth' || isMaximized) {
      return;
    }

    const frameNode = nodeRef.current;
    const aiAuthContentNode = aiAuthContentRef.current;
    if (!frameNode || !aiAuthContentNode) {
      return;
    }

    const headerHeight = frameNode.querySelector('.window-header')?.offsetHeight || 0;
    const menuHeight = frameNode.querySelector('.window-management-bar')?.offsetHeight || 0;
    const nextSize = clampWindowSize(
      {
        width: windowSizeRef.current.width,
        height: Math.ceil(headerHeight + menuHeight + aiAuthContentNode.offsetHeight)
      },
      windowPositionRef.current,
      desktopMetrics,
      minWindowSize,
      resolvedDesktopLayout
    );
    const nextPosition = clampWindowPosition(
      windowPositionRef.current,
      nextSize,
      desktopMetrics,
      resolvedDesktopLayout
    );
    const sizeChanged = nextSize.width !== windowSizeRef.current.width
      || nextSize.height !== windowSizeRef.current.height;
    const positionChanged = nextPosition.x !== windowPositionRef.current.x
      || nextPosition.y !== windowPositionRef.current.y;

    if (!sizeChanged && !positionChanged) {
      return;
    }

    if (sizeChanged) {
      windowSizeRef.current = nextSize;
      setWindowSize(nextSize);
    }

    if (positionChanged) {
      windowPositionRef.current = nextPosition;
      setWindowPosition(nextPosition);
    }

    commitWindowLayout(nextSize, nextPosition);
  }, [app.id, commitWindowLayout, desktopMetrics, isMaximized, minWindowSize, resolvedDesktopLayout]);

  useEffect(() => {
    if (app.id !== 'ai-auth') {
      return undefined;
    }

    let animationFrameId = null;
    const scheduleSync = () => {
      if (animationFrameId != null) {
        window.cancelAnimationFrame(animationFrameId);
      }

      animationFrameId = window.requestAnimationFrame(() => {
        animationFrameId = null;
        syncAiAuthWindowHeight();
      });
    };

    scheduleSync();

    const aiAuthContentNode = aiAuthContentRef.current;
    const resizeObserver = (typeof ResizeObserver !== 'undefined' && aiAuthContentNode)
      ? new ResizeObserver(() => scheduleSync())
      : null;

    resizeObserver?.observe(aiAuthContentNode);
    window.addEventListener('resize', scheduleSync);

    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener('resize', scheduleSync);
      if (animationFrameId != null) {
        window.cancelAnimationFrame(animationFrameId);
      }
    };
  }, [app.id, syncAiAuthWindowHeight]);

  useEffect(() => {
    if (!profileSaveMessage) {
      return undefined;
    }

    const timer = window.setTimeout(() => setProfileSaveMessage(''), 2200);
    return () => window.clearTimeout(timer);
  }, [profileSaveMessage]);

  useEffect(() => {
    if (!resumeSaveMessage) {
      return undefined;
    }

    const timer = window.setTimeout(() => setResumeSaveMessage(''), 2200);
    return () => window.clearTimeout(timer);
  }, [resumeSaveMessage]);

  useEffect(() => {
    if (!projectsSaveMessage) {
      return undefined;
    }

    const timer = window.setTimeout(() => setProjectsSaveMessage(''), 2200);
    return () => window.clearTimeout(timer);
  }, [projectsSaveMessage]);

  useEffect(() => {
    if (!iconMessage && !iconError) {
      return undefined;
    }

    const timer = window.setTimeout(() => {
      setIconMessage('');
      setIconError('');
    }, 2400);

    return () => window.clearTimeout(timer);
  }, [iconMessage, iconError]);

  useEffect(() => {
    if (!aiToolsError) {
      return undefined;
    }

    const timer = window.setTimeout(() => {
      setAiToolsError('');
    }, 2600);

    return () => window.clearTimeout(timer);
  }, [aiToolsError]);

  useEffect(() => {
    if (!['profile', 'resume', 'projects'].includes(app.id)) {
      return;
    }

    if (authenticatedImportTargets.length === 1) {
      setSelectedImportTarget(authenticatedImportTargets[0]);
      return;
    }

    if (!CHAT_TARGET_OPTIONS.some((target) => target.id === selectedImportTarget)) {
      setSelectedImportTarget(CHAT_TARGET_OPTIONS[0].id);
      return;
    }

    if (!authenticatedImportTargets.includes(selectedImportTarget) && authenticatedImportTargets.length > 0) {
      setSelectedImportTarget(authenticatedImportTargets[0]);
    }
  }, [app.id, authenticatedImportTargets, selectedImportTarget]);

  useEffect(() => {
    if (app.id !== 'ai-chat') {
      return;
    }

    if (!CHAT_TARGET_OPTIONS.some((target) => target.id === aiChatTarget)) {
      setAiChatTarget(CHAT_TARGET_OPTIONS[0].id);
      return;
    }

    // Keep the user's choice while it's selectable; only fall back when it isn't at all.
    if (!selectableChatTargets.includes(aiChatTarget) && selectableChatTargets.length > 0) {
      setAiChatTarget(selectableChatTargets[0]);
    }
  }, [aiChatTarget, app.id, selectableChatTargets]);

  // Models the hub allows for the selected target (edited in the aihub admin screen).
  useEffect(() => {
    if (app.id !== 'ai-chat') {
      return;
    }
    const target = getChatTargetDefinition(aiChatTarget);
    let cancelled = false;
    setAiChatModelsLoading(true);
    (async () => {
      try {
        const response = await fetch(`/api/ai-chat/models?tool=${encodeURIComponent(target.tool)}`, { credentials: 'include' });
        const data = await response.json().catch(() => ({}));
        if (cancelled) {
          return;
        }
        const models = Array.isArray(data.models) ? data.models : [];
        setAiChatAvailableModels(models);
        // Keep the current selection if still valid; otherwise fall back to the first model.
        setAiChatModel((prev) => (models.some((m) => m.id === prev) ? prev : (models[0]?.id || 'auto')));
      } catch (_) {
        if (!cancelled) {
          setAiChatAvailableModels([]);
        }
      } finally {
        if (!cancelled) {
          setAiChatModelsLoading(false);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [aiChatTarget, app.id]);

  useEffect(() => {
    if (app.id !== 'ai-chat' || !aiChatSessionId || !isAiChatPollingActive) {
      return undefined;
    }

    let isCancelled = false;
    let timer = null;

    const loadSession = async () => {
      try {
        const response = await fetch(`/api/ai-chat/terminal/sessions/${encodeURIComponent(aiChatSessionId)}`, {
          credentials: 'include',
          cache: 'no-store'
        });
        const data = await response.json().catch(() => ({}));

        if (!response.ok) {
          throw new Error(getApiErrorMessage(data, 'Failed to load AI chat session.'));
        }

        if (isCancelled) {
          return;
        }

        const session = data.session || {};
        setAiChatMessages((prevMessages) => mergeChatMessages(prevMessages, session.messages || []));
        updateAiChatStatusText(session.statusText);
        if (session.finished) {
          setIsAiChatPollingActive(false);
          setAiChatSending(false);
          setAiChatSessionId('');
          return;
        }

        const nextDelay = document.visibilityState === 'visible' && isFocused ? 500 : 1400;
        timer = window.setTimeout(loadSession, nextDelay);
      } catch (error) {
        if (isCancelled) {
          return;
        }

        showAiChatErrorToast(error.message || 'Failed to load AI chat session.');
        updateAiChatStatusText('Failed to load AI chat session.');
        setIsAiChatPollingActive(false);
        setAiChatSessionId('');
        setAiChatSending(false);
      }
    };

    timer = window.setTimeout(loadSession, 0);

    return () => {
      isCancelled = true;
      if (timer) {
        window.clearTimeout(timer);
      }
    };
  }, [aiChatSessionId, app.id, isAiChatPollingActive, isFocused, showAiChatErrorToast, updateAiChatStatusText]);

  useEffect(() => {
    if (app.id !== 'ai-chat') {
      return;
    }

    if (!aiChatShouldAutoScrollRef.current) {
      return;
    }

    scrollAiChatFeedToBottom();
  }, [aiChatMessages, app.id, scrollAiChatFeedToBottom]);

  useEffect(() => {
    if (app.id !== 'ai-chat') {
      return;
    }

    updateAiChatAutoScrollState();
  }, [aiChatMessages.length, app.id, updateAiChatAutoScrollState]);

  useEffect(() => {
    if (app.id !== 'resume' && app.contentSource && app.contentSource.endsWith('.md')) {
      setLoading(true);
      fetch(app.contentSource)
        .then((res) => res.text())
        .then((text) => {
          setContent(text);
          setLoading(false);
        })
        .catch(() => {
          setContent('# Error\nFailed to load content.');
          setLoading(false);
        });
    }
  }, [app.id, app.contentSource]);

  const loadWallpapers = useCallback(async (signal) => {
    const retryDelays = [0, 300, 900];
    let lastError = null;

    setWallpapersLoading(true);
    setUploadError('');

    for (let attempt = 0; attempt < retryDelays.length; attempt += 1) {
      if (signal?.aborted) {
        return;
      }

      if (retryDelays[attempt] > 0) {
        await new Promise((resolve) => {
          window.setTimeout(resolve, retryDelays[attempt]);
        });
      }

      try {
        const response = await fetch('/api/wallpapers', {
          signal,
          cache: 'no-store'
        });
        const data = await response.json().catch(() => ({}));

        if (!response.ok) {
          throw new Error(data.error || 'Failed to load uploaded wallpapers.');
        }

        if (signal?.aborted) {
          return;
        }

        setWallpapers(Array.isArray(data.wallpapers) ? data.wallpapers : []);
        setUploadError('');
        setWallpapersLoading(false);
        return;
      } catch (error) {
        if (signal?.aborted) {
          return;
        }

        lastError = error;
      }
    }

    console.warn('Failed to load uploaded wallpapers.', lastError);
    setWallpapers([]);
    setUploadError('Failed to load uploaded wallpapers.');
    setWallpapersLoading(false);
  }, []);

  useEffect(() => {
    if (app.id !== 'settings') {
      return;
    }

    const controller = new AbortController();
    loadWallpapers(controller.signal);

    return () => {
      controller.abort();
    };
  }, [app.id, loadWallpapers]);

  const isSupportedImageFile = (file) => {
    if (!file) {
      return false;
    }

    const lowerName = file.name.toLowerCase();
    const validExtension = ['.png', '.jpg', '.jpeg'].some((extension) => lowerName.endsWith(extension));
    const validMimeType = ['image/png', 'image/x-png', 'image/jpeg', 'image/jpg', 'image/pjpeg', 'application/octet-stream', ''].includes(file.type);

    return validExtension && validMimeType;
  };

  const isSupportedIconFile = (file) => {
    if (!file) {
      return false;
    }

    const lowerName = file.name.toLowerCase();
    const validExtension = ['.svg', '.png', '.jpg', '.jpeg', '.icns'].some((extension) => lowerName.endsWith(extension));
    const validMimeType = [
      'image/svg+xml',
      'image/png',
      'image/x-png',
      'image/jpeg',
      'image/jpg',
      'image/pjpeg',
      'image/x-icns',
      'application/icns',
      'application/octet-stream',
      ''
    ].includes(file.type);

    return validExtension && validMimeType;
  };

  const isTextLikePortfolioSourceFile = (file) => {
    if (!file) {
      return false;
    }

    const lowerName = file.name.toLowerCase();
    const validExtension = ['.txt', '.md', '.markdown', '.json', '.csv', '.yaml', '.yml', '.html', '.htm'].some((extension) => lowerName.endsWith(extension));
    const validMimeType = [
      'text/plain',
      'text/markdown',
      'application/json',
      'text/csv',
      'application/x-yaml',
      'text/yaml',
      'text/html',
      'application/octet-stream',
      ''
    ].includes(file.type);

    return validExtension && validMimeType;
  };

  const isSupportedPortfolioImportFile = (file) => {
    if (!file) {
      return false;
    }

    const lowerName = String(file.name || '').toLowerCase();
    const mimeType = String(file.type || '').toLowerCase();
    const matchedDocumentExtension = PORTFOLIO_IMPORT_DOCUMENT_EXTENSIONS.find((extension) => lowerName.endsWith(extension));

    if (lowerName.endsWith('.csv')) {
      return PORTFOLIO_IMPORT_TEXT_MIME_TYPES.includes(mimeType);
    }

    if (matchedDocumentExtension) {
      return PORTFOLIO_IMPORT_DOCUMENT_MIME_TYPES_BY_EXTENSION[matchedDocumentExtension].includes(mimeType);
    }

    return false;
  };

  const isSupportedAiChatAttachmentFile = (file) => {
    if (!file) {
      return false;
    }

    const lowerName = file.name.toLowerCase();
    const validExtension = [
      '.txt', '.md', '.markdown', '.json', '.csv', '.yaml', '.yml', '.html', '.htm',
      '.png', '.jpg', '.jpeg', '.webp', '.gif', '.bmp', '.svg',
      '.pdf',
      '.ppt', '.pptx',
      '.xls', '.xlsx',
      '.doc', '.docx',
      '.hwp', '.hwpx'
    ].some((extension) => lowerName.endsWith(extension));
    const validMimeTypePrefixes = [
      'text/',
      'image/'
    ];
    const validMimeTypes = [
      'application/json',
      'application/pdf',
      'application/vnd.ms-powerpoint',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/x-hwp',
      'application/haansofthwp',
      'application/vnd.hancom.hwp',
      'application/haansofthwpx',
      'application/vnd.hancom.hwpx',
      'application/x-hwp+zip',
      'application/zip',
      'application/octet-stream',
      ''
    ];

    return validExtension || validMimeTypePrefixes.some((prefix) => String(file.type || '').startsWith(prefix)) || validMimeTypes.includes(file.type);
  };

  const applyImportedPortfolioTarget = useCallback((target, payload) => {
    if (!target || !payload) {
      return;
    }

    if (target === 'profile') {
      onProfileDataChange(payload);
      setProfileDraft(cloneDraftValue(payload));
      return;
    }

    if (target === 'resume') {
      onResumeDataChange(payload);
      setResumeDraft(cloneDraftValue(payload));
      return;
    }

    if (target === 'projects') {
      onProjectsDataChange(payload);
      setProjectsDraft(cloneDraftValue(payload));
    }
  }, [onProfileDataChange, onProjectsDataChange, onResumeDataChange]);

  const releasePortfolioImportContinuationSource = useCallback(async (sourceId) => {
    const normalizedSourceId = String(sourceId || '').trim();
    if (!normalizedSourceId) {
      return;
    }

    try {
      await fetch(`/api/ai-import/sources/${encodeURIComponent(normalizedSourceId)}`, {
        method: 'DELETE',
        credentials: 'include'
      });
    } catch (error) {
      // Ignore cleanup failures and let TTL cleanup handle them later.
    }
  }, []);

  const dismissPortfolioImportSuggestions = useCallback(async () => {
    const sourceId = portfolioImportContinuationSourceId;
    setPortfolioImportSuggestions([]);
    setPortfolioImportContinuationSourceId('');
    await releasePortfolioImportContinuationSource(sourceId);
  }, [portfolioImportContinuationSourceId, releasePortfolioImportContinuationSource]);

  const applyPortfolioImportSuggestion = useCallback((suggestion) => {
    if (!suggestion?.target) {
      return;
    }

    const sourceId = portfolioImportContinuationSourceId;
    setPortfolioImportSuggestions([]);
    setPortfolioImportContinuationSourceId('');
    onRequestProtectedFileImport?.({
      targetWindowId: suggestion.target,
      continuationSourceId: sourceId
    });
  }, [onRequestProtectedFileImport, portfolioImportContinuationSourceId]);

  const startPortfolioImportJob = useCallback(async ({ createJob, sourceLabel = 'source file' }) => {
    setImportingPortfolioFile(true);
    setPortfolioImportError('');
    setPortfolioImportMessage('');
    setPortfolioImportSuggestions([]);
    setPortfolioImportContinuationSourceId('');
    onPortfolioImportProcessingChange?.({
      targetWindowId: app.id,
      isProcessing: true
    });

    try {
      const createJobData = await createJob();
      const jobId = getAiImportJobId(createJobData);
      if (!jobId) {
        throw new Error('Portfolio import job id was not returned.');
      }

      let pollCount = 0;
      while (pollCount < AI_IMPORT_JOB_MAX_POLLS) {
        pollCount += 1;
        const pollDelay = document.visibilityState === 'visible' && isFocused
          ? AI_IMPORT_JOB_POLL_VISIBLE_DELAY_MS
          : AI_IMPORT_JOB_POLL_HIDDEN_DELAY_MS;
        await waitForDelay(pollDelay);

        const statusResponse = await fetch(`/api/ai-import/jobs/${encodeURIComponent(jobId)}`, {
          credentials: 'include',
          cache: 'no-store'
        });
        const statusData = await statusResponse.json().catch(() => ({}));

        if (!statusResponse.ok) {
          throw new Error(getApiErrorMessage(statusData, 'Failed to load portfolio import status.'));
        }

        const status = String(statusData.status || '').trim().toLowerCase();
        if (['queued', 'pending', 'running', 'processing'].includes(status)) {
          continue;
        }

        if (['failed', 'error', 'cancelled', 'canceled'].includes(status)) {
          throw new Error(getApiErrorMessage(statusData, 'Failed to import portfolio source file.'));
        }

        if (['completed', 'success', 'succeeded', 'done'].includes(status)) {
          const resultPayload = getAiImportResultPayload(statusData);
          const target = String(resultPayload.target || app.id).trim() || app.id;
          const continuationSourceId = String(resultPayload.continuation_source_id || '').trim();
          applyImportedPortfolioTarget(target, resultPayload.payload || null);
          setPortfolioImportMessage(resultPayload.message || `${PORTFOLIO_IMPORT_TARGET_LABELS[target] || localizedWindowTitle} updated from ${sourceLabel}.`);
          setPortfolioImportSuggestions(Array.isArray(resultPayload.suggestions) ? resultPayload.suggestions : []);
          setPortfolioImportContinuationSourceId(continuationSourceId);
          setIsImportModalOpen(false);
          return;
        }
      }

      throw new Error('Portfolio import timed out while waiting for completion.');
    } catch (error) {
      setPortfolioImportError(error.message || 'Failed to import portfolio source file.');
    } finally {
      setImportingPortfolioFile(false);
      onPortfolioImportProcessingChange?.({
        targetWindowId: app.id,
        isProcessing: false
      });
    }
  }, [app.id, applyImportedPortfolioTarget, isFocused, localizedWindowTitle, onPortfolioImportProcessingChange]);

  useEffect(() => {
    if (fileMenuRequest?.targetWindowId !== app.id) {
      return;
    }

    if (!['profile', 'resume', 'projects'].includes(app.id)) {
      return;
    }

    setPortfolioImportError('');
    setPortfolioImportMessage('');
    const continuationSourceId = String(fileMenuRequest?.continuationSourceId || '').trim();
    onConsumeFileMenuRequest?.();

    if (!continuationSourceId) {
      setIsImportModalOpen(true);
      return;
    }

    void startPortfolioImportJob({
      createJob: async () => {
        const response = await fetch(`/api/ai-import/sources/${encodeURIComponent(continuationSourceId)}/continue`, {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            target: app.id
          })
        });
        const data = await response.json().catch(() => ({}));

        if (!response.ok) {
          throw new Error(getApiErrorMessage(data, 'Failed to continue portfolio import.'));
        }

        return data;
      }
    });
  }, [app.id, fileMenuRequest, onConsumeFileMenuRequest, startPortfolioImportJob]);

  const handlePortfolioImportFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';

    if (!file) {
      return;
    }

    const importTargetId = authenticatedImportTargets.length === 1 ? authenticatedImportTargets[0] : selectedImportTarget;
    const selectedImportTargetDefinition = getChatTargetDefinition(importTargetId);
    const toolKey = selectedImportTargetDefinition.tool;

    if (!importTargetId || !isChatTargetAuthenticated(aiToolsStatus, importTargetId)) {
      setPortfolioImportError('Choose an authenticated import target before uploading a source file.');
      return;
    }

    if (!isSupportedPortfolioImportFile(file)) {
      setPortfolioImportError(`Portfolio import supports ${PORTFOLIO_IMPORT_SUPPORTED_FILE_LABEL} files only.`);
      return;
    }

    if (file.size > MAX_UPLOAD_BYTES) {
      setPortfolioImportError(`Source file must be ${Math.round(MAX_UPLOAD_BYTES / (1024 * 1024))}MB or smaller.`);
      return;
    }

    await releasePortfolioImportContinuationSource(portfolioImportContinuationSourceId);

    await startPortfolioImportJob({
      sourceLabel: file.name,
      createJob: async () => {
        const formData = new FormData();
        formData.append('file', file, file.name);
        formData.append('mode', 'portfolio_import');
        formData.append('source', 'window');
        formData.append('client_request_id', `imp-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`);
        formData.append('target', app.id);
        formData.append('tool', toolKey);
        formData.append('authStrategy', selectedImportTargetDefinition.strategy);

        const createJobResponse = await fetch('/api/ai-import/jobs', {
          method: 'POST',
          credentials: 'include',
          body: formData
        });
        const createJobData = await createJobResponse.json().catch(() => ({}));

        if (!createJobResponse.ok) {
          throw new Error(getApiErrorMessage(createJobData, 'Failed to start portfolio import.'));
        }

        return createJobData;
      }
    });
  };

  const openPortfolioImportPicker = () => {
    if (importingPortfolioFile) {
      return;
    }

    if (authenticatedImportTargets.length > 0 && !isChatTargetAuthenticated(aiToolsStatus, selectedImportTarget)) {
      setPortfolioImportError('Choose an authenticated import target before uploading a source file.');
      return;
    }

    setPortfolioImportSuggestions([]);
    importFileInputRef.current?.click();
  };

  const resetPortfolioDocument = useCallback(async (target) => {
    if (!['profile', 'resume', 'projects'].includes(target) || resettingPortfolioTarget) {
      return;
    }

    try {
      setResettingPortfolioTarget(target);
      const response = await fetch(`/api/${encodeURIComponent(target)}/reset`, {
        method: 'POST',
        credentials: 'include'
      });
      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(getApiErrorMessage(data, `Failed to reset ${PORTFOLIO_IMPORT_TARGET_LABELS[target] || target}.`));
      }

      if (target === 'profile') {
        onProfileDataChange(data.profile || {});
      } else if (target === 'resume') {
        onResumeDataChange(data.resume || {});
      } else {
        onProjectsDataChange(data.projects || {});
      }

      onShowToast?.(`${PORTFOLIO_IMPORT_TARGET_LABELS[target] || target} initialized from the default template.`);
    } catch (error) {
      onShowToast?.(error.message || `Failed to reset ${PORTFOLIO_IMPORT_TARGET_LABELS[target] || target}.`, 'error');
    } finally {
      setResettingPortfolioTarget('');
    }
  }, [onProfileDataChange, onProjectsDataChange, onResumeDataChange, onShowToast, resettingPortfolioTarget]);

  const requestPortfolioInitialize = (target) => {
    if (!['profile', 'resume', 'projects'].includes(target) || resettingPortfolioTarget) {
      return;
    }

    setInitializePrompt({
      target,
      label: PORTFOLIO_IMPORT_TARGET_LABELS[target] || target
    });
  };

  const openAiChatAttachmentPicker = () => {
    if (aiChatSending || aiChatSessionId || !isChatTargetAuthenticated(aiToolsStatus, aiChatTarget)) {
      return;
    }

    aiChatFileInputRef.current?.click();
  };

  const handleAiChatAttachmentFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';

    if (!file) {
      return;
    }

    if (!isSupportedAiChatAttachmentFile(file)) {
      showAiChatErrorToast('This file type is not supported for AI chat attachment.');
      return;
    }

    if (file.size > MAX_UPLOAD_BYTES) {
      showAiChatErrorToast(`Attachment size must be ${Math.round(MAX_UPLOAD_BYTES / (1024 * 1024))}MB or smaller.`);
      return;
    }

    try {
      const buffer = await file.arrayBuffer();
      const textLikeAttachment = isTextLikePortfolioSourceFile(file);
      const fileText = textLikeAttachment ? await file.text() : '';
      const truncated = textLikeAttachment && fileText.length > MAX_AI_CHAT_ATTACHMENT_CHARS;
      setAiChatAttachment({
        fileName: file.name,
        mimeType: file.type || 'application/octet-stream',
        contentBase64: arrayBufferToBase64(buffer),
        previewText: textLikeAttachment ? fileText.slice(0, MAX_AI_CHAT_ATTACHMENT_CHARS) : '',
        truncated
      });
      updateAiChatStatusText(truncated ? `${file.name} attached. Preview text was truncated.` : `${file.name} attached.`);
    } catch (error) {
      showAiChatErrorToast(error.message || 'Failed to read the selected file.');
    }
  };

  const updateDesktopIcon = (iconId, patch, toastMessage = 'Shortcuts saved successfully.') => {
    if (toastMessage) {
      queueUserIconsPersistToast(toastMessage);
    }

    onDesktopIconsChange((prevIcons) => prevIcons.map((iconItem) => (
      iconItem.id === iconId
        ? { ...iconItem, ...patch }
        : iconItem
    )));
  };

  const handleShortcutLabelDraftChange = (iconId, nextValue) => {
    setShortcutLabelDrafts((prevDrafts) => ({
      ...prevDrafts,
      [iconId]: nextValue
    }));
  };

  const handleShortcutLabelCommit = (iconId) => {
    const nextLabel = sanitizeEditableShortcutLabel(shortcutLabelDrafts[iconId] || '');
    const alternateLanguage = activeLanguage === 'english' ? 'korean' : 'english';

    setShortcutLabelDrafts((prevDrafts) => ({
      ...prevDrafts,
      [iconId]: nextLabel
    }));

    const currentIcon = (config.desktopIcons || []).find((iconItem) => iconItem.id === iconId);
    if (!currentIcon) {
      return;
    }

    const previousLocalizedLabels = currentIcon.localizedLabels || {};
    const nextLocalizedLabels = {
      ...previousLocalizedLabels,
      [activeLanguage]: nextLabel
    };

    if (!(previousLocalizedLabels[alternateLanguage] || '').trim()) {
      nextLocalizedLabels[alternateLanguage] = nextLabel;
    }

    const fallbackLabel = nextLocalizedLabels.english || nextLocalizedLabels.korean || nextLabel;

    if (
      currentIcon.label === fallbackLabel
      && (previousLocalizedLabels.english || '') === (nextLocalizedLabels.english || '')
      && (previousLocalizedLabels.korean || '') === (nextLocalizedLabels.korean || '')
    ) {
      return;
    }

    updateDesktopIcon(iconId, {
      label: fallbackLabel,
      localizedLabels: nextLocalizedLabels
    });
  };

  const reorderDesktopIcons = (sourceIconId, targetIconId) => {
    queueUserIconsPersistToast('Shortcut order saved successfully.');
    onDesktopIconsChange((prevIcons) => {
      const protectedIcons = prevIcons.filter((iconItem) => PROTECTED_ICON_IDS.includes(iconItem.id));
      const customIcons = prevIcons.filter((iconItem) => !PROTECTED_ICON_IDS.includes(iconItem.id));
      const sourceIndex = customIcons.findIndex((iconItem) => iconItem.id === sourceIconId);
      const targetIndex = customIcons.findIndex((iconItem) => iconItem.id === targetIconId);

      if (sourceIndex === -1 || targetIndex === -1 || sourceIndex === targetIndex) {
        return prevIcons;
      }

      const nextCustomIcons = [...customIcons];
      const [movedIcon] = nextCustomIcons.splice(sourceIndex, 1);
      nextCustomIcons.splice(targetIndex, 0, movedIcon);

      return [...protectedIcons, ...nextCustomIcons];
    });

    setIconError('');
    setIconMessage('Shortcut order updated.');
  };

  const handleIconDragStart = (iconId, event) => {
    setDraggedIconId(iconId);
    setDragOverIconId(iconId);
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', iconId);
  };

  const handleIconDragEnter = (iconId) => {
    if (!draggedIconId || PROTECTED_ICON_IDS.includes(iconId) || draggedIconId === iconId) {
      return;
    }

    setDragOverIconId(iconId);
  };

  const handleIconDrop = (iconId, event) => {
    event.preventDefault();

    if (!draggedIconId || PROTECTED_ICON_IDS.includes(iconId) || draggedIconId === iconId) {
      setDragOverIconId('');
      return;
    }

    reorderDesktopIcons(draggedIconId, iconId);
    setDragOverIconId(iconId);
  };

  const handleIconDragEnd = () => {
    setDraggedIconId('');
    setDragOverIconId('');
  };

  const addDesktopIcon = () => {
    const nextId = `user-icon-${Date.now()}`;
    queueUserIconsPersistToast('Shortcut added successfully.');
    onDesktopIconsChange((prevIcons) => [
      ...prevIcons,
      {
        id: nextId,
        ...DEFAULT_NEW_ICON
      }
    ]);
    setIconMessage('Shortcut added.');
  };

  const deleteUploadedIconFile = async (iconValue) => {
    if (!isUploadedIconPath(iconValue)) {
      return;
    }

    const iconId = iconValue.split('/').pop();
    if (!iconId) {
      return;
    }

    await fetch(`/api/icons/${encodeURIComponent(iconId)}`, {
      method: 'DELETE'
    }).catch(() => null);
  };

  const removeDesktopIcon = async (iconId) => {
    const iconToRemove = (config.desktopIcons || []).find((iconItem) => iconItem.id === iconId);
    if (!iconToRemove || PROTECTED_ICON_IDS.includes(iconId)) {
      return;
    }

    const shouldDelete = window.confirm(`Delete "${iconToRemove.label}" shortcut?`);
    if (!shouldDelete) {
      return;
    }

    try {
      setDeletingIconId(iconId);
      await deleteUploadedIconFile(iconToRemove.icon);
      queueUserIconsPersistToast('Shortcut deleted successfully.');
      onDesktopIconsChange((prevIcons) => prevIcons.filter((iconItem) => iconItem.id !== iconId));
      setIconMessage('Shortcut deleted.');
    } catch (error) {
      setIconError('Failed to delete shortcut.');
    } finally {
      setDeletingIconId('');
    }
  };

  const readFileAsDataUrl = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = () => reject(new Error('Failed to read file.'));
      reader.readAsDataURL(file);
    });
  };

  const loadImageElement = (src) => {
    return new Promise((resolve, reject) => {
      const image = new Image();
      image.onload = () => resolve(image);
      image.onerror = () => reject(new Error('Failed to decode image.'));
      image.src = src;
    });
  };

  const normalizeIconUploadData = async (file) => {
    const lowerName = file.name.toLowerCase();

    if (lowerName.endsWith('.svg') || lowerName.endsWith('.icns')) {
      return {
        fileName: file.name,
        imageData: await readFileAsDataUrl(file)
      };
    }

    const sourceDataUrl = await readFileAsDataUrl(file);
    const image = await loadImageElement(sourceDataUrl);
    const canvas = document.createElement('canvas');

    canvas.width = image.naturalWidth || image.width;
    canvas.height = image.naturalHeight || image.height;

    if (!canvas.width || !canvas.height) {
      throw new Error('Failed to decode image.');
    }

    const context = canvas.getContext('2d');
    context.drawImage(image, 0, 0, canvas.width, canvas.height);

    return {
      fileName: `${file.name.replace(/\.[^.]+$/, '') || 'icon'}.png`,
      imageData: canvas.toDataURL('image/png')
    };
  };

  const handleWallpaperUpload = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    setUploadError('');
    setUploadMessage('');

    if (!isSupportedImageFile(file)) {
      setUploadError('Only PNG, JPG, and JPEG files can be uploaded.');
      return;
    }

    if (file.size > MAX_UPLOAD_BYTES) {
      setUploadError('Image file is too large. Limit is 10MB.');
      return;
    }

    try {
      setUploading(true);
      const imageData = await readFileAsDataUrl(file);
      const response = await fetch('/api/wallpapers', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          fileName: file.name,
          imageData
        })
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(data.error || 'Failed to upload wallpaper image.');
      }

      const nextWallpaper = data.wallpaper;
      setWallpapers((prev) => [...prev, nextWallpaper].sort((a, b) => a.name.localeCompare(b.name)));
      setUploadMessage('Wallpaper uploaded successfully.');
      if (wallpaperSelectionTarget === 'landing') {
        applySettingChange('landingPage', { backgroundImage: nextWallpaper.url }, 'Landing background saved successfully.');
      } else {
        applySettingChange('themeWallpaper', { theme: activeTheme, wallpaper: nextWallpaper.url }, 'Wallpaper saved successfully.');
      }
    } catch (error) {
      setUploadError(error.message);
    } finally {
      setUploading(false);
    }
  };

  const handleWallpaperDelete = async (wallpaper) => {
    if (!wallpaper?.id || deletingWallpaperId) {
      return;
    }

    const shouldDelete = window.confirm(`Delete "${wallpaper.name}" from uploaded wallpapers?`);
    if (!shouldDelete) {
      return;
    }

    try {
      setDeletingWallpaperId(wallpaper.id);
      setUploadError('');
      setUploadMessage('');

      const response = await fetch(`/api/wallpapers/${encodeURIComponent(wallpaper.id)}`, {
        method: 'DELETE'
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(data.error || 'Failed to delete wallpaper image.');
      }

      setWallpapers((prev) => prev.filter((item) => item.id !== wallpaper.id));
      setUploadMessage('Wallpaper deleted successfully.');

      if (activeThemeWallpaper === wallpaper.url) {
        applySettingChange('themeWallpaper', { theme: activeTheme, wallpaper: 'default' }, 'Wallpaper saved successfully.');
      }

      if (landingPageSettings.backgroundImage === wallpaper.url) {
        applySettingChange('landingPage', { backgroundImage: '/assets/grass.jpg' }, 'Landing background saved successfully.');
      }
    } catch (error) {
      setUploadError(error.message);
    } finally {
      setDeletingWallpaperId('');
    }
  };

  const handleIconUpload = async (iconId, event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    setIconError('');
    setIconMessage('');

    if (!isSupportedIconFile(file)) {
      setIconError('Only SVG, PNG, JPG, JPEG, and ICNS icon files can be uploaded.');
      return;
    }

    if (file.size > MAX_UPLOAD_BYTES) {
      setIconError('Icon file is too large. Limit is 10MB.');
      return;
    }

    try {
      setUploadingIconId(iconId);
      const { fileName, imageData } = await normalizeIconUploadData(file);
      const response = await fetch('/api/icons', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          fileName,
          imageData
        })
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(data.error || 'Failed to upload icon file.');
      }

      const currentIcon = (config.desktopIcons || []).find((iconItem) => iconItem.id === iconId);
      if (currentIcon?.icon && isUploadedIconPath(currentIcon.icon)) {
        await deleteUploadedIconFile(currentIcon.icon);
      }

      updateDesktopIcon(iconId, { icon: data.icon.url }, 'Shortcut icon saved successfully.');
      setIconMessage('Icon uploaded.');
    } catch (error) {
      setIconError(error.message || 'Failed to upload icon file.');
    } finally {
      setUploadingIconId('');
    }
  };

  const handleProfilePhotoUpload = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';

    if (!isSupportedImageFile(file)) {
      setProfileSaveMessage('Only PNG, JPG, and JPEG files can be uploaded.');
      return;
    }

    if (file.size > MAX_UPLOAD_BYTES) {
      setProfileSaveMessage('Image file is too large. Limit is 10MB.');
      return;
    }

    try {
      setUploading(true);
      const imageData = await readFileAsDataUrl(file);
      const response = await fetch('/api/profile-photo', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          fileName: file.name,
          imageData
        })
      });
      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(data.error || 'Failed to upload profile image.');
      }

      if (data.photo) {
        updateProfileBasicInfo('photo', data.photo.url);
        setProfileSaveMessage('Photo uploaded');
      }
    } catch (error) {
      setProfileSaveMessage(error.message || 'Failed to upload profile image.');
    } finally {
      setUploading(false);
    }
  };

  const updateDraftState = (setter, updater) => {
    setter((prev) => {
      const next = cloneDraftValue(prev);
      updater(next);
      return next;
    });
  };

  const updateProfileBasicInfo = (field, value) => {
    updateDraftState(setProfileDraft, (next) => {
      next.basic_info[field] = value;
    });
  };

  const updateProfileExperienceSummary = (field, value) => {
    updateDraftState(setProfileDraft, (next) => {
      next.experience_summary[field] = field.includes('_years') || field.includes('_months')
        ? Number(value || 0)
        : value;
    });
  };

  const updateProfileArrayItem = (sectionKey, index, field, value) => {
    updateDraftState(setProfileDraft, (next) => {
      const target = sectionKey === 'software_skills'
        ? next.skills.software_skills
        : sectionKey === 'self_sections'
          ? next.self_introduction.sections
          : next[sectionKey];

      if (!Array.isArray(target) || !target[index]) {
        return;
      }

      target[index][field] = value;
    });
  };

  const addProfileArrayItem = (sectionKey, factory) => {
    updateDraftState(setProfileDraft, (next) => {
      const target = sectionKey === 'software_skills'
        ? next.skills.software_skills
        : sectionKey === 'self_sections'
          ? next.self_introduction.sections
          : next[sectionKey];

      target.push(factory());
    });
  };

  const removeProfileArrayItem = (sectionKey, index) => {
    updateDraftState(setProfileDraft, (next) => {
      const target = sectionKey === 'software_skills'
        ? next.skills.software_skills
        : sectionKey === 'self_sections'
          ? next.self_introduction.sections
          : next[sectionKey];

      if (!Array.isArray(target)) {
        return;
      }

      target.splice(index, 1);
    });
  };

  const updateProfileSelfIntroduction = (field, value) => {
    updateDraftState(setProfileDraft, (next) => {
      next.self_introduction[field] = value;
    });
  };

  const updateResumeArrayItem = (sectionKey, index, field, value) => {
    updateDraftState(setResumeDraft, (next) => {
      const target = next[sectionKey];

      if (!Array.isArray(target) || !target[index]) {
        return;
      }

      target[index][field] = value;
    });
  };

  const updateResumeArrayLines = (sectionKey, index, field, value) => {
    updateDraftState(setResumeDraft, (next) => {
      const target = next[sectionKey];

      if (!Array.isArray(target) || !target[index]) {
        return;
      }

      target[index][field] = fromMultilineText(value);
    });
  };

  const addResumeArrayItem = (sectionKey, factory) => {
    updateDraftState(setResumeDraft, (next) => {
      next[sectionKey].push(factory());
    });
  };

  const removeResumeArrayItem = (sectionKey, index) => {
    updateDraftState(setResumeDraft, (next) => {
      if (!Array.isArray(next[sectionKey])) {
        return;
      }

      next[sectionKey].splice(index, 1);
    });
  };

  const updateResumeMilitary = (field, value) => {
    updateDraftState(setResumeDraft, (next) => {
      next.military_service[field] = field === 'served' ? value : value;
    });
  };

  const updateResumeDeclaration = (field, value) => {
    updateDraftState(setResumeDraft, (next) => {
      next.declaration[field] = value;
    });
  };

  const updateProjectItem = (index, field, value) => {
    updateDraftState(setProjectsDraft, (next) => {
      if (!Array.isArray(next.projects) || !next.projects[index]) {
        return;
      }

      next.projects[index][field] = value;
    });
  };

  const updateProjectOutcomes = (index, value) => {
    updateDraftState(setProjectsDraft, (next) => {
      if (!Array.isArray(next.projects) || !next.projects[index]) {
        return;
      }

      next.projects[index].outcomes = fromMultilineText(value);
    });
  };

  const addProjectItem = () => {
    updateDraftState(setProjectsDraft, (next) => {
      next.projects.push(createProjectEntry());
    });
  };

  const removeProjectItem = (index) => {
    updateDraftState(setProjectsDraft, (next) => {
      if (!Array.isArray(next.projects)) {
        return;
      }

      next.projects.splice(index, 1);
    });
  };

  const saveProfile = () => {
    onQueuePersistToast?.('profile', 'Profile saved successfully.');
    onProfileDataChange(profileDraft);
    setProfileSaveMessage('Saved');
    setSaveClosePrompt({ label: 'Profile', windowId: 'profile-settings' });
  };

  const resetProfile = () => {
    setProfileDraft(cloneDraftValue(config.profileData || {}));
    setProfileSaveMessage('Reset');
  };
  const saveResume = () => {
    onQueuePersistToast?.('resume', 'Resume saved successfully.');
    onResumeDataChange(resumeDraft);
    setResumeSaveMessage('Saved');
    setSaveClosePrompt({ label: 'Resume', windowId: 'resume-settings' });
  };

  const resetResume = () => {
    setResumeDraft(cloneDraftValue(config.resumeData || {}));
    setResumeSaveMessage('Reset');
  };

  const saveProjects = () => {
    onQueuePersistToast?.('projects', 'Projects saved successfully.');
    onProjectsDataChange(projectsDraft);
    setProjectsSaveMessage('Saved');
    setSaveClosePrompt({ label: 'Projects', windowId: 'projects-settings' });
  };

  const renderSaveClosePrompt = () => {
    if (!saveClosePrompt) {
      return null;
    }

    const settingsLabel = `"${saveClosePrompt.label}" Settings`;

    return (
      <div className="settings-modal-backdrop" onClick={() => setSaveClosePrompt(null)}>
        <div className="settings-modal save-close-modal" onClick={(event) => event.stopPropagation()}>
          <h4>{settingsLabel}를 닫을까요?</h4>
          <p className="save-close-modal-message">
            저장이 완료되었습니다. 계속 수정하려면 취소를 누르고, 창을 닫으려면 닫기를 누르세요.
          </p>
          <div className="settings-modal-actions">
            <button
              type="button"
              className="profile-ghost-button"
              onClick={() => setSaveClosePrompt(null)}
            >
              취소
            </button>
            <button
              type="button"
              className="profile-primary-button"
              onClick={() => {
                const targetWindowId = saveClosePrompt.windowId || app.id;
                setSaveClosePrompt(null);
                onClose(targetWindowId);
              }}
            >
              닫기
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderInitializePrompt = () => {
    if (!initializePrompt) {
      return null;
    }

    return (
      <div className="settings-modal-backdrop" onClick={() => setInitializePrompt(null)}>
        <div className="settings-modal save-close-modal" onClick={(event) => event.stopPropagation()}>
          <h4>{initializePrompt.label}를 초기화 할까요?</h4>
          <p className="save-close-modal-message">
            확인을 누르면 기본 템플릿으로 초기화됩니다. 현재 내용은 초기화된 데이터로 대체됩니다.
          </p>
          <div className="settings-modal-actions">
            <button
              type="button"
              className="profile-ghost-button"
              onClick={() => setInitializePrompt(null)}
            >
              취소
            </button>
            <button
              type="button"
              className="profile-primary-button"
              onClick={() => {
                const target = initializePrompt.target;
                setInitializePrompt(null);
                resetPortfolioDocument(target);
              }}
            >
              확인
            </button>
          </div>
        </div>
      </div>
    );
  };

  const resetProjects = () => {
    setProjectsDraft(cloneDraftValue(config.projectsData || {}));
    setProjectsSaveMessage('Reset');
  };

  const handleResizeStart = useCallback((event) => {
    if (!isResizable) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    onFocus(app.id);

    resizeStateRef.current = {
      startX: event.clientX,
      startY: event.clientY,
      width: windowSize.width,
      height: windowSize.height
    };
    isResizingRef.current = true;
    setIsInteracting(true);

    const handleResizeMove = (moveEvent) => {
      if (!resizeStateRef.current) {
        return;
      }

      const nextWidth = resizeStateRef.current.width + (moveEvent.clientX - resizeStateRef.current.startX);
      const nextHeight = resizeStateRef.current.height + (moveEvent.clientY - resizeStateRef.current.startY);

      setWindowSize(
        clampWindowSize(
          {
            width: nextWidth,
            height: nextHeight
          },
          windowPositionRef.current,
          desktopMetrics,
          minWindowSize,
          resolvedDesktopLayout
        )
      );
    };

    const handleResizeEnd = () => {
      const finalSize = clampWindowSize(windowSizeRef.current, windowPositionRef.current, desktopMetrics, minWindowSize, resolvedDesktopLayout);
      windowSizeRef.current = finalSize;
      setWindowSize(finalSize);
      commitWindowLayout(finalSize, windowPositionRef.current);
      resizeStateRef.current = null;
      isResizingRef.current = false;
      setIsInteracting(false);
      window.removeEventListener('mousemove', handleResizeMove);
      window.removeEventListener('mouseup', handleResizeEnd);
    };

    window.addEventListener('mousemove', handleResizeMove);
    window.addEventListener('mouseup', handleResizeEnd);
  }, [app.id, commitWindowLayout, desktopMetrics, isResizable, minWindowSize, onFocus, resolvedDesktopLayout, windowSize.height, windowSize.width]);

  const renderSettings = () => {
    const desktopIcons = config.desktopIcons || [];
    const isDraggingCustomIcon = Boolean(draggedIconId);
    const handleAdminLogoutClick = () => {
      onAdminLogout?.({ closeWindowId: app.id });
    };

    return (
      <div className="window-body settings-content">
        <div className="setting-section settings-theme-section">
          <label>{t(activeLanguage, 'auth_out')}</label>
          <div className="settings-theme-header settings-authout-header">
            <div className="theme-switch-copy">
              <strong>{isAdminAuthenticated ? t(activeLanguage, 'auth_out_ready') : t(activeLanguage, 'auth_out_idle')}</strong>
              <span>{t(activeLanguage, 'auth_out_help')}</span>
            </div>
            <div className="settings-authout-actions">
              <button
                type="button"
                className="profile-ghost-button settings-authout-button"
                onClick={handleAdminLogoutClick}
                disabled={!isAdminAuthenticated}
              >
                {t(activeLanguage, 'auth_out_action')}
              </button>
            </div>
          </div>

          <label>Auth Center</label>
          <div className="settings-theme-header settings-authout-header">
            <div className="theme-switch-copy">
              <strong>Auth Center</strong>
              <span>Open the AI Chat Hub status for NVIDIA, Claude, Antigravity and Codex.</span>
            </div>
            <div className="settings-authout-actions">
              <button
                type="button"
                className="profile-ghost-button settings-authout-button"
                onClick={() => onOpenApp?.('ai-auth')}
              >
                Open Auth
              </button>
            </div>
          </div>

          <label>Guard Bypass</label>
          <div className="settings-theme-header settings-authout-header">
            <div className="theme-switch-copy">
              <strong>Bypass 인증</strong>
              <span>OCIOTP 승인 후 이 브라우저에만 blacklist-guard 필터 예외 키를 발급합니다.</span>
            </div>
            <div className="settings-authout-actions">
              <a
                className="profile-ghost-button settings-authout-button"
                href={getGuardBypassLoginUrl()}
                title="blacklist-guard 필터 예외를 이 브라우저에만 적용합니다."
              >
                Bypass 인증
              </a>
            </div>
          </div>

          <label>{t(activeLanguage, 'auth_pass')}</label>
          <div className="settings-theme-header settings-authpass-header">
            <div className="theme-switch-copy">
              <strong>{isAdminAuthPassEnabled ? t(activeLanguage, 'auth_pass_enabled') : t(activeLanguage, 'auth_pass_disabled')}</strong>
              <span>
                {t(activeLanguage, 'auth_pass_help')}
              </span>
            </div>
            <label className="theme-switch" aria-label="Toggle auth pass">
              <input
                type="checkbox"
                checked={Boolean(isAdminAuthPassEnabled)}
                onChange={(event) => onAdminAuthPassToggle(event.target.checked)}
              />
              <span className="theme-switch-track">
                <span className="theme-switch-label theme-switch-label-light" aria-hidden="true">
                  <span className="theme-switch-label-text">Off</span>
                </span>
                <span className="theme-switch-label theme-switch-label-dark" aria-hidden="true">
                  <span className="theme-switch-label-text">On</span>
                </span>
                <span className="theme-switch-thumb"></span>
              </span>
            </label>
          </div>

          <label>{t(activeLanguage, 'language')}</label>
          <div className="settings-language-header">
            <div className="theme-switch-copy">
              <strong>{activeLanguage === 'korean' ? t(activeLanguage, 'language_korean') : t(activeLanguage, 'language_english')}</strong>
              <span>{t(activeLanguage, 'language_help')}</span>
            </div>
            <div className="settings-segmented-control" role="radiogroup" aria-label="Select language">
              <button
                type="button"
                className={`settings-segmented-option ${activeLanguage === 'english' ? 'active' : ''}`.trim()}
                onClick={() => applySettingChange('language', 'english', 'Language saved successfully.')}
                role="radio"
                aria-checked={activeLanguage === 'english'}
              >
                {t(activeLanguage, 'language_english')}
              </button>
              <button
                type="button"
                className={`settings-segmented-option ${activeLanguage === 'korean' ? 'active' : ''}`.trim()}
                onClick={() => applySettingChange('language', 'korean', 'Language saved successfully.')}
                role="radio"
                aria-checked={activeLanguage === 'korean'}
              >
                {t(activeLanguage, 'language_korean')}
              </button>
            </div>
          </div>

          <label>{t(activeLanguage, 'change_os')}</label>
          <div className="settings-language-header">
            <div className="theme-switch-copy">
              <strong>{t(activeLanguage, `os_${resolvedOsMode}`)}</strong>
              <span>{t(activeLanguage, 'change_os_help')}</span>
            </div>
            <div className="settings-segmented-control" role="radiogroup" aria-label="Select OS shell">
              {['win', 'mac', 'lin'].map((mode) => (
                <button
                  key={mode}
                  type="button"
                  className={`settings-segmented-option ${resolvedOsMode === mode ? 'active' : ''}`.trim()}
                  onClick={() => applySettingChange('osMode', mode, 'OS mode saved successfully.')}
                  role="radio"
                  aria-checked={resolvedOsMode === mode}
                >
                  {t(activeLanguage, `os_${mode}`)}
                </button>
              ))}
            </div>
          </div>

          <label>{t(activeLanguage, 'system_theme')}</label>
          <div
            className={`settings-theme-header ${wallpaperSelectionTarget === 'theme' ? 'selection-target-active' : ''}`.trim()}
            onClick={() => setWallpaperSelectionTarget('theme')}
            role="button"
            tabIndex={0}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                setWallpaperSelectionTarget('theme');
              }
            }}
          >
            <div className="theme-switch-copy">
              <strong>{activeTheme === 'light' ? t(activeLanguage, 'theme_light') : t(activeLanguage, 'theme_dark')}</strong>
              <span>
                {t(activeLanguage, 'system_theme_help')}
                {wallpaperSelectionTarget === 'theme' ? ` ${t(activeLanguage, 'system_theme_selected_hint')}` : ''}
              </span>
            </div>
            <label className="theme-switch" aria-label="Toggle system theme">
              <input
                type="checkbox"
                checked={activeTheme === 'dark'}
                onChange={(e) => applySettingChange('theme', e.target.checked ? 'dark' : 'light', 'Theme saved successfully.')}
              />
              <span className="theme-switch-track">
                <span className="theme-switch-label theme-switch-label-light" aria-hidden="true">
                  <span className="theme-switch-badge theme-switch-badge-light">L</span>
                  <span className="theme-switch-label-text">{t(activeLanguage, 'theme_light')}</span>
                </span>
                <span className="theme-switch-label theme-switch-label-dark" aria-hidden="true">
                  <span className="theme-switch-badge theme-switch-badge-dark">D</span>
                  <span className="theme-switch-label-text">{t(activeLanguage, 'theme_dark')}</span>
                </span>
                <span className="theme-switch-thumb"></span>
              </span>
            </label>
          </div>
          <div
            className={`landing-settings-grid ${wallpaperSelectionTarget === 'landing' ? 'selection-target-active' : ''}`.trim()}
            onClick={() => setWallpaperSelectionTarget('landing')}
            role="button"
            tabIndex={0}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                setWallpaperSelectionTarget('landing');
              }
            }}
          >
            <div className="theme-switch-copy">
              <strong className="landing-badge-title">
                <span>{t(activeLanguage, 'landingpage_background_prefix')}</span>
                <span className="theme-switch-badge wallpaper-card-badge-landing">B</span>
                <span>{t(activeLanguage, 'landingpage_background_suffix')}</span>
              </strong>
              <span>{t(activeLanguage, 'landingpage_background_help')}</span>
            </div>
          </div>
        </div>

        <div className="setting-section">
          <div className="setting-section-header">
            <label>{t(activeLanguage, 'uploaded_wallpapers')}</label>
            <button
              type="button"
              className="settings-upload-button settings-upload-button-i18n"
              data-label={t(activeLanguage, 'upload_file')}
              onClick={() => setIsUploadModalOpen(true)}
              disabled={uploading}
            >
              {t(activeLanguage, 'upload_file')}
            </button>
          </div>
          <input
            ref={wallpaperInputRef}
            className="settings-hidden-file-input"
            type="file"
            accept="image/png,image/jpeg,.png,.jpg,.jpeg"
            onChange={handleWallpaperUpload}
            disabled={uploading}
          />
          {uploading && <p className="upload-status settings-upload-feedback">Uploading wallpaper image...</p>}
          {deletingWallpaperId ? <p className="upload-status settings-upload-feedback">Deleting wallpaper image...</p> : null}
          {uploadError && <p className="upload-error settings-upload-feedback">{uploadError}</p>}
          {uploadMessage && <p className="upload-success settings-upload-feedback">{uploadMessage}</p>}
          {wallpapersLoading ? (
            <div className="loading-spinner">Loading wallpapers...</div>
          ) : wallpapers.length === 0 ? (
            <p className="empty-state">No uploaded wallpaper images yet.</p>
          ) : (
            <div className={`uploaded-wallpapers-container ${wallpapers.length > 10 ? 'scrollable' : ''}`}>
              <div className="uploaded-wallpapers-grid">
                {wallpapers.map((wallpaper) => (
                  <WallpaperCard
                    key={wallpaper.id}
                    label={wallpaper.name}
                    selected={
                      wallpaperSelectionTarget === 'landing'
                        ? landingPageSettings.backgroundImage === wallpaper.url
                        : activeThemeWallpaper === wallpaper.url
                    }
                    assignedThemes={[
                      ...(config.settings.themeWallpapers?.light === wallpaper.url ? ['light'] : []),
                      ...(config.settings.themeWallpapers?.dark === wallpaper.url ? ['dark'] : []),
                      ...(landingPageSettings.backgroundImage === wallpaper.url ? ['landing'] : [])
                    ]}
                    onClick={() => {
                      if (wallpaperSelectionTarget === 'landing') {
                        applySettingChange('landingPage', { backgroundImage: wallpaper.url }, 'Landing background saved successfully.');
                        return;
                      }

                      applySettingChange('themeWallpaper', { theme: activeTheme, wallpaper: wallpaper.url }, 'Wallpaper saved successfully.');
                    }}
                    previewStyle={{ backgroundImage: `url(${wallpaper.url})` }}
                    onDelete={() => handleWallpaperDelete(wallpaper)}
                  />
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="setting-section">
          <div className="setting-section-header">
            <label>{t(activeLanguage, 'icons')}</label>
            <button
              type="button"
              className="settings-upload-button"
              onClick={addDesktopIcon}
            >
              {t(activeLanguage, 'add_icon')}
            </button>
          </div>
          {(iconMessage || iconError || uploadingIconId || deletingIconId) ? (
            <div className="icons-status-row">
              {uploadingIconId ? <p className="upload-status">Uploading icon file...</p> : null}
              {deletingIconId ? <p className="upload-status">Deleting shortcut...</p> : null}
              {iconError ? <p className="upload-error">{iconError}</p> : null}
              {iconMessage ? <p className="upload-success">{iconMessage}</p> : null}
            </div>
          ) : null}
          <div className="icons-table-shell">
            <div className="icons-table-header">
              <span>{t(activeLanguage, 'table_no')}</span>
              <span>{t(activeLanguage, 'table_shortcut_name')}</span>
              <span>{t(activeLanguage, 'table_icon_emoji')}</span>
              <span>{t(activeLanguage, 'table_upload')}</span>
              <span>{t(activeLanguage, 'table_file_name')}</span>
              <span>{t(activeLanguage, 'table_link')}</span>
              <span>{t(activeLanguage, 'table_delete')}</span>
            </div>
            <div className="icons-table-body">
              {desktopIcons.map((iconItem, index) => {
                const isProtected = PROTECTED_ICON_IDS.includes(iconItem.id);
                const localizedShortcutLabel = isProtected
                  ? getLocalizedAppLabel(iconItem.id, iconItem.label, activeLanguage)
                  : (shortcutLabelDrafts[iconItem.id] ?? getLocalizedShortcutLabel(iconItem, activeLanguage));
                return (
                  <div
                    key={iconItem.id}
                    className={`icons-table-row ${isProtected ? 'protected' : ''} ${draggedIconId === iconItem.id ? 'dragging' : ''} ${dragOverIconId === iconItem.id && !isProtected && draggedIconId !== iconItem.id ? 'drag-target' : ''} ${isProtected && isDraggingCustomIcon ? 'drag-disabled' : ''}`.trim()}
                    draggable={!isProtected}
                    onDragStart={(event) => handleIconDragStart(iconItem.id, event)}
                    onDragEnter={() => handleIconDragEnter(iconItem.id)}
                    onDragOver={(event) => {
                      if (!isProtected) {
                        event.preventDefault();
                        event.dataTransfer.dropEffect = 'move';
                      }
                    }}
                    onDrop={(event) => handleIconDrop(iconItem.id, event)}
                    onDragEnd={handleIconDragEnd}
                  >
                    <span className="icons-cell index">{index + 1}</span>
                    <span className="icons-cell">
                      <input
                        className="icons-text-input"
                        value={localizedShortcutLabel}
                        onChange={(event) => handleShortcutLabelDraftChange(iconItem.id, event.target.value)}
                        onBlur={() => handleShortcutLabelCommit(iconItem.id)}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter') {
                            event.currentTarget.blur();
                          }
                        }}
                        maxLength={12}
                        readOnly={isProtected}
                      />
                    </span>
                    <span className="icons-cell icon-value">
                      <div className="icons-preview">
                        <SettingsShortcutPreview
                          iconId={iconItem.id}
                          iconValue={iconItem.icon}
                          altText={iconItem.label}
                        />
                      </div>
                    </span>
                    <span className="icons-cell">
                      <input
                        ref={(node) => {
                          if (node) {
                            iconInputRefs.current[iconItem.id] = node;
                          }
                        }}
                        className="settings-hidden-file-input"
                        type="file"
                        accept="image/svg+xml,image/png,image/jpeg,image/x-icns,.svg,.png,.jpg,.jpeg,.icns"
                        onChange={(event) => handleIconUpload(iconItem.id, event)}
                      />
                      <button
                        type="button"
                        className="profile-ghost-button"
                        onClick={() => iconInputRefs.current[iconItem.id]?.click()}
                        disabled={uploadingIconId === iconItem.id}
                      >
                        {t(activeLanguage, 'upload')}
                      </button>
                    </span>
                    <span className="icons-cell file-name">{getIconDisplayName(iconItem.icon)}</span>
                    <span className="icons-cell">
                      {isProtected ? null : (
                        <input
                          className="icons-text-input"
                          value={iconItem.link || ''}
                          onChange={(event) => updateDesktopIcon(iconItem.id, { link: event.target.value }, null)}
                          placeholder="https://..."
                        />
                      )}
                    </span>
                    <span className="icons-cell">
                      {isProtected ? null : (
                        <button
                          type="button"
                          className="profile-ghost-button danger"
                          onClick={() => removeDesktopIcon(iconItem.id)}
                          disabled={deletingIconId === iconItem.id}
                        >
                          {t(activeLanguage, 'delete')}
                        </button>
                      )}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {isUploadModalOpen ? (
          <div className="settings-modal-backdrop" onClick={() => setIsUploadModalOpen(false)}>
            <div
              className="settings-modal"
              onClick={(event) => event.stopPropagation()}
            >
              <h4>Upload Wallpaper Image</h4>
              <p className="upload-help">
                Uploaded PNG, JPG, and JPEG files are stored in <code>user-data/wallpapers</code>.
              </p>
              <div className="settings-modal-actions">
                <button
                  type="button"
                  className="profile-ghost-button"
                  onClick={() => setIsUploadModalOpen(false)}
                >
                  {t(activeLanguage, 'cancel')}
                </button>
                <button
                  type="button"
                  className="settings-upload-button settings-upload-button-i18n"
                  data-label={t(activeLanguage, 'upload_file')}
                  onClick={() => {
                    wallpaperInputRef.current?.click();
                    setIsUploadModalOpen(false);
                  }}
                  disabled={uploading}
                >
                  {t(activeLanguage, 'upload_file')}
                </button>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    );
  };

  const renderSettingsToolbar = (title, description, saveMessage, onReset, onSave) => (
    <div className="profile-editor-toolbar">
      <div>
        <h3>{title}</h3>
        <p>{description}</p>
      </div>
      <div className="profile-editor-actions">
        {saveMessage ? <span className="profile-save-message">{saveMessage}</span> : null}
        <button type="button" className="profile-ghost-button" onClick={onReset}>
          Reset
        </button>
        <button type="button" className="profile-primary-button" onClick={onSave}>
          Save
        </button>
      </div>
    </div>
  );

  const renderProfileSettings = () => {
    const basicInfo = profileDraft.basic_info || {};
    const experienceSummary = profileDraft.experience_summary || {};
    const softwareSkills = Array.isArray(profileDraft.skills?.software_skills) ? profileDraft.skills.software_skills : [];
    const languages = Array.isArray(profileDraft.languages) ? profileDraft.languages : [];
    const certificates = Array.isArray(profileDraft.certificates) ? profileDraft.certificates : [];
    const activities = Array.isArray(profileDraft.activities) ? profileDraft.activities : [];
    const awards = Array.isArray(profileDraft.awards) ? profileDraft.awards : [];
    const selfSections = Array.isArray(profileDraft.self_introduction?.sections) ? profileDraft.self_introduction.sections : [];

    return (
      <div className="window-body profile-preview-page settings-preview-page">
        {renderSettingsToolbar(
          'Profile Settings',
          'Edit `userProfile.json` directly from the same layout used by the Profile window.',
          profileSaveMessage,
          resetProfile,
          saveProfile
        )}
        <div className="resume-preview-shell">
          <div className="resume-preview">
            <div className="resume-preview-header">
              <div className="resume-preview-title">Profile</div>
              <div className="profile-hero profile-hero-edit">
                <div className="profile-photo-panel">
                  <input
                    ref={profilePhotoInputRef}
                    className="settings-hidden-file-input"
                    type="file"
                    accept="image/png,image/jpeg,.png,.jpg,.jpeg"
                    onChange={handleProfilePhotoUpload}
                    disabled={uploading}
                  />
                  <button
                    type="button"
                    className="profile-photo-upload-button"
                    onClick={() => profilePhotoInputRef.current?.click()}
                    disabled={uploading}
                  >
                    {uploading ? 'Uploading...' : 'File Upload'}
                  </button>
                  {basicInfo.photo ? (
                    <img className="profile-photo-preview" src={basicInfo.photo} alt="Profile" />
                  ) : (
                    <div className="profile-photo-placeholder">Photo</div>
                  )}
                </div>
                <div className="profile-hero-copy">
                  <input
                    className="preview-edit-input preview-edit-heading"
                    value={basicInfo.name_en || ''}
                    onChange={(event) => updateProfileBasicInfo('name_en', event.target.value)}
                    placeholder="Your Name"
                  />
                  <input
                    className="preview-edit-input preview-edit-subtitle"
                    value={basicInfo.headline || ''}
                    onChange={(event) => updateProfileBasicInfo('headline', event.target.value)}
                    placeholder="Your Headline"
                  />
                </div>
              </div>
              <div className="resume-contact-row preview-edit-contact">
                <input className="preview-edit-input" value={basicInfo.address || ''} onChange={(event) => updateProfileBasicInfo('address', event.target.value)} placeholder="Location" />
                <input className="preview-edit-input" value={basicInfo.email || ''} onChange={(event) => updateProfileBasicInfo('email', event.target.value)} placeholder="email@example.com" />
                <input className="preview-edit-input" value={basicInfo.homepage || ''} onChange={(event) => updateProfileBasicInfo('homepage', event.target.value)} placeholder="website.com" />
              </div>
            </div>

            <div className="resume-preview-body">
              <div className="resume-main-column">
                <section className="resume-section">
                  <h2>Summary</h2>
                  <textarea
                    className="preview-edit-textarea"
                    value={basicInfo.summary || ''}
                    onChange={(event) => updateProfileBasicInfo('summary', event.target.value)}
                    rows={5}
                    placeholder="Write your profile summary."
                  />
                </section>

                <section className="resume-section">
                  <div className="preview-edit-section-header">
                    <h2>Activities</h2>
                    <button type="button" className="profile-ghost-button" onClick={() => addProfileArrayItem('activities', createProfileActivity)}>Add</button>
                  </div>
                  {activities.map((item, index) => (
                    <article key={`profile-settings-activity-${index}`} className="resume-entry">
                      <div className="preview-edit-card-actions">
                        <button type="button" className="profile-ghost-button danger" onClick={() => removeProfileArrayItem('activities', index)}>Remove</button>
                      </div>
                      <div className="resume-entry-heading">
                        <div className="preview-edit-stack">
                          <input className="preview-edit-input" value={item.name || ''} onChange={(event) => updateProfileArrayItem('activities', index, 'name', event.target.value)} placeholder="Activity" />
                          <input className="preview-edit-input" value={item.activity_type || ''} onChange={(event) => updateProfileArrayItem('activities', index, 'activity_type', event.target.value)} placeholder="Activity type" />
                        </div>
                        <div className="resume-entry-meta preview-edit-stack">
                          <input className="preview-edit-input" value={item.start_date || ''} onChange={(event) => updateProfileArrayItem('activities', index, 'start_date', event.target.value)} placeholder="Start date" />
                          <input className="preview-edit-input" value={item.end_date || ''} onChange={(event) => updateProfileArrayItem('activities', index, 'end_date', event.target.value)} placeholder="End date" />
                        </div>
                      </div>
                      <input className="preview-edit-input" value={item.institution || ''} onChange={(event) => updateProfileArrayItem('activities', index, 'institution', event.target.value)} placeholder="Institution" />
                      <textarea className="preview-edit-textarea compact" value={item.description || ''} onChange={(event) => updateProfileArrayItem('activities', index, 'description', event.target.value)} rows={3} placeholder="Description" />
                    </article>
                  ))}
                </section>

                <section className="resume-section">
                  <div className="preview-edit-section-header">
                    <h2>Self Introduction</h2>
                    <button type="button" className="profile-ghost-button" onClick={() => addProfileArrayItem('self_sections', createSelfIntroductionSection)}>Add</button>
                  </div>
                  {selfSections.map((section, index) => (
                    <article key={`self-section-${index}`} className="resume-side-entry">
                      <div className="preview-edit-card-actions">
                        <button type="button" className="profile-ghost-button danger" onClick={() => removeProfileArrayItem('self_sections', index)}>Remove</button>
                      </div>
                      <input className="preview-edit-input" value={section.title || ''} onChange={(event) => updateProfileArrayItem('self_sections', index, 'title', event.target.value)} placeholder="Section title" />
                      <textarea className="preview-edit-textarea compact" value={section.content || ''} onChange={(event) => updateProfileArrayItem('self_sections', index, 'content', event.target.value)} rows={4} placeholder="Section content" />
                    </article>
                  ))}
                  <textarea className="preview-edit-textarea compact" value={profileDraft.self_introduction?.cover_letter || ''} onChange={(event) => updateProfileSelfIntroduction('cover_letter', event.target.value)} rows={4} placeholder="Cover letter" />
                </section>
              </div>

              <aside className="resume-side-column">
                <section className="resume-section">
                  <h2>Experience Summary</h2>
                  <textarea className="preview-edit-textarea compact" value={experienceSummary.total_experience || ''} onChange={(event) => updateProfileExperienceSummary('total_experience', event.target.value)} rows={3} placeholder="Total experience summary" />
                  <div className="preview-edit-inline">
                    <input className="preview-edit-input preview-edit-number" type="number" value={experienceSummary.total_experience_years ?? 0} onChange={(event) => updateProfileExperienceSummary('total_experience_years', event.target.value)} placeholder="Years" />
                    <input className="preview-edit-input preview-edit-number" type="number" value={experienceSummary.total_experience_months ?? 0} onChange={(event) => updateProfileExperienceSummary('total_experience_months', event.target.value)} placeholder="Months" />
                  </div>
                </section>

                <section className="resume-section">
                  <div className="preview-edit-section-header">
                    <h2>Skills</h2>
                    <button type="button" className="profile-ghost-button" onClick={() => addProfileArrayItem('software_skills', createProfileSkill)}>Add</button>
                  </div>
                  {softwareSkills.map((item, index) => (
                    <div key={`profile-skill-edit-${index}`} className="resume-skill-row preview-edit-skill-row">
                      <input className="preview-edit-input" value={item.name || ''} onChange={(event) => updateProfileArrayItem('software_skills', index, 'name', event.target.value)} placeholder="Skill" />
                      <input className="preview-edit-input" value={item.proficiency || ''} onChange={(event) => updateProfileArrayItem('software_skills', index, 'proficiency', event.target.value)} placeholder="Level" />
                      <button type="button" className="profile-ghost-button danger" onClick={() => removeProfileArrayItem('software_skills', index)}>Remove</button>
                    </div>
                  ))}
                </section>

                <section className="resume-section">
                  <div className="preview-edit-section-header">
                    <h2>Languages</h2>
                    <button type="button" className="profile-ghost-button" onClick={() => addProfileArrayItem('languages', createProfileLanguage)}>Add</button>
                  </div>
                  {languages.map((item, index) => (
                    <article key={`profile-language-edit-${index}`} className="resume-side-entry">
                      <div className="preview-edit-card-actions">
                        <button type="button" className="profile-ghost-button danger" onClick={() => removeProfileArrayItem('languages', index)}>Remove</button>
                      </div>
                      <input className="preview-edit-input" value={item.language || ''} onChange={(event) => updateProfileArrayItem('languages', index, 'language', event.target.value)} placeholder="Language" />
                      <div className="preview-edit-inline">
                        <input className="preview-edit-input" value={item.proficiency || ''} onChange={(event) => updateProfileArrayItem('languages', index, 'proficiency', event.target.value)} placeholder="Proficiency" />
                        <input className="preview-edit-input" value={item.score || ''} onChange={(event) => updateProfileArrayItem('languages', index, 'score', event.target.value)} placeholder="Score" />
                      </div>
                      <input className="preview-edit-input" value={item.test_name || ''} onChange={(event) => updateProfileArrayItem('languages', index, 'test_name', event.target.value)} placeholder="Test name" />
                    </article>
                  ))}
                </section>

                <section className="resume-section">
                  <div className="preview-edit-section-header">
                    <h2>Certificates</h2>
                    <button type="button" className="profile-ghost-button" onClick={() => addProfileArrayItem('certificates', createProfileCertificate)}>Add</button>
                  </div>
                  {certificates.map((item, index) => (
                    <article key={`profile-certificate-edit-${index}`} className="resume-side-entry">
                      <div className="preview-edit-card-actions">
                        <button type="button" className="profile-ghost-button danger" onClick={() => removeProfileArrayItem('certificates', index)}>Remove</button>
                      </div>
                      <input className="preview-edit-input" value={item.name || ''} onChange={(event) => updateProfileArrayItem('certificates', index, 'name', event.target.value)} placeholder="Certificate" />
                      <div className="preview-edit-inline">
                        <input className="preview-edit-input" value={item.issuer || ''} onChange={(event) => updateProfileArrayItem('certificates', index, 'issuer', event.target.value)} placeholder="Issuer" />
                        <input className="preview-edit-input" value={item.acquired_date || ''} onChange={(event) => updateProfileArrayItem('certificates', index, 'acquired_date', event.target.value)} placeholder="Acquired date" />
                      </div>
                      <textarea className="preview-edit-textarea compact" value={item.note || ''} onChange={(event) => updateProfileArrayItem('certificates', index, 'note', event.target.value)} rows={2} placeholder="Note" />
                    </article>
                  ))}
                </section>

                <section className="resume-section">
                  <div className="preview-edit-section-header">
                    <h2>Awards</h2>
                    <button type="button" className="profile-ghost-button" onClick={() => addProfileArrayItem('awards', createProfileAward)}>Add</button>
                  </div>
                  {awards.map((item, index) => (
                    <article key={`profile-award-edit-${index}`} className="resume-side-entry">
                      <div className="preview-edit-card-actions">
                        <button type="button" className="profile-ghost-button danger" onClick={() => removeProfileArrayItem('awards', index)}>Remove</button>
                      </div>
                      <input className="preview-edit-input" value={item.award_name || ''} onChange={(event) => updateProfileArrayItem('awards', index, 'award_name', event.target.value)} placeholder="Award" />
                      <div className="preview-edit-inline">
                        <input className="preview-edit-input" value={item.organization || ''} onChange={(event) => updateProfileArrayItem('awards', index, 'organization', event.target.value)} placeholder="Organization" />
                        <input className="preview-edit-input" value={item.award_date || ''} onChange={(event) => updateProfileArrayItem('awards', index, 'award_date', event.target.value)} placeholder="Award date" />
                      </div>
                      <textarea className="preview-edit-textarea compact" value={item.description || ''} onChange={(event) => updateProfileArrayItem('awards', index, 'description', event.target.value)} rows={2} placeholder="Description" />
                    </article>
                  ))}
                </section>
              </aside>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderResumeSettings = () => {
    const workExperiences = Array.isArray(resumeDraft.work_experiences) ? resumeDraft.work_experiences : [];
    const educationItems = Array.isArray(resumeDraft.education) ? resumeDraft.education : [];
    const trainingItems = Array.isArray(resumeDraft.training) ? resumeDraft.training : [];
    const military = resumeDraft.military_service || {};
    const declaration = resumeDraft.declaration || {};

    return (
      <div className="window-body context-settings-page settings-preview-page">
        {renderSettingsToolbar(
          'Resume Settings',
          'Edit `userResume.json` directly from the same layout used by the Resume window.',
          resumeSaveMessage,
          resetResume,
          saveResume
        )}
        <div className="resume-preview-shell">
          <div className="resume-preview">
            <div className="resume-preview-body">
              <div className="resume-main-column">
                <section className="resume-section">
                  <div className="preview-edit-section-header">
                    <h2>Work Experiences</h2>
                    <button type="button" className="profile-ghost-button" onClick={() => addResumeArrayItem('work_experiences', createResumeWorkExperience)}>Add</button>
                  </div>
                  {workExperiences.map((item, index) => (
                    <article key={`resume-work-edit-${index}`} className="resume-entry">
                      <div className="preview-edit-card-actions">
                        <button type="button" className="profile-ghost-button danger" onClick={() => removeResumeArrayItem('work_experiences', index)}>Remove</button>
                      </div>
                      <div className="resume-entry-heading">
                        <div className="preview-edit-stack">
                          <input className="preview-edit-input" value={item.company || ''} onChange={(event) => updateResumeArrayItem('work_experiences', index, 'company', event.target.value)} placeholder="Company" />
                          <input className="preview-edit-input" value={item.job_title || ''} onChange={(event) => updateResumeArrayItem('work_experiences', index, 'job_title', event.target.value)} placeholder="Job title" />
                          <input className="preview-edit-input" value={item.department || ''} onChange={(event) => updateResumeArrayItem('work_experiences', index, 'department', event.target.value)} placeholder="Department" />
                        </div>
                        <div className="resume-entry-meta preview-edit-stack">
                          <input className="preview-edit-input" value={item.start_date || ''} onChange={(event) => updateResumeArrayItem('work_experiences', index, 'start_date', event.target.value)} placeholder="Start date" />
                          <input className="preview-edit-input" value={item.end_date || ''} onChange={(event) => updateResumeArrayItem('work_experiences', index, 'end_date', event.target.value)} placeholder="End date" />
                          <input className="preview-edit-input" value={item.duration || ''} onChange={(event) => updateResumeArrayItem('work_experiences', index, 'duration', event.target.value)} placeholder="Duration" />
                        </div>
                      </div>
                      <textarea className="preview-edit-textarea compact" value={toMultilineText(item.main_tasks)} onChange={(event) => updateResumeArrayLines('work_experiences', index, 'main_tasks', event.target.value)} rows={4} placeholder="Main tasks, one per line" />
                      <textarea className="preview-edit-textarea compact" value={toMultilineText(item.achievements)} onChange={(event) => updateResumeArrayLines('work_experiences', index, 'achievements', event.target.value)} rows={4} placeholder="Achievements, one per line" />
                    </article>
                  ))}
                </section>

                <section className="resume-section">
                  <div className="preview-edit-section-header">
                    <h2>Education</h2>
                    <button type="button" className="profile-ghost-button" onClick={() => addResumeArrayItem('education', createResumeEducation)}>Add</button>
                  </div>
                  {educationItems.map((item, index) => (
                    <article key={`resume-education-edit-${index}`} className="resume-side-entry">
                      <div className="preview-edit-card-actions">
                        <button type="button" className="profile-ghost-button danger" onClick={() => removeResumeArrayItem('education', index)}>Remove</button>
                      </div>
                      <input className="preview-edit-input" value={item.school_name || ''} onChange={(event) => updateResumeArrayItem('education', index, 'school_name', event.target.value)} placeholder="School" />
                      <div className="preview-edit-inline">
                        <input className="preview-edit-input" value={item.degree || ''} onChange={(event) => updateResumeArrayItem('education', index, 'degree', event.target.value)} placeholder="Degree" />
                        <input className="preview-edit-input" value={item.major || ''} onChange={(event) => updateResumeArrayItem('education', index, 'major', event.target.value)} placeholder="Major" />
                      </div>
                      <div className="preview-edit-inline">
                        <input className="preview-edit-input" value={item.start_date || ''} onChange={(event) => updateResumeArrayItem('education', index, 'start_date', event.target.value)} placeholder="Start date" />
                        <input className="preview-edit-input" value={item.end_date || ''} onChange={(event) => updateResumeArrayItem('education', index, 'end_date', event.target.value)} placeholder="End date" />
                      </div>
                      <input className="preview-edit-input" value={item.status || ''} onChange={(event) => updateResumeArrayItem('education', index, 'status', event.target.value)} placeholder="Status" />
                    </article>
                  ))}
                </section>
              </div>

              <aside className="resume-side-column">
                <section className="resume-section">
                  <div className="preview-edit-section-header">
                    <h2>Training</h2>
                    <button type="button" className="profile-ghost-button" onClick={() => addResumeArrayItem('training', createResumeTraining)}>Add</button>
                  </div>
                  {trainingItems.map((item, index) => (
                    <article key={`resume-training-edit-${index}`} className="resume-side-entry">
                      <div className="preview-edit-card-actions">
                        <button type="button" className="profile-ghost-button danger" onClick={() => removeResumeArrayItem('training', index)}>Remove</button>
                      </div>
                      <input className="preview-edit-input" value={item.course_name || ''} onChange={(event) => updateResumeArrayItem('training', index, 'course_name', event.target.value)} placeholder="Course" />
                      <input className="preview-edit-input" value={item.institution || ''} onChange={(event) => updateResumeArrayItem('training', index, 'institution', event.target.value)} placeholder="Institution" />
                      <div className="preview-edit-inline">
                        <input className="preview-edit-input" value={item.completion_date || ''} onChange={(event) => updateResumeArrayItem('training', index, 'completion_date', event.target.value)} placeholder="Completion date" />
                        <input className="preview-edit-input" value={item.hours || ''} onChange={(event) => updateResumeArrayItem('training', index, 'hours', event.target.value)} placeholder="Hours" />
                      </div>
                      <textarea className="preview-edit-textarea compact" value={item.description || ''} onChange={(event) => updateResumeArrayItem('training', index, 'description', event.target.value)} rows={3} placeholder="Description" />
                    </article>
                  ))}
                </section>

                <section className="resume-section">
                  <h2>Military Service</h2>
                  <div className="preview-edit-inline">
                    <select className="preview-edit-input" value={military.served === null ? '' : String(military.served)} onChange={(event) => updateResumeMilitary('served', event.target.value === '' ? null : event.target.value === 'true')}>
                      <option value="">Not specified</option>
                      <option value="true">Served</option>
                      <option value="false">Not served</option>
                    </select>
                    <input className="preview-edit-input" value={military.branch || ''} onChange={(event) => updateResumeMilitary('branch', event.target.value)} placeholder="Branch" />
                  </div>
                  <div className="preview-edit-inline">
                    <input className="preview-edit-input" value={military.rank || ''} onChange={(event) => updateResumeMilitary('rank', event.target.value)} placeholder="Rank" />
                    <input className="preview-edit-input" value={military.specialty || ''} onChange={(event) => updateResumeMilitary('specialty', event.target.value)} placeholder="Specialty" />
                  </div>
                  <div className="preview-edit-inline">
                    <input className="preview-edit-input" value={military.start_date || ''} onChange={(event) => updateResumeMilitary('start_date', event.target.value)} placeholder="Start date" />
                    <input className="preview-edit-input" value={military.end_date || ''} onChange={(event) => updateResumeMilitary('end_date', event.target.value)} placeholder="End date" />
                  </div>
                  <textarea className="preview-edit-textarea compact" value={military.exemption_reason || ''} onChange={(event) => updateResumeMilitary('exemption_reason', event.target.value)} rows={2} placeholder="Exemption reason" />
                </section>

                <section className="resume-section">
                  <h2>Declaration</h2>
                  <textarea className="preview-edit-textarea compact" value={declaration.statement || ''} onChange={(event) => updateResumeDeclaration('statement', event.target.value)} rows={3} placeholder="Statement" />
                  <div className="preview-edit-inline">
                    <input className="preview-edit-input" value={declaration.date || ''} onChange={(event) => updateResumeDeclaration('date', event.target.value)} placeholder="Date" />
                    <input className="preview-edit-input" value={declaration.name || ''} onChange={(event) => updateResumeDeclaration('name', event.target.value)} placeholder="Name" />
                  </div>
                </section>
              </aside>
            </div>
          </div>
        </div>
      </div>
    );
  };

  /**
   * Edit exactly the project the Projects window is showing, and carry that window's own
   * left/right navigation into the editor.
   *
   * It used to render all forty projects as one long grid, which meant "Edit" dropped the
   * operator at the top of a list and left them to find the card they had been looking at.
   * The index is shared with the viewer, so moving here moves there too.
   */
  /**
   * The project's images, one at a time, with the same navigation idiom as the project
   * carousel around it.
   *
   * The frame keeps its size when there is nothing to show: a card that changes height the
   * moment an image is added would make the whole carousel jump, and an empty slot is also
   * the honest answer to "does this project have images yet".
   */
  const renderProjectImageSlider = (project, { canManage = false } = {}) => {
    const projectId = project?.id || '';
    const images = getProjectImages(projectId);
    const total = images.length;
    const safeIndex = total ? Math.min(Math.max(projectImageIndex, 0), total - 1) : 0;
    const current = total ? images[safeIndex] : null;
    const atLimit = total >= projectImageLimit;
    const pad = (value) => String(value).padStart(2, '0');

    return (
      <div className={`projects-image-slider${canManage ? ' projects-image-slider-manage' : ''}`}>
        <div className="projects-image-stage">
          <button
            type="button"
            className="projects-image-nav projects-image-nav-left"
            onClick={() => setProjectImageIndex((prevIndex) => Math.max(0, prevIndex - 1))}
            disabled={safeIndex === 0 || total === 0}
            aria-label="Previous image"
          >
            {'<'}
          </button>

          <div className="projects-image-frame">
            {current ? (
              <>
                <img src={current.url} alt={current.name} />
                {current.source === 'comfy' ? <span className="projects-image-badge">AI</span> : null}
              </>
            ) : (
              <p className="projects-image-empty">등록된 이미지가 없습니다.</p>
            )}
          </div>

          <button
            type="button"
            className="projects-image-nav projects-image-nav-right"
            onClick={() => setProjectImageIndex((prevIndex) => Math.min(total - 1, prevIndex + 1))}
            disabled={total === 0 || safeIndex >= total - 1}
            aria-label="Next image"
          >
            {'>'}
          </button>
        </div>

        <div className="projects-image-meta">
          <span className="projects-image-count">({pad(total ? safeIndex + 1 : 0)}/{projectImageLimit})</span>
          {canManage && current ? (
            <button
              type="button"
              className="projects-image-button projects-image-button-danger projects-image-remove"
              onClick={() => handleProjectImageDelete(projectId, current.id)}
              disabled={projectImageBusy}
            >
              Remove
            </button>
          ) : null}
        </div>

        {canManage ? (
          <>
            <div className="projects-image-actions">
              <input
                ref={projectImageInputRef}
                type="file"
                accept="image/png,image/jpeg"
                style={{ display: 'none' }}
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  event.target.value = '';
                  if (file) handleProjectImageUpload(projectId, file);
                }}
              />
              <button
                type="button"
                className="projects-image-button"
                onClick={() => projectImageInputRef.current?.click()}
                disabled={projectImageBusy || atLimit}
              >
                File Upload
              </button>
              <button
                type="button"
                className="projects-image-button projects-image-button-primary"
                onClick={() => generateProjectImage(project)}
                disabled={projectImageBusy || atLimit}
              >
                {projectImageBusy ? '생성 중…' : 'image Generate'}
              </button>
              {atLimit ? <span className="projects-image-status">최대 {projectImageLimit}장까지 추가할 수 있습니다.</span> : null}
            </div>

            {/* A render takes the better part of a minute on a GPU three apps share, so the
                step counter and the bar are the only thing telling the operator the button
                did anything at all. */}
            {projectImageStatus ? (
              <div className="projects-image-progress" role="status" aria-live="polite">
                <span className="projects-image-status">{projectImageStatus}</span>
                <span className="projects-image-progress-track">
                  <span
                    className="projects-image-progress-bar"
                    style={{ width: `${Math.round(Math.min(1, Math.max(0, projectImageProgress)) * 100)}%` }}
                  />
                </span>
              </div>
            ) : null}
            {projectImageError ? <p className="projects-image-error">{projectImageError}</p> : null}
          </>
        ) : null}
      </div>
    );
  };

  const renderProjectsSettings = () => {
    const projectItems = Array.isArray(projectsDraft.projects) ? projectsDraft.projects : [];
    const lastIndex = projectItems.length - 1;
    const editIndex = Math.min(Math.max(currentProjectIndex, 0), Math.max(lastIndex, 0));
    const item = projectItems[editIndex] || null;

    const addAndFocus = () => {
      addProjectItem();
      setCurrentProjectIndex(projectItems.length); // the entry the push is about to create
    };

    const removeAndClamp = () => {
      removeProjectItem(editIndex);
      setCurrentProjectIndex(Math.max(0, Math.min(editIndex, lastIndex - 1)));
    };

    return (
      <div className="window-body context-settings-page settings-preview-page">
        {renderSettingsToolbar(
          'Projects Settings',
          'Edit `userProjects.json` directly from the same layout used by the Projects window.',
          projectsSaveMessage,
          resetProjects,
          saveProjects
        )}
        <div className="grid-view projects-edit-view">
          <div className="preview-edit-section-header">
            <h2>Projects</h2>
            <button type="button" className="profile-ghost-button" onClick={addAndFocus}>Add</button>
          </div>

          {!item ? (
            <p className="empty-state">No project cards available yet. Use Add to create one.</p>
          ) : (
            <>
              <div className="projects-edit-stage">
                <button
                  type="button"
                  className="projects-nav-button projects-nav-button-left"
                  onClick={() => setCurrentProjectIndex((prevIndex) => Math.max(0, prevIndex - 1))}
                  disabled={editIndex === 0}
                  aria-label="Edit previous project"
                >
                  {'<'}
                </button>

                <div className="grid-item preview-edit-grid-item projects-edit-card">
                  <div className="preview-edit-card-actions">
                    <button type="button" className="profile-ghost-button danger" onClick={removeAndClamp}>Remove</button>
                  </div>
                  <div className="grid-info">
                    {renderProjectImageSlider(item, { canManage: true })}
                    <input className="preview-edit-input" value={item.project_name || ''} onChange={(event) => updateProjectItem(editIndex, 'project_name', event.target.value)} placeholder="Project name" />
                    <div className="preview-edit-inline">
                      <input className="preview-edit-input" value={item.company || ''} onChange={(event) => updateProjectItem(editIndex, 'company', event.target.value)} placeholder="Company" />
                      <input className="preview-edit-input" value={item.department || ''} onChange={(event) => updateProjectItem(editIndex, 'department', event.target.value)} placeholder="Department" />
                    </div>
                    <div className="preview-edit-inline">
                      <input className="preview-edit-input" value={item.position_rank || ''} onChange={(event) => updateProjectItem(editIndex, 'position_rank', event.target.value)} placeholder="Position / rank" />
                      <input className="preview-edit-input" value={item.start_date || ''} onChange={(event) => updateProjectItem(editIndex, 'start_date', event.target.value)} placeholder="Start (2007.05)" />
                      <input className="preview-edit-input" value={item.end_date || ''} onChange={(event) => updateProjectItem(editIndex, 'end_date', event.target.value)} placeholder="End (2008.10)" />
                    </div>
                    <textarea className="preview-edit-textarea compact" value={item.description || ''} onChange={(event) => updateProjectItem(editIndex, 'description', event.target.value)} rows={4} placeholder="Description" />
                    <textarea className="preview-edit-textarea compact" value={item.role_and_responsibility || ''} onChange={(event) => updateProjectItem(editIndex, 'role_and_responsibility', event.target.value)} rows={3} placeholder="Role and responsibility" />
                    <textarea className="preview-edit-textarea compact" value={toMultilineText(item.outcomes)} onChange={(event) => updateProjectOutcomes(editIndex, event.target.value)} rows={3} placeholder="Outcomes, one per line" />
                  </div>
                </div>

                <button
                  type="button"
                  className="projects-nav-button projects-nav-button-right"
                  onClick={() => setCurrentProjectIndex((prevIndex) => Math.min(lastIndex, prevIndex + 1))}
                  disabled={editIndex === lastIndex}
                  aria-label="Edit next project"
                >
                  {'>'}
                </button>
              </div>

              <div className="projects-carousel-footer">
                <span className="projects-carousel-count">Total {projectItems.length} Projects</span>
                <span className="projects-carousel-position">{editIndex + 1} / {projectItems.length}</span>
              </div>
            </>
          )}
        </div>
      </div>
    );
  };

  const renderChangeOsWindow = () => (
    <div className="window-body ai-auth-page change-os-window">
      <div className="change-os-modal-backdrop">
        <div className="settings-modal change-os-modal-card" onClick={(event) => event.stopPropagation()}>
          <h4>{t(activeLanguage, 'change_os')}</h4>
          <div className="settings-language-header change-os-selector">
            <div className="settings-segmented-control" role="radiogroup" aria-label="Select OS shell">
              {['win', 'mac', 'lin'].map((mode) => (
                <button
                  key={mode}
                  type="button"
                  className={`settings-segmented-option ${resolvedOsMode === mode ? 'active' : ''}`.trim()}
                  onClick={() => applySettingChange('osMode', mode, 'OS mode saved successfully.')}
                  role="radio"
                  aria-checked={resolvedOsMode === mode}
                >
                  {t(activeLanguage, `os_${mode}`)}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // Keys, logins and model lists moved to ai-chat-hub; this window only reports readiness.
  const renderAiAuthCenter = () => {
    const hub = aiToolsStatus?.hub || null;
    const lanes = hub?.lanes || null;
    const laneStateLabel = { ok: 'Active', cooldown: 'Cooling down', down: 'Down', no_keys: 'No key', logged_out: 'Logged out', degraded: 'Degraded' };
    return (
      <div ref={aiAuthContentRef} className="window-body ai-auth-page ai-auth-center-page">
        <div className="profile-preview-banner">
          <div className="profile-preview-banner-copy">
            <h3>AI Chat Hub</h3>
            <p>Gemma (this server's CPU), NVIDIA, Codex, Claude and Antigravity keys, logins and model lists are managed in the AI Chat Hub. This window shows which chat targets are ready.</p>
          </div>
          <div className="profile-preview-banner-action ai-auth-banner-action">
            {hub?.adminUrl ? (
              <a className="profile-ghost-button settings-authout-button" href={hub.adminUrl} target="_blank" rel="noopener noreferrer">
                Open AI Chat Hub
              </a>
            ) : null}
            <button
              type="button"
              className="profile-ghost-button settings-authout-button"
              onClick={() => onRefreshAiToolsStatus?.({ silent: false, persist: true })}
            >
              Refresh
            </button>
            <button
              type="button"
              className="profile-ghost-button settings-authout-button profile-initialize-button"
              onClick={() => onAdminLogout?.({ closeWindowId: app.id })}
              disabled={!isAdminAuthenticated}
            >
              {t(activeLanguage, 'auth_out_action')}
            </button>
          </div>
        </div>

        {aiToolsError || hub?.error ? (
          <div className="ai-tool-feedback">
            <p className="upload-error">{aiToolsError || (hub.locked ? GUARD_ACCESS_KEY_REQUIRED : `AI Chat Hub unreachable: ${hub.error}`)}</p>
          </div>
        ) : null}

        {aiToolsLoading && !aiToolsStatus ? (
          <div className="loading-spinner">Loading AI Chat Hub status...</div>
        ) : (
          <div className="ai-tool-grid">
            {CHAT_TARGET_OPTIONS.map((target) => {
              const state = hub?.targets?.[target.id] || { available: false, reason: hub?.locked ? 'Access key required.' : 'No status yet.' };
              return (
                <div key={target.id} className="ai-tool-card">
                  <div className="ai-tool-card-header">
                    <strong>{target.label}</strong>
                    <span className={`ai-chat-composer-tag ${state.available ? 'ready' : 'inactive'}`.trim()}>{state.available ? 'Active' : 'InActive'}</span>
                  </div>
                  <p className="ai-tool-card-description">{state.available ? 'Ready for chat.' : (state.reason || 'Not available.')}</p>
                  {target.id === 'gemma' && lanes?.local ? (
                    <p className="ai-tool-card-description">{`${(lanes.local.servers || []).map((server) => `${server.model || 'llm-broker'} · ${laneStateLabel[server.state] || server.state}${server.busy ? ' · 생성 중' : ''}${server.queued ? ` · 대기 ${server.queued}` : ''}`).join(' / ') || 'llm-broker'} · CPU 2코어라 느림`}</p>
                  ) : null}
                  {target.id === 'nvidia' && lanes?.nvidia ? (
                    <ul className="ai-tool-card-description">
                      {(lanes.nvidia.keys || []).map((key) => (
                        <li key={key.id}>{`${key.id} · ${key.state} · ${key.used}/${key.limit} RPM`}</li>
                      ))}
                    </ul>
                  ) : null}
                  {(target.id === 'claude' || target.id === 'agy') && lanes?.ccacw ? (
                    <p className="ai-tool-card-description">{`ccacw ${laneStateLabel[lanes.ccacw.state] || lanes.ccacw.state} · running ${lanes.ccacw.running}/${lanes.ccacw.effectiveCap} · waiting ${lanes.ccacw.waiting}`}</p>
                  ) : null}
                  {target.id === 'codex' && lanes?.codex ? (
                    <p className="ai-tool-card-description">{`${lanes.codex.version || 'codex'} · ${lanes.codex.login || laneStateLabel[lanes.codex.state] || lanes.codex.state}`}</p>
                  ) : null}
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  const refreshAiChatModels = async () => {
    const target = getChatTargetDefinition(aiChatTarget);
    if (aiChatModelsLoading) {
      return;
    }
    try {
      setAiChatModelsLoading(true);
      const response = await fetch('/api/ai-chat/models/refresh', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ tool: target.tool })
      });
      const data = await response.json().catch(() => ({}));
      if (response.ok && Array.isArray(data.models)) {
        setAiChatAvailableModels(data.models);
        setAiChatModel((prev) => (data.models.some((m) => m.id === prev) ? prev : (data.models[0]?.id || 'auto')));
      }
    } catch (_) {
      /* non-fatal — keep the current list */
    } finally {
      setAiChatModelsLoading(false);
    }
  };

  const handleAiChatSubmit = async () => {
    const trimmedPrompt = String(aiChatPrompt || '').trim();
    const selectedChatTarget = getChatTargetDefinition(aiChatTarget);
    const selectedToolReady = isChatTargetAuthenticated(aiToolsStatus, aiChatTarget);
    const selectedToolLabel = selectedChatTarget.label;
    const attachmentInputText = aiChatAttachment
      ? [
          `Attached file: ${aiChatAttachment.fileName}`,
          aiChatAttachment.mimeType ? `MIME type: ${aiChatAttachment.mimeType}` : '',
          aiChatAttachment.previewText ? 'Preview text from the attachment is included below.' : 'Use the attached file from the workspace.',
          aiChatAttachment.truncated ? 'The preview text was truncated to fit the chat limit.' : '',
          '',
          aiChatAttachment.previewText
        ].filter(Boolean).join('\n')
      : '';

    if (!selectedToolReady) {
      showAiChatErrorToast(aiToolsStatus?.hub?.targets?.[selectedChatTarget.id]?.reason || `${selectedToolLabel} is not available right now (see AI Chat Hub status).`);
      return;
    }

    if (!trimmedPrompt && !attachmentInputText) {
      showAiChatErrorToast('Enter a message before sending.');
      return;
    }

    try {
      setAiChatSending(true);
      aiChatShouldAutoScrollRef.current = true;
      setIsAiChatPollingActive(false);
      updateAiChatStatusText(`${selectedToolLabel} is thinking...`);

      const response = await fetch('/api/ai-chat/terminal/sessions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          tool: selectedChatTarget.tool,
          model: aiChatModel,
          // One hub session per browser conversation: later messages carry the history.
          conversationId: activeAiChatHistoryId,
          messageText: trimmedPrompt,
          inputText: attachmentInputText,
          attachment: aiChatAttachment ? {
            fileName: aiChatAttachment.fileName,
            mimeType: aiChatAttachment.mimeType,
            contentBase64: aiChatAttachment.contentBase64
          } : null
        })
      });
      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(getApiErrorMessage(data, 'Failed to start AI chat session.'));
      }

      const session = data.session || {};
      setAiChatPrompt('');
      setAiChatAttachment(null);
      setAiChatMessages((prevMessages) => mergeChatMessages(prevMessages, session.messages || []));
      updateAiChatStatusText(session.statusText || `${selectedToolLabel} is thinking...`);
      setIsAiChatPollingActive(Boolean(!session.finished && session.id));
      setAiChatSessionId(session.finished ? '' : (session.id || ''));
      setAiChatSending(false);
    } catch (error) {
      showAiChatErrorToast(error.message || 'Failed to start AI chat session.');
      updateAiChatStatusText('Failed to start AI chat session.');
      setIsAiChatPollingActive(false);
      setAiChatSending(false);
      setAiChatSessionId('');
    }
  };

  const clearAiChatConversation = () => {
    setAiChatSessionId('');
    setAiChatSending(false);
    setIsAiChatPollingActive(false);
    setAiChatAttachment(null);
    setAiChatPrompt('');
    setAiChatMessages([]);
    setActiveAiChatHistoryId(createAiChatHistoryId());
    aiChatShouldAutoScrollRef.current = true;
    updateAiChatStatusText('Ready for a new chat.');
  };

  const openAiChatHistorySession = (historySession) => {
    if (!historySession || aiChatSending || aiChatSessionId) {
      return;
    }

    setActiveAiChatHistoryId(historySession.id);
    setAiChatTarget(normalizeChatTargetId(historySession.target));
    setAiChatModel(historySession.model || 'auto');
    setAiChatPrompt('');
    setAiChatAttachment(null);
    setAiChatMessages(Array.isArray(historySession.messages) ? historySession.messages : []);
    setAiChatSessionId('');
    setAiChatSending(false);
    setIsAiChatPollingActive(false);
    aiChatShouldAutoScrollRef.current = true;
    updateAiChatStatusText(historySession.statusText || 'Ready for a new chat.');
  };

  const deleteAiChatHistorySession = (historySessionId) => {
    setAiChatHistorySessions((prevSessions) => prevSessions.filter((session) => session.id !== historySessionId));

    if (historySessionId === activeAiChatHistoryId) {
      clearAiChatConversation();
    }
  };

  const renderAiChatWindow = () => {
    const selectedChatTarget = getChatTargetDefinition(aiChatTarget);
    const selectedToolReady = isChatTargetAuthenticated(aiToolsStatus, aiChatTarget);
    const selectedToolReason = aiToolsStatus?.hub?.targets?.[selectedChatTarget.id]?.reason || '';
    const selectedToolLabel = selectedChatTarget.label;
    const selectedModelLabel = aiChatAvailableModels.find((option) => option.id === aiChatModel)?.label || aiChatModel || 'Default model';
    const canSendAiChatMessage = selectedToolReady && !aiChatSending && !aiChatSessionId && (Boolean(aiChatAttachment) || Boolean(String(aiChatPrompt || '').trim()));
    const isHistoryLocked = aiChatSending || Boolean(aiChatSessionId);

    return (
      <div className="window-body ai-chat-panel">
        <div className="ai-chat-header">
          <div className="ai-chat-session-meta ai-chat-session-meta-header">
            <span className="ai-chat-session-meta-label">Session</span>
            <span className="ai-chat-session-meta-value">{aiChatStatusText}</span>
          </div>
          <div className="ai-chat-header-controls">
            <button
              type="button"
              className={`ai-chat-header-action ${isAiChatHistoryOpen ? 'active' : ''}`.trim()}
              onClick={() => setIsAiChatHistoryOpen((isOpen) => !isOpen)}
              aria-expanded={isAiChatHistoryOpen}
            >
              Sessions
            </button>
            <button
              type="button"
              className="ai-chat-header-action ai-chat-new-chat-action"
              onClick={clearAiChatConversation}
              disabled={aiChatSending || (!aiChatMessages.length && !aiChatPrompt.trim() && !aiChatAttachment)}
            >
              New Chat
            </button>
              <label className="ai-chat-tool-select-wrap ai-chat-model-select-wrap-header">
                <select
                  className={`ai-chat-tool-select ai-chat-model-select ${selectedToolReady ? 'tool-select-authenticated' : 'tool-select-inactive'}`.trim()}
                  value={aiChatModel}
                  onChange={(event) => setAiChatModel(event.target.value)}
                  onFocus={() => onFocus(app.id)}
                  disabled={aiChatSending || Boolean(aiChatSessionId) || aiChatModelsLoading || aiChatAvailableModels.length === 0}
                  title="Choose a model for the selected provider"
                >
                  {aiChatAvailableModels.length === 0 ? (
                    <option value={aiChatModel}>{aiChatModelsLoading ? 'Loading models…' : 'Default model'}</option>
                  ) : (
                    aiChatAvailableModels.map((option) => (
                      <option key={option.id} value={option.id} style={chatToolOptionStyle(selectedToolReady)}>
                        {option.label}{option.source === 'live' ? ' •' : ''}
                      </option>
                    ))
                  )}
                </select>
                <button
                  type="button"
                  className="ai-chat-header-action ai-chat-model-refresh"
                  onClick={refreshAiChatModels}
                  disabled={aiChatSending || Boolean(aiChatSessionId) || aiChatModelsLoading}
                  title="Reload the model list from the AI Chat Hub"
                  aria-label="Refresh models"
                >
                  {aiChatModelsLoading ? '…' : '↻'}
                </button>
              </label>
            <label className="ai-chat-tool-select-wrap ai-chat-tool-select-wrap-header">
              <select
                className={`ai-chat-tool-select ${selectedToolReady ? 'tool-select-authenticated' : 'tool-select-inactive'}`.trim()}
                value={aiChatTarget}
                onChange={(event) => setAiChatTarget(event.target.value)}
                onFocus={() => onFocus(app.id)}
              >
                {CHAT_TARGET_OPTIONS.map((target) => {
                  const authenticated = isChatTargetAuthenticated(aiToolsStatus, target.id);
                  return (
                    <option key={target.id} value={target.id} style={chatToolOptionStyle(authenticated)}>
                      {authenticated ? target.label : `${target.label} (unavailable)`}
                    </option>
                  );
                })}
              </select>
            </label>
          </div>
        </div>

        <div className={`ai-chat-main ${isAiChatHistoryOpen ? 'sessions-open' : 'sessions-closed'}`.trim()}>
          {isAiChatHistoryOpen ? (
            <aside className="ai-chat-history-panel" aria-label="Chat sessions">
              <div className="ai-chat-history-header">
                <strong>Chats</strong>
                <button
                  type="button"
                  className="ai-chat-history-close"
                  onClick={() => setIsAiChatHistoryOpen(false)}
                  aria-label="Close sessions"
                >
                  x
                </button>
              </div>
              <div className="ai-chat-history-list">
                {aiChatHistorySessions.length > 0 ? (
                  aiChatHistorySessions.map((historySession) => {
                    const isActiveHistorySession = historySession.id === activeAiChatHistoryId;
                    return (
                      <div key={historySession.id} className={`ai-chat-history-item ${isActiveHistorySession ? 'active' : ''}`.trim()}>
                        <button
                          type="button"
                          className="ai-chat-history-session-button"
                          onClick={() => openAiChatHistorySession(historySession)}
                          disabled={isHistoryLocked}
                        >
                          <span className="ai-chat-history-title">{historySession.title}</span>
                          <span className="ai-chat-history-preview">{historySession.preview}</span>
                          <span className="ai-chat-history-time">{formatAiChatHistoryTimestamp(historySession.updatedAt)}</span>
                        </button>
                        <button
                          type="button"
                          className="ai-chat-history-delete"
                          onClick={() => deleteAiChatHistorySession(historySession.id)}
                          disabled={isHistoryLocked}
                          aria-label={`Delete ${historySession.title}`}
                        >
                          x
                        </button>
                      </div>
                    );
                  })
                ) : (
                  <div className="ai-chat-history-empty">No chats yet.</div>
                )}
              </div>
            </aside>
          ) : null}
          <div className="ai-chat-conversation">
            <div ref={aiChatFeedRef} className="ai-chat-feed" aria-live="polite" onScroll={updateAiChatAutoScrollState}>
              {aiChatMessages.length === 0 && aiToolsStatus?.hub?.locked ? (
                <div className="ai-chat-empty ai-chat-locked" role="status">
                  <div className="ai-chat-empty-eyebrow">Access key required · 접근키 필요</div>
                  <strong>AI Chat is locked on this browser.</strong>
                  <p>Like Profile, Resume and Projects, AI Chat only works after this site is opened with your access key link. Open the link once and this browser keeps the key.</p>
                  <p>Profile · Resume · Projects 와 마찬가지로 AI Chat 은 접근키 링크로 이 사이트를 연 브라우저에서만 쓸 수 있습니다. 한 번 열면 이 브라우저에 키가 저장됩니다.</p>
                </div>
              ) : aiChatMessages.length === 0 ? (
                <div className="ai-chat-empty">
                  <div className="ai-chat-empty-eyebrow">Portfolio AI Workspace</div>
                  <strong>Build portfolio content only.</strong>
                  <p>Use this chat to generate or refine {PROJECT_AI_CHAT_SUPPORTED_SCOPE_LABEL} content from your prompts and uploaded source files.</p>
                </div>
              ) : (
                aiChatMessages.map((message) => (
                  <AiChatMessageBubble key={message.id} message={message} onAttachmentLoad={handleAiChatAttachmentLoad} />
                ))
              )}
            </div>

            <div className="ai-chat-composer-shell">
              {aiToolsStatus?.hub?.locked && aiChatMessages.length > 0 ? (
                <p className="ai-chat-locked-note" role="status">Access key required · 접근키가 필요합니다 — open this site with your access key link to keep chatting.</p>
              ) : null}
              <div className="ai-chat-composer-card">
                <div className="ai-chat-composer-tags">
                  <span className="ai-chat-composer-tag">Portfolio Scope</span>
                  <span className={`ai-chat-composer-tag ${selectedToolReady ? 'ready' : 'inactive'}`.trim()}>{selectedToolLabel}</span>
                  <span className={`ai-chat-composer-tag ${selectedModelLabel ? 'ready' : 'neutral'}`.trim()}>{selectedModelLabel}</span>
                  <span className={`ai-chat-composer-tag ${selectedToolReady ? 'ready' : 'inactive'}`.trim()} title={selectedToolReason}>{selectedToolReady ? 'Active' : 'InActive'}</span>
                  {aiChatAttachment ? (
                    <span className="ai-chat-composer-tag neutral">
                      {aiChatAttachment.fileName}{aiChatAttachment.truncated ? ' (truncated)' : ''}
                    </span>
                  ) : null}
                </div>

                <textarea
                  rows={4}
                  className="ai-chat-composer-input"
                  placeholder="Ask for Profile, Resume, or Projects generation"
                  value={aiChatPrompt}
                  onChange={(event) => setAiChatPrompt(event.target.value)}
                  onFocus={() => onFocus(app.id)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' && !event.shiftKey) {
                      event.preventDefault();
                      handleAiChatSubmit();
                    }
                  }}
                />

                <div className="ai-chat-composer-toolbar">
                  <div className="ai-chat-composer-toolbar-left">
                    <button
                      type="button"
                      className="ai-chat-composer-icon-button"
                      onClick={openAiChatAttachmentPicker}
                      disabled={!selectedToolReady || aiChatSending || Boolean(aiChatSessionId)}
                      title="Upload file"
                      aria-label="Upload file"
                    >
                      +
                    </button>
                  </div>

                  <div className="ai-chat-composer-toolbar-right">
                    <button
                      type="button"
                      className="ai-chat-composer-send-button"
                      onClick={handleAiChatSubmit}
                      disabled={!canSendAiChatMessage}
                      aria-label="Send message"
                      title="Send message"
                    >
                      <span className="ai-chat-composer-send-arrow">^</span>
                    </button>
                  </div>
                </div>
                <input
                  ref={aiChatFileInputRef}
                  type="file"
                  accept=".txt,.md,.markdown,.json,.csv,.yaml,.yml,.html,.htm,.png,.jpg,.jpeg,.webp,.gif,.bmp,.svg,.pdf,.ppt,.pptx,.xls,.xlsx,.doc,.docx,.hwp,.hwpx"
                  className="settings-hidden-file-input"
                  onChange={handleAiChatAttachmentFile}
                  disabled={!selectedToolReady || aiChatSending || Boolean(aiChatSessionId)}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderProfile = () => {
    const profileData = config.profileData || {};
    const basicInfo = profileData.basic_info || {};
    const experienceSummary = profileData.experience_summary || {};
    const skills = profileData.skills || {};
    const softwareSkills = Array.isArray(skills.software_skills) ? skills.software_skills.filter((item) => item?.name || item?.proficiency) : [];
    const languages = Array.isArray(profileData.languages) ? profileData.languages.filter((item) => item?.language || item?.proficiency) : [];
    const certificates = Array.isArray(profileData.certificates) ? profileData.certificates.filter((item) => item?.name) : [];
    const activities = Array.isArray(profileData.activities) ? profileData.activities.filter((item) => item?.name || item?.description) : [];
    const awards = Array.isArray(profileData.awards) ? profileData.awards.filter((item) => item?.award_name) : [];
    const selfIntroduction = profileData.self_introduction || {};

    return (
      <div className="window-body profile-preview-page">
        <div className="profile-preview-banner">
          <div className="profile-preview-banner-copy">
            <h3>Profile</h3>
            <p>Edit this content in Settings &gt; Profile Settings, or use the File menu after authenticating in Auth Center to rebuild all profile documents from an uploaded source file.</p>
          </div>
          {!isPublicShareView ? (
            <div className="profile-preview-banner-action">
              <button
                type="button"
                className="profile-ghost-button settings-authout-button profile-initialize-button"
                onClick={() => requestPortfolioInitialize('profile')}
                disabled={resettingPortfolioTarget === 'profile'}
              >
                {resettingPortfolioTarget === 'profile' ? '...' : initializeActionLabel}
              </button>
            </div>
          ) : null}
        </div>
        <div className="resume-preview-shell">
          <div className="resume-preview">
            <div className="resume-preview-header">
              <div className="resume-preview-title">Profile</div>
              <div className="profile-hero">
                <div className="profile-photo-panel profile-photo-panel-display">
                  {basicInfo.photo ? (
                    <img className="profile-photo-preview" src={basicInfo.photo} alt="Profile" />
                  ) : (
                    <div className="profile-photo-placeholder">Photo</div>
                  )}
                </div>
                <div className="profile-hero-copy">
                  <h1>{basicInfo.name_en || basicInfo.name_kr || 'Your Name'}</h1>
                  <div className="resume-preview-subtitle">{basicInfo.headline || basicInfo.target_position || 'Your Headline'}</div>
                </div>
              </div>
              <div className="resume-contact-row">
                <span>{basicInfo.address || 'Location'}</span>
                <span>{basicInfo.email || 'email@example.com'}</span>
                <span>{basicInfo.homepage || basicInfo.links?.find?.((item) => item?.url)?.url || 'website.com'}</span>
              </div>
            </div>

            <div className="resume-preview-body">
              <div className="resume-main-column">
                <section className="resume-section">
                  <h2>Summary</h2>
                  <p>{basicInfo.summary || selfIntroduction.free_text || 'Write your profile summary in the editor.'}</p>
                </section>

                <section className="resume-section">
                  <h2>Activities</h2>
                  {activities.length === 0 ? (
                    <p className="resume-empty-text">Add activity details.</p>
                  ) : (
                    activities.map((item, index) => (
                      <article key={`profile-activity-${index}`} className="resume-entry">
                        <div className="resume-entry-heading">
                          <div>
                            <strong>{item.name || 'Activity'}</strong>
                            {item.activity_type ? <div className="resume-entry-role">{item.activity_type}</div> : null}
                          </div>
                          <div className="resume-entry-meta">
                            <div>{[item.start_date, item.end_date].filter(Boolean).join(' - ')}</div>
                          </div>
                        </div>
                        {item.institution ? <p>{item.institution}</p> : null}
                        {item.description ? <p>{item.description}</p> : null}
                      </article>
                    ))
                  )}
                </section>

                <section className="resume-section">
                  <h2>Self Introduction</h2>
                  {Array.isArray(selfIntroduction.sections) && selfIntroduction.sections.some((item) => item?.title || item?.content) ? (
                    selfIntroduction.sections.map((section, index) => (
                      <article key={`self-introduction-${index}`} className="resume-side-entry">
                        <strong>{section.title || `Section ${index + 1}`}</strong>
                        {section.content ? <p>{section.content}</p> : null}
                      </article>
                    ))
                  ) : (
                    <p className="resume-empty-text">Add self introduction sections.</p>
                  )}
                  {selfIntroduction.cover_letter ? <p>{selfIntroduction.cover_letter}</p> : null}
                </section>
              </div>

              <aside className="resume-side-column">
                <section className="resume-section">
                  <h2>Experience Summary</h2>
                  <p>{experienceSummary.total_experience || 'Add your total experience summary.'}</p>
                  <p>{`${experienceSummary.total_experience_years || 0} years ${experienceSummary.total_experience_months || 0} months`}</p>
                </section>

                <section className="resume-section">
                  <h2>Skills</h2>
                  {softwareSkills.length === 0 ? (
                    <p className="resume-empty-text">Add software skills.</p>
                  ) : (
                    softwareSkills.map((item, index) => (
                      <div key={`profile-skill-${index}`} className="resume-skill-row">
                        <span>{item.name || 'Skill'}</span>
                        <span>{item.proficiency || 'Level'}</span>
                      </div>
                    ))
                  )}
                </section>

                <section className="resume-section">
                  <h2>Languages</h2>
                  {languages.length === 0 ? (
                    <p className="resume-empty-text">Add language details.</p>
                  ) : (
                    languages.map((item, index) => (
                      <article key={`profile-language-${index}`} className="resume-side-entry">
                        <strong>{item.language || 'Language'}</strong>
                        {(item.proficiency || item.score) ? <div className="resume-side-period">{[item.proficiency, item.score].filter(Boolean).join(' / ')}</div> : null}
                        {item.test_name ? <p>{item.test_name}</p> : null}
                      </article>
                    ))
                  )}
                </section>

                <section className="resume-section">
                  <h2>Certificates</h2>
                  {certificates.length === 0 ? (
                    <p className="resume-empty-text">Add certificate details.</p>
                  ) : (
                    certificates.map((item, index) => (
                      <article key={`profile-certificate-${index}`} className="resume-side-entry">
                        <strong>{item.name || 'Certificate'}</strong>
                        {(item.issuer || item.acquired_date) ? <div className="resume-side-period">{[item.issuer, item.acquired_date].filter(Boolean).join(' / ')}</div> : null}
                        {item.note ? <p>{item.note}</p> : null}
                      </article>
                    ))
                  )}
                </section>

                <section className="resume-section">
                  <h2>Awards</h2>
                  {awards.length === 0 ? (
                    <p className="resume-empty-text">Add award details.</p>
                  ) : (
                    awards.map((item, index) => (
                      <article key={`profile-award-${index}`} className="resume-side-entry">
                        <strong>{item.award_name || 'Award'}</strong>
                        {(item.organization || item.award_date) ? <div className="resume-side-period">{[item.organization, item.award_date].filter(Boolean).join(' / ')}</div> : null}
                        {item.description ? <p>{item.description}</p> : null}
                      </article>
                    ))
                  )}
                </section>
              </aside>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderResume = () => (
    <div className="window-body context-settings-page">
      <div className="profile-preview-banner">
        <div className="profile-preview-banner-copy">
          <h3>Resume</h3>
          <p>Use the File menu after authenticating in Auth Center to rebuild `userResume.json`, or use Edit to modify its schema-aligned content.</p>
        </div>
        {!isPublicShareView ? (
          <div className="profile-preview-banner-action">
            <button
              type="button"
              className="profile-ghost-button settings-authout-button profile-initialize-button"
              onClick={() => requestPortfolioInitialize('resume')}
              disabled={resettingPortfolioTarget === 'resume'}
            >
              {resettingPortfolioTarget === 'resume' ? '...' : initializeActionLabel}
            </button>
          </div>
        ) : null}
      </div>
      <div className="resume-preview-shell">
        <div className="resume-preview">
          <div className="resume-preview-body">
            <div className="resume-main-column">
              <section className="resume-section">
                <h2>Work Experiences</h2>
                {(config.resumeData?.work_experiences || []).filter((item) => item?.company || item?.job_title || item?.department).length === 0 ? (
                  <p className="resume-empty-text">Add work experience items in Resume Settings.</p>
                ) : (
                  (config.resumeData?.work_experiences || []).filter((item) => item?.company || item?.job_title || item?.department).map((item, index) => (
                    <article key={`resume-work-${index}`} className="resume-entry">
                      <div className="resume-entry-heading">
                        <div>
                          <strong>{item.company || 'Company'}</strong>
                          <div className="resume-entry-role">{item.job_title || item.position_rank || 'Role'}</div>
                          {item.department ? <div className="resume-entry-team">{item.department}</div> : null}
                        </div>
                        <div className="resume-entry-meta">
                          <div>{[item.start_date, item.end_date].filter(Boolean).join(' - ') || (item.is_current ? 'Current' : '')}</div>
                          {item.duration ? <div>{item.duration}</div> : null}
                        </div>
                      </div>
                      {Array.isArray(item.main_tasks) && item.main_tasks.filter(Boolean).length > 0 ? (
                        <ul>
                          {item.main_tasks.filter(Boolean).map((task, taskIndex) => (
                            <li key={`resume-task-${index}-${taskIndex}`}>{task}</li>
                          ))}
                        </ul>
                      ) : null}
                      {Array.isArray(item.achievements) && item.achievements.filter(Boolean).length > 0 ? (
                        <ul>
                          {item.achievements.filter(Boolean).map((achievement, achievementIndex) => (
                            <li key={`resume-achievement-${index}-${achievementIndex}`}>{achievement}</li>
                          ))}
                        </ul>
                      ) : null}
                    </article>
                  ))
                )}
              </section>

              <section className="resume-section">
                <h2>Education</h2>
                {(config.resumeData?.education || []).filter((item) => item?.school_name || item?.major || item?.degree).length === 0 ? (
                  <p className="resume-empty-text">Add education items in Resume Settings.</p>
                ) : (
                  (config.resumeData?.education || []).filter((item) => item?.school_name || item?.major || item?.degree).map((item, index) => (
                    <article key={`resume-education-${index}`} className="resume-side-entry">
                      <strong>{item.school_name || 'School'}</strong>
                      <div className="resume-side-period">{[item.degree, item.major].filter(Boolean).join(' / ')}</div>
                      <div className="resume-side-period">{[item.start_date, item.end_date].filter(Boolean).join(' - ')}</div>
                      {item.status ? <p>{item.status}</p> : null}
                    </article>
                  ))
                )}
              </section>
            </div>

            <aside className="resume-side-column">
              <section className="resume-section">
                <h2>Training</h2>
                {(config.resumeData?.training || []).filter((item) => item?.course_name || item?.institution).length === 0 ? (
                  <p className="resume-empty-text">Add training items in Resume Settings.</p>
                ) : (
                  (config.resumeData?.training || []).filter((item) => item?.course_name || item?.institution).map((item, index) => (
                    <article key={`resume-training-${index}`} className="resume-side-entry">
                      <strong>{item.course_name || 'Course'}</strong>
                      {(item.institution || item.completion_date) ? <div className="resume-side-period">{[item.institution, item.completion_date].filter(Boolean).join(' / ')}</div> : null}
                      {item.description ? <p>{item.description}</p> : null}
                    </article>
                  ))
                )}
              </section>

              <section className="resume-section">
                <h2>Military Service</h2>
                <p>{config.resumeData?.military_service?.served == null ? 'Not specified' : (config.resumeData?.military_service?.served ? 'Served' : 'Not served')}</p>
                {[config.resumeData?.military_service?.branch, config.resumeData?.military_service?.rank, config.resumeData?.military_service?.specialty].filter(Boolean).length > 0 ? (
                  <p>{[config.resumeData?.military_service?.branch, config.resumeData?.military_service?.rank, config.resumeData?.military_service?.specialty].filter(Boolean).join(' / ')}</p>
                ) : null}
                {[config.resumeData?.military_service?.start_date, config.resumeData?.military_service?.end_date].filter(Boolean).length > 0 ? (
                  <p>{[config.resumeData?.military_service?.start_date, config.resumeData?.military_service?.end_date].filter(Boolean).join(' - ')}</p>
                ) : null}
                {config.resumeData?.military_service?.exemption_reason ? <p>{config.resumeData.military_service.exemption_reason}</p> : null}
              </section>

              <section className="resume-section">
                <h2>Declaration</h2>
                <p>{config.resumeData?.declaration?.statement || 'Add declaration content in Resume Settings.'}</p>
                {[config.resumeData?.declaration?.date, config.resumeData?.declaration?.name].filter(Boolean).length > 0 ? (
                  <p>{[config.resumeData?.declaration?.date, config.resumeData?.declaration?.name].filter(Boolean).join(' / ')}</p>
                ) : null}
              </section>
            </aside>
          </div>
        </div>
      </div>
    </div>
  );

  const renderProjects = () => {
    const projectItems = Array.isArray(config.projectsData?.projects) ? config.projectsData.projects : [];
    const currentProject = projectItems[currentProjectIndex] || null;
    const previousProject = currentProjectIndex > 0 ? projectItems[currentProjectIndex - 1] : null;
    const nextProject = currentProjectIndex < projectItems.length - 1 ? projectItems[currentProjectIndex + 1] : null;

    const renderProjectMetaPill = (value, className = '') => {
      if (!String(value || '').trim()) {
        return null;
      }

      return (
        <span className={`projects-meta-pill ${className}`.trim()}>
          {value}
        </span>
      );
    };

    const renderProjectSideCard = (item, direction) => {
      if (!item) {
        return <div className={`projects-side-card projects-side-card-${direction} projects-side-card-empty`.trim()} aria-hidden="true"></div>;
      }

      // The first image stands in for the project. The frame is drawn either way so the two
      // side cards keep the same shape whether or not their project has pictures yet.
      const sideImage = getProjectImages(item.id)[0] || null;

      return (
        <div className={`projects-side-card projects-side-card-${direction}`.trim()} aria-hidden="true">
          <div className="projects-side-card-thumb">
            {sideImage ? <img src={sideImage.url} alt="" loading="lazy" /> : null}
          </div>
          {item.company || item.department ? (
            <div className="projects-side-card-kicker">{[item.company, item.department].filter(Boolean).join(' / ')}</div>
          ) : null}
          <h4>{item.project_name || 'Project'}</h4>
          {[item.start_date, item.end_date].filter(Boolean).length > 0 ? (
            <p>{[item.start_date, item.end_date].filter(Boolean).join(' ~ ')}</p>
          ) : null}
        </div>
      );
    };

    return (
      <div className="window-body context-settings-page">
        <div className="profile-preview-banner">
          <div className="profile-preview-banner-copy">
            <h3>Projects</h3>
            <p>Use the File menu after authenticating in Auth Center to rebuild `userProjects.json`, or use Edit to modify its schema-aligned content.</p>
          </div>
          {!isPublicShareView ? (
            <div className="profile-preview-banner-action">
              <button
                type="button"
                className="profile-ghost-button settings-authout-button profile-initialize-button"
                onClick={() => requestPortfolioInitialize('projects')}
                disabled={resettingPortfolioTarget === 'projects'}
              >
                {resettingPortfolioTarget === 'projects' ? '...' : initializeActionLabel}
              </button>
            </div>
          ) : null}
        </div>
        <div className="projects-carousel-shell">
          {projectItems.length === 0 || !currentProject ? (
            <p className="empty-state">No project cards available yet.</p>
          ) : (
            <>
              <div className="projects-carousel-stage">
                {renderProjectSideCard(previousProject, 'left')}

                <div className="projects-card-frame">
                  <button
                    type="button"
                    className="projects-nav-button projects-nav-button-left"
                    onClick={() => setCurrentProjectIndex((prevIndex) => Math.max(0, prevIndex - 1))}
                    disabled={currentProjectIndex === 0}
                    aria-label="View previous project"
                  >
                    {'<'}
                  </button>

                  <article className="projects-feature-card">
                    <div className="projects-feature-card-top">
                      {currentProject.company || currentProject.department ? (
                        <div className="projects-kicker">{[currentProject.company, currentProject.department].filter(Boolean).join(' / ')}</div>
                      ) : null}
                      <h2>{currentProject.project_name || 'Project'}</h2>
                      <div className="projects-meta-row">
                        {renderProjectMetaPill([currentProject.start_date, currentProject.end_date].filter(Boolean).join(' ~ '), 'projects-meta-pill-date')}
                        {renderProjectMetaPill(currentProject.position_rank, 'projects-meta-pill-rank')}
                      </div>
                    </div>

                    {renderProjectImageSlider(currentProject)}

                    <div className="projects-section-block">
                      <h3>Description</h3>
                      <p>{currentProject.description || 'Add project descriptions in Projects Settings.'}</p>
                    </div>

                    <div className="projects-section-block">
                      <h3>Role & Responsibility</h3>
                      <p>{currentProject.role_and_responsibility || 'Add your role and responsibilities for this project.'}</p>
                    </div>

                    {(currentProject.outcomes || []).filter(Boolean).length > 0 ? (
                      <div className="projects-section-block">
                        <h3>Outcomes</h3>
                        <div className="projects-outcome-list">
                          {(currentProject.outcomes || []).filter(Boolean).map((outcome) => (
                            <span key={`${currentProject.project_name || 'project'}-${outcome}`} className="projects-outcome-chip">
                              {outcome}
                            </span>
                          ))}
                        </div>
                      </div>
                    ) : null}
                  </article>

                  <button
                    type="button"
                    className="projects-nav-button projects-nav-button-right"
                    onClick={() => setCurrentProjectIndex((prevIndex) => Math.min(projectItems.length - 1, prevIndex + 1))}
                    disabled={currentProjectIndex === projectItems.length - 1}
                    aria-label="View next project"
                  >
                    {'>'}
                  </button>
                </div>

                {renderProjectSideCard(nextProject, 'right')}
              </div>

              <div className="projects-carousel-footer">
                <span className="projects-carousel-count">Total {projectItems.length} Projects</span>
                <span className="projects-carousel-position">
                  {currentProjectIndex + 1} / {projectItems.length}
                </span>
              </div>
            </>
          )}
        </div>

      </div>
    );
  };
  const renderPortfolioImportModal = () => {
    if (!isImportModalOpen || !['profile', 'resume', 'projects'].includes(app.id)) {
      return null;
    }

    const hasAuthenticatedImportTool = authenticatedImportTargets.length > 0;
    const selectedImportTargetDefinition = getChatTargetDefinition(selectedImportTarget);
    const selectedImportTargetReady = isChatTargetAuthenticated(aiToolsStatus, selectedImportTarget);
    const isUploadDisabled = authenticatedImportTargets.length === 0
      || importingPortfolioFile
      || !selectedImportTargetReady;

    return (
      <div className="settings-modal-backdrop" onClick={() => !importingPortfolioFile && setIsImportModalOpen(false)}>
        <div
          className="settings-modal portfolio-import-modal"
          onClick={(event) => event.stopPropagation()}
        >
          <h4>{localizedWindowTitle} Import</h4>
          <p className="upload-help">
            Upload a {PORTFOLIO_IMPORT_SUPPORTED_FILE_LABEL} source file. The authenticated CLI session will analyze it and rebuild only the current {localizedWindowTitle} document using the default {PORTFOLIO_IMPORT_TARGET_LABELS[app.id] || localizedWindowTitle} structure.
          </p>
          <div className="portfolio-import-session-row">
            {authenticatedImportTargets.length === 0 ? (
              <p className="upload-error">Codex is not available in the AI Chat Hub right now (see AI Auth window).</p>
            ) : null}
            {authenticatedImportTargets.length === 1 ? (
              <div className="portfolio-import-selected-tool">
                Using {selectedImportTargetDefinition.label}
              </div>
            ) : null}
            <label className="ai-chat-tool-select-wrap portfolio-import-target-select-wrap">
              <select
                className={`ai-chat-tool-select ${selectedImportTargetReady ? 'tool-select-authenticated' : 'tool-select-inactive'}`.trim()}
                value={selectedImportTarget}
                onChange={(event) => setSelectedImportTarget(event.target.value)}
                onFocus={() => onFocus(app.id)}
              >
                {CHAT_TARGET_OPTIONS.filter((target) => target.importable).map((target) => {
                  const authenticated = isChatTargetAuthenticated(aiToolsStatus, target.id);
                  return (
                    <option key={target.id} value={target.id} style={chatToolOptionStyle(authenticated)}>
                      {authenticated ? target.label : `${target.label} (unavailable)`}
                    </option>
                  );
                })}
              </select>
            </label>
          </div>
          <div className="settings-modal-actions">
            <button
              type="button"
              className="profile-ghost-button"
              onClick={() => setIsImportModalOpen(false)}
              disabled={importingPortfolioFile}
            >
              Cancel
            </button>
            {!hasAuthenticatedImportTool ? (
              <button
                type="button"
                className="profile-primary-button"
                onClick={() => {
                  setIsImportModalOpen(false);
                  onOpenApp('ai-auth');
                }}
                disabled={importingPortfolioFile}
              >
                Open Auth Center
              </button>
            ) : null}
            <button
              type="button"
              className="profile-primary-button"
              onClick={openPortfolioImportPicker}
              disabled={isUploadDisabled}
            >
              {importingPortfolioFile ? 'Importing...' : 'Upload Source File'}
            </button>
          </div>
          <input
            ref={importFileInputRef}
            type="file"
            className="settings-hidden-file-input"
            accept={PORTFOLIO_IMPORT_UPLOAD_ACCEPT}
            onChange={handlePortfolioImportFile}
            disabled={importingPortfolioFile}
          />
          {portfolioImportError ? <p className="upload-error settings-upload-feedback">{portfolioImportError}</p> : null}
        </div>
      </div>
    );
  };

  const renderPortfolioImportSuggestionsModal = () => {
    if (!portfolioImportSuggestions.length || !['profile', 'resume', 'projects'].includes(app.id)) {
      return null;
    }

    return (
      <div className="settings-modal-backdrop" onClick={dismissPortfolioImportSuggestions}>
        <div
          className="settings-modal portfolio-import-modal"
          onClick={(event) => event.stopPropagation()}
        >
          <h4>Continue Import?</h4>
          <p className="upload-help">
            The uploaded source file appears to contain enough information to continue other portfolio sections. Choose where to continue next.
          </p>
          <div className="portfolio-import-suggestion-list">
            {portfolioImportSuggestions.map((suggestion) => (
              <div key={suggestion.target} className="portfolio-import-suggestion-card">
                <div>
                  <strong>{PORTFOLIO_IMPORT_TARGET_LABELS[suggestion.target] || suggestion.target}</strong>
                  <p>{suggestion.reason || 'Additional structured content was detected for this section.'}</p>
                </div>
                <button
                  type="button"
                  className="profile-primary-button"
                  onClick={() => applyPortfolioImportSuggestion(suggestion)}
                >
                  Continue with {PORTFOLIO_IMPORT_TARGET_LABELS[suggestion.target] || suggestion.target}
                </button>
              </div>
            ))}
          </div>
          <div className="settings-modal-actions">
            <button
              type="button"
              className="profile-ghost-button"
              onClick={dismissPortfolioImportSuggestions}
            >
              Later
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderContent = () => {
    if (app.id === 'resume') {
      return renderResume();
    }

    if (app.id === 'projects') {
      return renderProjects();
    }

    if (app.viewType === 'grid' && app.items) {
      return (
        <div className="grid-view">
          {app.items.map((item, index) => (
            <div key={index} className="grid-item" onClick={() => item.link && window.open(item.link, '_blank')}>
              <div className="grid-icon">+</div>
              <div className="grid-info">
                <h4>{item.title}</h4>
                <p>{item.desc}</p>
                <div className="tags">
                  {item.tags.map((tag) => (
                    <span key={tag} className="tag">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      );
    }

    if (app.contentSource && app.contentSource.endsWith('.md')) {
      if (loading) {
        return <div className="loading-spinner">Loading...</div>;
      }

      return (
        <div className="markdown-body">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{content}</ReactMarkdown>
        </div>
      );
    }

    if (app.id === 'ai-auth') {
      return renderAiAuthCenter();
    }

    if (app.id === 'change-os') {
      return renderChangeOsWindow();
    }

    if (app.id === 'ai-chat') {
      return renderAiChatWindow();
    }

    if (app.id === 'profile') {
      return renderProfile();
    }

    if (app.id === 'profile-settings') {
      return renderProfileSettings();
    }

    if (app.id === 'resume-settings') {
      return renderResumeSettings();
    }

    if (app.id === 'projects-settings') {
      return renderProjectsSettings();
    }

    if (app.id === 'settings') {
      return renderSettings();
    }

    return (
      <div className="window-body">
        <p>
          No content available for <strong>{app.label}</strong>.
        </p>
      </div>
    );
  };

  const draggableKey = isMaximized
    ? `${app.id}-maximized`
    : `${app.id}-${clampedWindowPosition.x}-${clampedWindowPosition.y}`;
  const isCompactContent = !isPublicShareView;
  const windowContentClassName = `window-content-scroll ${isCompactContent ? 'window-content-scroll-compact' : ''} ${app.id === 'settings' ? 'window-content-scroll-settings' : ''} ${app.id === 'ai-chat' ? 'window-content-scroll-ai-chat' : ''} ${app.id === 'ai-auth' ? 'window-content-scroll-ai-auth' : ''} ${app.id === 'change-os' ? 'window-content-scroll-change-os' : ''} ${isPublicShareView ? 'window-content-scroll-public-share' : ''}`.trim();
  const handleDragStart = useCallback(() => {
    if (!isMaximized) {
      isDraggingRef.current = true;
      setIsInteracting(true);
    }
  }, [isMaximized]);
  const handleDragStop = useCallback((event, data) => {
    if (isMaximized) {
      return;
    }

    isDraggingRef.current = false;
    setIsInteracting(false);
    const nextPosition = clampWindowPosition({ x: data.x, y: data.y }, windowSizeRef.current, desktopMetrics, resolvedDesktopLayout);
    windowPositionRef.current = nextPosition;
    setWindowPosition(nextPosition);
    commitWindowLayout(windowSizeRef.current, nextPosition);
  }, [commitWindowLayout, desktopMetrics, isMaximized, resolvedDesktopLayout]);
  const contentNode = renderContent();
  const windowMenuNode = shouldRenderWindowMenu ? (
    <div className={`window-management-bar window-management-bar-${resolvedOsMode}`.trim()}>
      {windowMenuItems.map((item) => (
        <button
          key={`${app.id}-${item.id}`}
          type="button"
          className={`window-management-button window-management-button-${item.id}`.trim()}
          disabled={inactiveWindowMenuItemIds.has(item.id)}
          onClick={() => onProtectedMenuAction?.(item.id, windowEditorTargetById[app.id] || null)}
        >
          {item.label}
        </button>
      ))}
    </div>
  ) : null;
  const overlayNode = (
    <>
      {renderPortfolioImportModal()}
      {renderPortfolioImportSuggestionsModal()}
      {renderSaveClosePrompt()}
      {renderInitializePrompt()}
    </>
  );

  return (
    <WindowFrame
      draggableKey={draggableKey}
      nodeRef={nodeRef}
      dragBounds={dragBounds}
      isMaximized={isMaximized}
      maximizedFrame={maximizedFrame}
      clampedWindowPosition={clampedWindowPosition}
      activeTheme={activeTheme}
      osMode={resolvedOsMode}
      isFocused={isFocused}
      isResizable={isResizable}
      isVisuallyHidden={isVisuallyHidden}
      isInteracting={isInteracting}
      frameStyle={resolvedFrameStyle}
      localizedWindowTitle={localizedWindowTitle}
      onFocus={onFocus}
      onDragStart={handleDragStart}
      onDragStop={handleDragStop}
      onClose={onClose}
      onMinimize={onMinimize}
      onToggleMaximize={onToggleMaximize}
      appId={app.id}
      windowContentClassName={windowContentClassName}
      windowContentStyle={windowContentStyle}
      windowMenuNode={windowMenuNode}
      contentNode={contentNode}
      overlayNode={overlayNode}
      portfolioImportMessage={portfolioImportMessage}
      onResizeStart={handleResizeStart}
      isPublicShareView={isPublicShareView}
      isCompactContent={isCompactContent}
    />
  );
};

const shouldCompareAiToolProps = (windowId) => ['ai-auth', 'ai-chat', 'profile', 'resume', 'projects'].includes(windowId);

const getRelevantFileMenuNonce = (fileMenuRequest, windowId) => (
  fileMenuRequest?.targetWindowId === windowId ? fileMenuRequest.nonce : null
);

const areWindowPropsEqual = (previousProps, nextProps) => {
  if (previousProps.app !== nextProps.app) {
    return false;
  }

  if (previousProps.config !== nextProps.config) {
    return false;
  }

  if (
    previousProps.isFocused !== nextProps.isFocused
    || previousProps.isMaximized !== nextProps.isMaximized
    || previousProps.isVisuallyHidden !== nextProps.isVisuallyHidden
    || previousProps.osMode !== nextProps.osMode
    || previousProps.desktopLayout !== nextProps.desktopLayout
  ) {
    return false;
  }

  if (
    previousProps.onClose !== nextProps.onClose
    || previousProps.onMinimize !== nextProps.onMinimize
    || previousProps.onToggleMaximize !== nextProps.onToggleMaximize
    || previousProps.onFocus !== nextProps.onFocus
    || previousProps.onSettingChange !== nextProps.onSettingChange
    || previousProps.onProfileDataChange !== nextProps.onProfileDataChange
    || previousProps.onResumeDataChange !== nextProps.onResumeDataChange
    || previousProps.onProjectsDataChange !== nextProps.onProjectsDataChange
    || previousProps.onDesktopIconsChange !== nextProps.onDesktopIconsChange
    || previousProps.onOpenApp !== nextProps.onOpenApp
    || previousProps.onWindowLayoutChange !== nextProps.onWindowLayoutChange
    || previousProps.onAdminAuthPassToggle !== nextProps.onAdminAuthPassToggle
    || previousProps.onAdminLogout !== nextProps.onAdminLogout
    || previousProps.onConsumeFileMenuRequest !== nextProps.onConsumeFileMenuRequest
    || previousProps.onShowToast !== nextProps.onShowToast
    || previousProps.onQueuePersistToast !== nextProps.onQueuePersistToast
    || previousProps.onProtectedMenuAction !== nextProps.onProtectedMenuAction
    || previousProps.onRequestProtectedFileImport !== nextProps.onRequestProtectedFileImport
    || previousProps.onPortfolioImportProcessingChange !== nextProps.onPortfolioImportProcessingChange
    || previousProps.isPublicShareView !== nextProps.isPublicShareView
  ) {
    return false;
  }

  if (
    previousProps.isAdminAuthPassEnabled !== nextProps.isAdminAuthPassEnabled
    || previousProps.isAdminAuthenticated !== nextProps.isAdminAuthenticated
  ) {
    return false;
  }

  // State the two Projects windows share. Without these the desktop's update lands in the
  // store and stops there: the other window keeps rendering what it last saw, which is why an
  // upload only appeared after closing and reopening the viewer.
  if (
    (nextProps.app.id === 'projects' || nextProps.app.id === 'projects-settings')
    && (
      previousProps.projectViewIndex !== nextProps.projectViewIndex
      || previousProps.projectImages !== nextProps.projectImages
      || previousProps.projectImageLimit !== nextProps.projectImageLimit
    )
  ) {
    return false;
  }

  if (
    getRelevantFileMenuNonce(previousProps.fileMenuRequest, previousProps.app.id)
    !== getRelevantFileMenuNonce(nextProps.fileMenuRequest, nextProps.app.id)
  ) {
    return false;
  }

  if (shouldCompareAiToolProps(previousProps.app.id)) {
    return previousProps.aiToolsStatus === nextProps.aiToolsStatus
      && previousProps.aiToolsLoading === nextProps.aiToolsLoading
      && previousProps.onRefreshAiToolsStatus === nextProps.onRefreshAiToolsStatus;
  }

  return true;
};

export default React.memo(Window, areWindowPropsEqual);
